import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import config from './config';
import TicketDetail from './components/TicketDetail';
import { libellePlateforme } from './constants/plateformes';
// Les classes de la fenêtre de détail (`statistics-modal*`, `clickable-row`) viennent de
// `components/StatisticsView.css`, chargée par `App.js` — la seule entrée que `index.js`
// monte. Ne pas l'importer ici : les deux feuilles définissent vingt-six classes communes
// (cartes, grilles, pastilles), et l'importer inverse l'ordre de cascade sur toutes.
import { Users, Building2, DollarSign, UserCheck, AlertCircle, BarChart2, BarChart3, Shield, Mail, X, Clock, Play, CheckCircle, XCircle, Loader2, Rss, FileText, RefreshCw, Filter, Database, HardDrive, TrendingUp, Activity, Gauge, Package, Cpu, History, Calendar, MessageSquare, Bug, HelpCircle, Lightbulb, MoreHorizontal, ChevronRight, ChevronDown, Trash2, ArrowLeft, Eye, EyeOff, Sparkles } from 'lucide-react';
import {
  COLORS as ACTIVITY_COLORS, ACTIVITY_LABELS, DASHBOARD_MODEL_LABELS, presencePlusRecente
} from './constants/activite';
import BadgeMcp from './components/BadgeMcp';
import JaugesQuotas from './components/JaugesQuotas';

/**
 * Les familles d'activité portant une pastille, dans l'ordre d'affichage.
 *
 * ⚠️ **Une seule liste pour les deux tableaux de cet écran.** Elles étaient recopiées, et
 * `AI_ANALYSIS` manquait aux deux : les analyses étaient comptées côté serveur, rangées dans
 * `activities`, et simplement jamais rendues. Le même oubli frappait l'onglet « Dernières
 * connexions » de l'écran Statistiques, pour la même raison.
 */
const TYPES_ACTIVITE = ['DASHBOARD', 'TOOLS', 'AI_ANALYSIS', 'MCP'];

const API_URL = config.API_URL;

// Retourne le nom d'affichage du plan
const getPlanDisplayName = (plan) => {
  const names = {
    'SOLO': 'Solo',
    'TEAM': 'Team',
    'AGENCY': 'Agency',
    'SCALE': 'Scale',
    'CLIENT': 'Client'
  };
  return names[plan] || plan || 'Solo';
};

// Couleurs et labels pour les activités (comme StatisticsView)
/**
 * Les deux populations de l'histogramme, aux couleurs de l'écran Statistiques.
 *
 * Le même bleu y désigne les membres et le tableau de bord, le même violet les clients et
 * l'analyse IA : c'est la convention de cet écran, et s'en écarter ici ferait lire deux
 * langages de couleur dans la même application.
 */
const COULEURS_POPULATION = {
  members: '#3b82f6',
  clients: '#8b5cf6'
};

/**
 * Le badge de rôle, posé derrière le nom dans le tableau des clients.
 *
 * Seuls l'administrateur et l'accès client en portaient un : un membre se lisait donc à
 * l'absence de badge, c'est-à-dire au même signe qu'une ligne dont le rôle n'aurait pas
 * chargé. Les trois rôles sont nommés, et la pastille du décompte en tête d'écran les
 * désigne déjà des mêmes teintes — s'en écarter ici ferait deux langages de couleur.
 *
 * ⚠️ Le membre reste en sourdine, fond clair et texte foncé : c'est le rôle ordinaire, et
 * trois aplats pleins côte à côte mettraient le cas banal au même poids que l'accès
 * privilégié et l'accès restreint, qui sont les deux qu'on cherche des yeux.
 */
const BADGE_DE_ROLE = {
  ADMIN: { libelle: 'Admin', classe: 'admin-badge', fond: '#dc2626', texte: 'white' },
  USER: { libelle: 'Membre', classe: 'membre-badge', fond: '#f1f5f9', texte: '#475569' },
  CLIENT: { libelle: 'Client', classe: 'client-badge', fond: '#7c3aed', texte: 'white' }
};


/**
 * Tri de colonnes du tableau des connexions — repris de l'écran Statistiques.
 *
 * `null` signale une absence, que le tri renvoie en fin de liste **dans les deux sens** : une
 * personne sans activité n'a rien à faire en tête d'un classement, et l'y voir remonter au
 * premier clic sur « croissant » se lirait comme un défaut de tri. Un zéro n'est pas une
 * absence : zéro connexion est une mesure, et se classe comme telle.
 */
const valeurTexte = (v) => (v ? String(v).toLocaleLowerCase('fr') : null);
const valeurDate = (v) => (v ? new Date(v).getTime() : null);

const sommeActivites = (a = {}) => (
  (a.DASHBOARD || 0) + (a.TOOLS || 0) + (a.MCP || 0)
);

const EXTRACTEURS_RECENSEMENT = {
  utilisateur: p => valeurTexte(p.name || p.email),
  organisation: p => valeurTexte(p.organisation),
  compte: p => valeurTexte(p.compteClient),
  invitePar: p => valeurTexte(p.invitePar),
  plan: p => valeurTexte(p.plan),
  role: p => valeurTexte(p.organisationRole || p.role),
  lastLoginAt: p => valeurDate(p.lastLoginAt),
  // Un zéro est une mesure — « jamais venu » — et doit se classer comme tel, pas finir en bas
  // avec les valeurs absentes.
  connexions: p => p.totalConnections || 0,
  activites: p => sommeActivites(p.activities)
};

/** Trie un recensement. Même règle de vide que les connexions : absent en bas des deux sens. */
const trierRecensement = (lignes, { cle, sens }) => {
  const extraire = EXTRACTEURS_RECENSEMENT[cle];
  if (!extraire) return lignes;
  const facteur = sens === 'asc' ? 1 : -1;
  return [...lignes].sort((a, b) => {
    const va = extraire(a);
    const vb = extraire(b);
    const aVide = va === null || va === undefined;
    const bVide = vb === null || vb === undefined;
    if (aVide && bVide) return 0;
    if (aVide) return 1;
    if (bVide) return -1;
    if (va === vb) return 0;
    return va > vb ? facteur : -facteur;
  });
};

const trierConnexions = (lignes, { cle, sens }, totaux) => {
  const EXTRACTEURS = {
    utilisateur: l => valeurTexte(l.userName || l.userEmail),
    plan: l => valeurTexte(l.planType),
    organisation: l => valeurTexte(l.organizationName || l.accountName),
    type: l => valeurTexte(l.userRole === 'CLIENT' ? 'client' : (l.organizationRole || 'member')),
    lastLoginAt: l => valeurDate(l.loginAt),
    // On trie sur ce que la pastille affiche : la somme des activités fonctionnelles.
    activites: l => {
      const t = totaux[l.userId] || {};
      return (t.DASHBOARD || 0) + (t.TOOLS || 0) + (t.MCP || 0);
    }
  };

  const extraire = EXTRACTEURS[cle];
  if (!extraire) return lignes;

  const facteur = sens === 'asc' ? 1 : -1;
  return [...lignes].sort((a, b) => {
    const va = extraire(a);
    const vb = extraire(b);
    const aVide = va === null || va === undefined;
    const bVide = vb === null || vb === undefined;
    if (aVide && bVide) return 0;
    if (aVide) return 1;
    if (bVide) return -1;
    if (va === vb) return 0;
    return va > vb ? facteur : -facteur;
  });
};

/**
 * En-tête de colonne triable.
 *
 * Défini hors du composant : déclaré à l'intérieur, React le verrait comme un type neuf à chaque
 * rendu et remonterait toute la ligne d'en-tête.
 *
 * La flèche est toujours rendue, en retrait tant que la colonne ne trie pas : la réserver au
 * survol ferait sursauter l'en-tête au passage de la souris.
 */
const EnTeteTriable = ({ cle, children, style, tri, onTrier }) => {
  const actif = tri.cle === cle;
  return (
    <th
      className={`statistics-th-triable${actif ? ' actif' : ''}`}
      style={style}
      onClick={() => onTrier(cle)}
      title="Trier sur cette colonne"
    >
      {children}
      <span className="statistics-tri-fleche">{actif ? (tri.sens === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
};

// Fonction pour formater une date complète
const formatFullDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/** Le nom lisible d'un type de source MCP. */
const LIBELLES_TYPE_MCP = {
  GOOGLE_ADS: 'Google Ads',
  MICROSOFT_ADS: 'Microsoft Ads',
  FACEBOOK_ADS: 'Meta Ads',
  PINTEREST_ADS: 'Pinterest Ads',
  TIKTOK_ADS: 'TikTok Ads',
  OPENAI_ADS: 'ChatGPT Ads',
  AWIN: 'Awin',
  PRESTASHOP: 'PrestaShop',
  MATOMO: 'Matomo',
  MATOMO_OAUTH: 'Matomo',
  PIANO_ANALYTICS: 'Piano Analytics',
  GOOGLE_ANALYTICS: 'Google Analytics 4',
  GOOGLE_SEARCH_CONSOLE: 'Search Console',
  GOOGLE_TAG_MANAGER: 'Tag Manager',
  GOOGLE_MERCHANT_API: 'Merchant Center',
  OPEN_METEO: 'Open-Meteo'
};

const AdminPanel = ({ token, user, activeTab = 'dashboard', onTabChange, initialTicketId = null, onTicketSelect }) => {
  const [adminData, setAdminData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [whitelistData, setWhitelistData] = useState({ enabled: false, emails: [] });
  const [newEmail, setNewEmail] = useState('');
  const [whitelistLoading, setWhitelistLoading] = useState(false);
  const [loginStats, setLoginStats] = useState(null);
  const [loginStatsLoading, setLoginStatsLoading] = useState(false);
  const [selectedPlanFilter, setSelectedPlanFilter] = useState(null);
  const [selectedActivityFilter, setSelectedActivityFilter] = useState(null);
  const [recentLogins, setRecentLogins] = useState(null);
  const [recentLoginsLoading, setRecentLoginsLoading] = useState(false);
  /** La personne dont on affiche le détail des actions, et le chargement de ce détail. */
  const [detailPersonne, setDetailPersonne] = useState(null);
  const [chargementActions, setChargementActions] = useState(false);

  const [loginsTab, setLoginsTab] = useState('recent'); // 'recent', 'members' ou 'clients'
  const [hoveredBar, setHoveredBar] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [cronStatus, setCronStatus] = useState(null);
  const [cronLoading, setCronLoading] = useState(false);
  const [triggeringCron, setTriggeringCron] = useState(false);

  /**
   * Ce que le cache de trafic Awin contient, par compte.
   *
   * La carte de la tâche dit ce que la dernière nuit a écrit ; elle ne dit pas ce qui est en
   * base. C'est pourtant la seule façon de trancher devant un trou dans une courbe : la journée
   * a-t-elle été rapatriée, ou attend-elle encore son tour ?
   *
   * Chargé à la demande, jamais au montage : c'est une requête par compte Awin, inutile tant que
   * personne ne pose la question.
   */
  /** Groupe d'erreurs dont le bloc de diagnostic vient d'être copié — un retour visuel bref. */
  /**
   * La fenêtre d'observation des erreurs.
   *
   * Vingt-quatre heures était la seule possible, et rien ne le disait : un problème hebdomadaire
   * y était invisible, alors que la table garde tout depuis la mise en service.
   */
  const [fenetreErreurs, setFenetreErreurs] = useState('24h');

  const [erreurCopiee, setErreurCopiee] = useState(null);

  /**
   * Le bloc de diagnostic d'une erreur, en texte brut.
   *
   * Tout ce qu'il faut pour agir, et rien d'autre : la cause, où elle se produit, sur quel
   * compte, d'où part l'appel, et la requête exacte. Recopier ces éléments à la main depuis
   * l'écran est fastidieux et on en oublie toujours un — celui qui manquait.
   */
  const blocDeDiagnostic = (e) => {
    const lignes = [
      `Erreur ${e.provider || '?'} · ${e.operationType || '?'}`,
      `Occurrences : ${e.occurrences || 1}` + (e.occurrences > 1
        ? ` (de ${new Date(e.premiereVue).toLocaleString('fr-FR')} à ${new Date(e.derniereVue).toLocaleString('fr-FR')})`
        : ` (${new Date(e.createdAt).toLocaleString('fr-FR')})`),
      e.customerId ? `Compte : ${e.customerId}${e.metadata?.accountName ? ` — ${e.metadata.accountName}` : ''}` : null,
      e.user?.email ? `Propriétaire des données : ${e.user.email}` : null,
      e.metadata?.demandeur?.email && e.metadata.demandeur.email !== e.user?.email
        ? `Demandé par : ${e.metadata.demandeur.email} (${e.metadata.demandeur.role || 'rôle inconnu'})`
        : null,
      e.metadata?.sourceType ? `Source : ${e.metadata.sourceType}` : null,
      e.metadata?.resource ? `Ressource : ${e.metadata.resource}` : null,
      e.durationMs != null ? `Durée : ${e.durationMs} ms` : null,
      '',
      'Message :',
      e.errorMessage || 'non spécifié'
    ];

    if (Array.isArray(e.metadata?.origine) && e.metadata.origine.length > 0) {
      lignes.push('', "Origine de l'appel :", ...e.metadata.origine.map(o => `  ${o}`));
    }
    if (e.metadata?.query) {
      lignes.push('', 'Requête :', String(e.metadata.query).trim());
    }

    return lignes.filter(l => l !== null).join('\n');
  };

  const copierDiagnostic = async (e, cle) => {
    try {
      await navigator.clipboard.writeText(blocDeDiagnostic(e));
      setErreurCopiee(cle);
      setTimeout(() => setErreurCopiee(null), 2000);
    } catch {
      // Le presse-papiers est refusé hors contexte sécurisé : la sélection manuelle reste
      // possible, et un échec silencieux vaut mieux qu'une alerte qui bloque la page.
      console.warn('Presse-papiers indisponible');
    }
  };

  const [cacheAwin, setCacheAwin] = useState(null);
  const [cacheAwinOuvert, setCacheAwinOuvert] = useState(false);
  const [cacheAwinChargement, setCacheAwinChargement] = useState(false);
  const [cacheAwinErreur, setCacheAwinErreur] = useState('');

  const ouvrirCacheAwin = async () => {
    setCacheAwinOuvert(true);
    setCacheAwinChargement(true);
    setCacheAwinErreur('');
    try {
      const r = await axios.get(`${API_URL}/api/admin/awin-cache`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCacheAwin(r.data);
    } catch (e) {
      setCacheAwinErreur(e.response?.data?.error || 'Impossible de lire le cache Awin');
    } finally {
      setCacheAwinChargement(false);
    }
  };
  /**
   * La plateforme dont on veut voir les erreurs. `null` : toutes.
   *
   * Le filtre existe parce que l'écran ne montrait qu'une source — seul Google Ads était
   * journalisé. Maintenant que dix connecteurs y écrivent, une liste à plat mêlerait un quota
   * Google dépassé et un jeton Piano expiré, qui n'appellent ni le même remède ni la même
   * personne.
   */
  const [filtrePlateforme, setFiltrePlateforme] = useState(null);
  const [expandedCronTasks, setExpandedCronTasks] = useState({}); // Track which cron tasks are expanded

  // Toggle cron task expansion
  const toggleCronTask = (taskKey) => {
    setExpandedCronTasks(prev => ({
      ...prev,
      [taskKey]: !prev[taskKey]
    }));
  };

  // Une exécution dépliée montre le détail de ses sous-tâches. Le backend les enregistre
  // depuis toujours (nom, durée, statut) sans que rien ne les affiche : une tâche lente ou en
  // échec au milieu d'un enchaînement restait invisible derrière la durée totale.
  const [expandedCronRuns, setExpandedCronRuns] = useState({});

  const toggleCronRun = (runKey) => {
    setExpandedCronRuns(prev => ({
      ...prev,
      [runKey]: !prev[runKey]
    }));
  };

  /** Durée en secondes, ou en millisecondes tant qu'elle reste sous la seconde. */
  const formaterDuree = (ms) => {
    if (ms === null || ms === undefined) return '-';
    return ms < 1000 ? `${ms} ms` : `${Math.round(ms / 1000)}s`;
  };

  /**
   * Une exécution est-elle automatique ?
   *
   * Le backend écrit `'scheduled'` depuis les cron.schedule(), `'manual'` depuis le bouton
   * « Exécuter maintenant », et garde `'cron'` comme valeur par défaut des fonctions. Ne tester
   * que `'cron'` étiquetait « Manuel » toutes les exécutions nocturnes — y compris celles de
   * 3h00 pile, ce qui donnait un écran se contredisant lui-même sous « Planifié tous les jours
   * à 3h00 ».
   */
  const estDeclenchementAuto = (triggeredBy) => triggeredBy === 'scheduled' || triggeredBy === 'cron';

  /**
   * Une tâche est-elle réellement en échec ?
   *
   * `lastError` est conservé en base tant qu'aucune nouvelle erreur ne l'écrase : il dit « la
   * dernière fois que ça a cassé », pas « c'est cassé ». Le prendre pour un état courant faisait
   * afficher ERREUR en rouge sur une tâche dont le dernier passage était un succès — relevé sur
   * les alertes de dépassement, rouges à cause d'une erreur vieille de douze jours alors
   * qu'elles tournaient toutes les heures sans faute.
   *
   * L'erreur ne compte donc que si elle est postérieure au dernier succès. Sans date d'erreur
   * exploitable, on s'en remet à l'existence d'un succès : une tâche qui n'a jamais réussi
   * reste en erreur.
   */
  const estEnEchec = (task) => {
    if (!task?.lastError) return false;
    if (!task.lastSuccess) return true;

    const dateErreur = task.lastError.at || task.lastError.timestamp;
    if (!dateErreur) return false;

    return new Date(dateErreur).getTime() > new Date(task.lastSuccess).getTime();
  };

  /**
   * Le dernier passage a-t-il laissé une partie du parc de côté ?
   *
   * Le badge de carte ne connaissait que trois états — en cours, en erreur, OK — et déduisait
   * « OK » de la seule existence d'un `lastSuccess`. Il annonçait donc OK au-dessus d'un tableau
   * d'historique affichant « Partiel » sur la même exécution : l'écran se contredisait, et
   * l'information qui compte — quatre étapes sur six n'ont pas servi tout le parc — n'était
   * visible qu'en dépliant une ligne.
   *
   * Le statut est lu sur la dernière exécution, seule à décrire l'état courant. Les sous-tâches
   * servent de repli : les exécutions enregistrées avant que le backend ne porte `partial` sur
   * l'exécution elle-même ne l'ont que sur leurs étapes.
   */
  const estPartielle = (task) => {
    const derniere = task?.history?.[0];
    if (!derniere) return false;
    if (derniere.status === 'partial') return true;
    return derniere.status === 'success' && (derniere.tasks || []).some(t => t.status === 'partial');
  };

  /**
   * Tableau d'historique d'une tâche cron.
   *
   * Quatre cartes n'en affichaient aucun — alertes de dépassement, les deux synchronisations
   * Social Ads et les e-mails de synthèse — alors que l'API rend les cinq dernières exécutions
   * pour les douze tâches. On ne voyait donc d'elles que leur dernier passage, sans pouvoir
   * distinguer une tâche stable d'une tâche qui échoue un jour sur deux.
   *
   * Écrit comme fonction plutôt que recopié une cinquième fois : les sept tableaux existants
   * ont divergé au fil des ajouts, c'est ainsi qu'un « Manuel » erroné a pu survivre dans l'un
   * d'eux pendant que les autres étaient corrigés.
   *
   * @param {string} cle - clé de la tâche dans cronStatus.tasks
   * @param {Array<{titre: string, valeur: Function}>} colonnes - colonnes propres à la tâche
   */
  const rendreHistoriqueCron = (cle, colonnes = []) => {
    const historique = cronStatus?.tasks?.[cle]?.history;
    if (!historique || historique.length === 0) return null;

    return (
      <div className="cron-history">
        <h4>Historique des dernières exécutions</h4>
        <table className="cron-history-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Déclenché par</th>
              {colonnes.map(c => <th key={c.titre}>{c.titre}</th>)}
              <th>Durée</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {historique.map((entry, index) => (
              <tr key={index} className={entry.status === 'success' ? 'row-success' : 'row-error'}>
                <td>{entry.startTime ? new Date(entry.startTime).toLocaleString('fr-FR') : '-'}</td>
                <td>
                  <span className={`trigger-badge ${entry.triggeredBy}`}>
                    {estDeclenchementAuto(entry.triggeredBy) ? 'Auto' : 'Manuel'}
                  </span>
                </td>
                {colonnes.map(c => <td key={c.titre}>{c.valeur(entry)}</td>)}
                <td>{formaterDuree(entry.duration)}</td>
                <td>
                  {/* `partial` est un vrai statut pour ces tâches : le scan est allé au bout,
                      mais une partie du parc est restée de côté. */}
                  {entry.status === 'success' ? (
                    <span className="status-badge success"><CheckCircle size={12} /> Succès</span>
                  ) : entry.status === 'partial' ? (
                    <span className="status-badge error" style={{ background: '#fef3c7', color: '#b45309' }}>
                      <AlertCircle size={12} /> Partiel
                    </span>
                  ) : entry.status === 'running' ? (
                    <span className="status-badge running"><Loader2 size={12} className="spin" /> En cours</span>
                  ) : (
                    <span
                      className="status-badge error"
                      title={Array.isArray(entry.errors) ? entry.errors.join(' | ') : (entry.error || '')}
                    >
                      <XCircle size={12} /> Erreur
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Action Logs state
  const [actionLogs, setActionLogs] = useState(null);
  const [actionLogsLoading, setActionLogsLoading] = useState(false);
  const [actionLogsFilters, setActionLogsFilters] = useState(null);
  const [actionLogsPage, setActionLogsPage] = useState(1);
  const [actionLogsFilter, setActionLogsFilter] = useState({
    accountId: '',
    clientId: '',
    actionType: '',
    source: ''
  });
  const [syncingGoogleAds, setSyncingGoogleAds] = useState(false);
  const [refreshingCustomers, setRefreshingCustomers] = useState(false);

  // BigQuery state
  const [bigQueryStats, setBigQueryStats] = useState(null);
  const [bigQueryTables, setBigQueryTables] = useState(null);
  const [bigQueryLoading, setBigQueryLoading] = useState(false);
  const [bigQueryPeriod, setBigQueryPeriod] = useState('7d');

  // Quota API state
  const [quotaStatus, setQuotaStatus] = useState(null);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [quotaErrors, setQuotaErrors] = useState(null);
  const [quotaErrorsLoading, setQuotaErrorsLoading] = useState(false);

  /**
   * Consommation Resend. À part du quota Google Ads : les deux n'ont ni source ni fenêtre
   * commune, et un échec de l'un ne doit pas effacer l'autre de l'écran.
   */
  const [resendUsage, setResendUsage] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);

  /**
   * Consommation Claude. À part elle aussi, et pour la même raison : son relevé vient de
   * l'Admin API d'Anthropic, qui réclame une clé que le quota Google Ads n'a jamais eue.
   */
  const [claudeUsage, setClaudeUsage] = useState(null);
  /** L'interrupteur de la génération de dashboards sur mesure. `null` = pas encore lu. */
  const [generationSurMesure, setGenerationSurMesure] = useState(null);
  const [generationEnBascule, setGenerationEnBascule] = useState(false);
  const [claudeLoading, setClaudeLoading] = useState(false);

  /**
   * La source dont le détail est déplié, ou `null`.
   *
   * ⚠️ **Une seule à la fois, et aucune par défaut.** Les trois détails empilés faisaient
   * plusieurs écrans de défilement avant qu'on sache si quelque chose allait mal — c'est
   * justement ce que les jauges résolvent. En ouvrir une d'office annulerait le gain pour la
   * source choisie, et il n'y a aucune raison de préférer l'une des trois.
   */
  const [detailQuota, setDetailQuota] = useState(null);
  const ouvrirDetailQuota = useCallback((cle) => {
    setDetailQuota((courant) => (courant === cle ? null : cle));
  }, []);

  // MCP Write Access state
  const [mcpAccessList, setMcpAccessList] = useState([]);
  const [mcpWriteLogs, setMcpWriteLogs] = useState([]);
  const [mcpLoading, setMcpLoading] = useState(false);
  const [mcpSearch, setMcpSearch] = useState({ accountId: '', accountName: '' });
  const [mcpLogsTotal, setMcpLogsTotal] = useState(0);
  const [mcpLogsPage, setMcpLogsPage] = useState(1);

  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsStats, setTicketsStats] = useState(null);
  const [ticketsFilter, setTicketsFilter] = useState('IN_PROGRESS');
  const [ticketsPage, setTicketsPage] = useState(1);
  const [ticketsTotal, setTicketsTotal] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Grouper les connexions récentes par utilisateur (comme StatisticsView)
  const groupedRecentLogins = useMemo(() => {
    if (!recentLogins?.logins) return [];

    const userMap = new Map();
    recentLogins.logins.forEach(login => {
      const key = login.userEmail; // Grouper par email
      if (!userMap.has(key)) {
        userMap.set(key, {
          id: login.userId || login.id,
          name: login.userName,
          email: login.userEmail,
          planType: login.planType,
          userRole: login.userRole, // ADMIN, USER, CLIENT depuis LoginHistory
          organizationName: login.organizationName,
          organizationRole: login.organizationRole, // OWNER, ADMIN, MEMBER depuis OrganizationMember
          picture: null, // Admin API ne fournit pas de photo
          lastLoginAt: login.loginAt,
          actions: []
        });
      }
      // Mettre à jour la dernière connexion si plus récente
      const userData = userMap.get(key);
      if (new Date(login.loginAt) > new Date(userData.lastLoginAt)) {
        userData.lastLoginAt = login.loginAt;
      }
      userData.actions.push({
        id: login.id,
        activity: login.activityType,
        loginAt: login.loginAt,
        dashboardModel: login.dashboardModel,
        accountName: login.accountName
      });
    });

    // Trier par dernière connexion (plus récent en premier)
    return Array.from(userMap.values()).sort((a, b) =>
      new Date(b.lastLoginAt) - new Date(a.lastLoginAt)
    );
  }, [recentLogins?.logins]);

  // Formater la date pour l'axe X du graphique
  const formatChartDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  // Données pour le graphique journalier (BarChart)
  const dailyChartData = useMemo(() => {
    if (!loginStats?.daily) return [];
    return loginStats.daily.map(d => ({
      ...d,
      dateLabel: formatChartDate(d.date),
      // Les journées d'avant la ventilation ne portent que `connections` : on les verse dans
      // les membres plutôt que de les perdre, une barre absente valant pire qu'une barre
      // imprécise sur un historique déjà écrit.
      members: d.members ?? d.connections ?? 0,
      clients: d.clients ?? 0,
      total: (d.members ?? d.connections ?? 0) + (d.clients ?? 0)
    }));
  }, [loginStats?.daily]);

  // Données pour le pie chart (répartition par activité)
  const activityChartData = useMemo(() => {
    if (!loginStats?.byActivity) return [];
    const total = loginStats.byActivity.reduce((sum, a) => sum + a.connections, 0);
    return loginStats.byActivity
      .filter(a => a.connections > 0 && a.activity !== 'LOGIN')
      .map(a => ({
        name: ACTIVITY_LABELS[a.activity] || a.activity,
        value: a.connections,
        percentage: total > 0 ? Math.round((a.connections / total) * 100) : 0,
        fill: ACTIVITY_COLORS[a.activity] || '#6b7280'
      }));
  }, [loginStats?.byActivity]);

  // Tooltip personnalisé pour le bar chart
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const members = payload.find(p => p.dataKey === 'members')?.value || 0;
    const clients = payload.find(p => p.dataKey === 'clients')?.value || 0;
    return (
      <div className="stats-tooltip">
        <p className="stats-tooltip-label">{label}</p>
        <p style={{ color: COULEURS_POPULATION.members }}>Membres : {members}</p>
        <p style={{ color: COULEURS_POPULATION.clients }}>Clients : {clients}</p>
        <p><strong>Total : {members + clients}</strong></p>
      </div>
    );
  };

  // Tooltip personnalisé pour le pie chart
  const CustomPieTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0].payload;
    return (
      <div className="stats-tooltip">
        <p><strong>{data.name}</strong></p>
        <p>{data.value} connexion{data.value > 1 ? 's' : ''} ({data.percentage}%)</p>
      </div>
    );
  };

  // Top comptes par type d'activité (données de loginStats sur 30 jours)
  const topAccountsByActivity = useMemo(() => {
    if (!loginStats?.topAccountsByActivity) {
      return { DASHBOARD: [], TOOLS: [], AI_ANALYSIS: [] };
    }
    return loginStats.topAccountsByActivity;
  }, [loginStats?.topAccountsByActivity]);

  // Filtrer les logins par type (tous, membres, clients)
  const filteredLogins = useMemo(() => {
    if (!recentLogins?.logins) return { recent: [], members: [], clients: [] };

    const members = recentLogins.logins.filter(login => login.userRole !== 'CLIENT');
    const clients = recentLogins.logins.filter(login => login.userRole === 'CLIENT');

    return { recent: recentLogins.logins, members, clients };
  }, [recentLogins?.logins]);

  /**
   * Les connexions de la page courante, groupées par utilisateur.
   *
   * Le tableau listait une ligne **par connexion** : le même utilisateur y revenait vingt fois
   * d'affilée, et il fallait faire défiler pour savoir qui d'autre s'était connecté. L'écran
   * Statistiques groupe par personne — une ligne, sa dernière visite, et ce qu'elle a fait sur
   * la période en pastilles.
   *
   * Le groupement porte sur les cent dernières connexions rapatriées — la même profondeur que
   * l'écran de référence. Il n'y a plus de pagination : elle comptait des connexions sous un
   * tableau qui compte des personnes, et annonçait cent trente-trois pages pour une ligne.
   */
  /**
   * Tri courant du tableau des connexions.
   *
   * La dernière connexion, décroissante, est le défaut — c'est la question qu'on se pose en
   * ouvrant cet écran : qui est venu récemment.
   */
  const [triConnexions, setTriConnexions] = useState({ cle: 'lastLoginAt', sens: 'desc' });

  /**
   * Chaque onglet garde son propre tri.
   *
   * Classer les membres par nombre de connexions puis passer aux clients ne doit pas défaire
   * le classement qu'on venait d'établir : c'est la règle de l'écran Statistiques, et ce sont
   * trois tableaux différents.
   */
  const [triRecensement, setTriRecensement] = useState({
    members: { cle: 'lastLoginAt', sens: 'desc' },
    clients: { cle: 'lastLoginAt', sens: 'desc' }
  });

  /** Même colonne : on inverse le sens. Nouvelle colonne : on repart du décroissant. */
  const changerTriConnexions = (cle) => setTriConnexions(actuel => ({
    cle,
    sens: actuel.cle === cle && actuel.sens === 'desc' ? 'asc' : 'desc'
  }));

  const changerTriRecensement = (onglet, cle) => setTriRecensement(actuel => {
    const courant = actuel[onglet];
    return {
      ...actuel,
      [onglet]: { cle, sens: courant.cle === cle && courant.sens === 'desc' ? 'asc' : 'desc' }
    };
  });

  /**
   * Les connexions groupées par personne, pour chacun des trois onglets.
   *
   * Les trois sont calculés d'avance parce que les compteurs des onglets en dépendent : annoncer
   * « Membres (96) » au-dessus d'un tableau de deux lignes comptait des connexions sous un
   * tableau qui compte des personnes. L'écran de référence affiche des personnes ; on fait de
   * même.
   */
  const grouperParUtilisateur = useCallback((connexions) => {
    const parUtilisateur = new Map();

    for (const login of connexions || []) {
      if (!parUtilisateur.has(login.userId)) {
        parUtilisateur.set(login.userId, { ...login, connexionsSurLaPage: 0 });
      }
      const entree = parUtilisateur.get(login.userId);
      entree.connexionsSurLaPage++;
      // La liste arrive triée du plus récent au plus ancien ; on garde malgré tout le maximum,
      // pour ne pas dépendre d'un ordre que l'API pourrait changer.
      if (new Date(login.loginAt) > new Date(entree.loginAt)) entree.loginAt = login.loginAt;
    }

    return [...parUtilisateur.values()].sort(
      (a, b) => new Date(b.loginAt) - new Date(a.loginAt)
    );
  }, []);

  const groupesParOnglet = useMemo(() => ({
    recent: grouperParUtilisateur(filteredLogins.recent),
    members: grouperParUtilisateur(filteredLogins.members),
    clients: grouperParUtilisateur(filteredLogins.clients)
  }), [filteredLogins, grouperParUtilisateur]);

  const groupedLogins = useMemo(
    () => groupesParOnglet[loginsTab] || [],
    [groupesParOnglet, loginsTab]
  );

  /** Les totaux d'activité de la période, par utilisateur — MCP compris. */
  const totauxParUtilisateur = useMemo(
    () => loginStats?.totauxParUtilisateur || {},
    [loginStats?.totauxParUtilisateur]
  );

  /** Les lignes du tableau, dans l'ordre demandé par l'en-tête. */
  const lignesTriees = useMemo(
    () => trierConnexions(groupedLogins, triConnexions, totauxParUtilisateur),
    [groupedLogins, triConnexions, totauxParUtilisateur]
  );

  /** En-tête cliquable, lié à l'état de tri de ce composant. */
  const EnTete = useCallback(
    (props) => <EnTeteTriable {...props} tri={triConnexions} onTrier={changerTriConnexions} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [triConnexions]
  );

  /** Le recensement du parc, et ses en-têtes triables — un par onglet. */
  const recensement = useMemo(
    () => loginStats?.recensement || { membres: [], clients: [] },
    [loginStats?.recensement]
  );

  const EnTeteMembres = useCallback(
    (props) => <EnTeteTriable {...props} tri={triRecensement.members} onTrier={(cle) => changerTriRecensement('members', cle)} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [triRecensement.members]
  );

  const EnTeteClients = useCallback(
    (props) => <EnTeteTriable {...props} tri={triRecensement.clients} onTrier={(cle) => changerTriRecensement('clients', cle)} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [triRecensement.clients]
  );

  const fetchAdminData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminData(response.data.stats);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement du dashboard admin:', error);
      setError(error.response?.data?.error || 'Erreur de chargement');
      setLoading(false);
    }
  }, [token]);

  const fetchCustomers = useCallback(async () => {
    try {
      /**
       * Tous les utilisateurs, et non la première page.
       *
       * L'appel se faisait sans limite, donc le défaut de vingt s'appliquait — en silence, et
       * sans pagination à l'écran pour aller voir les suivants. Le tri par dernière connexion se
       * faisait alors sur ces vingt-là, découpés par **date de création** : un utilisateur ancien
       * connecté le matin même n'était pas dans le lot, et l'écran semblait figé.
       *
       * Le serveur trie désormais par dernière connexion ; l'ordre reçu est donc le bon, et le
       * tri local disparaît.
       */
      const response = await axios.get(`${API_URL}/api/admin/customers`, {
        params: { limit: 200 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
    }
  }, [token]);

  const refreshCustomers = useCallback(async () => {
    setRefreshingCustomers(true);
    try {
      await fetchCustomers();
    } finally {
      setRefreshingCustomers(false);
    }
  }, [fetchCustomers]);

  const fetchWhitelist = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/beta-whitelist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWhitelistData(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de la liste blanche:', error);
    }
  }, [token]);

  const fetchLoginStats = useCallback(async (planFilter = null, activityFilter = null) => {
    try {
      setLoginStatsLoading(true);
      let url = `${API_URL}/api/admin/login-stats?days=30`;
      if (planFilter) url += `&planFilter=${planFilter}`;
      if (activityFilter) url += `&activityFilter=${activityFilter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Debug - vérifier les données reçues
      // console.log('Login stats reçues:', response.data.data);
      
      // Délai minimal pour éviter les scintillements
      const minDelay = new Promise(resolve => setTimeout(resolve, 200));
      await minDelay;
      
      setLoginStats(response.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des stats de connexion:', error);
    } finally {
      setLoginStatsLoading(false);
    }
  }, [token]);

  /**
   * Ouvre le détail des actions d'une personne, au clic sur sa ligne.
   *
   * La fenêtre s'ouvre immédiatement, vide, puis se remplit : le parc compte des personnes à
   * plusieurs centaines d'actions, et attendre la réponse avant d'afficher quoi que ce soit
   * donnerait l'impression d'un clic sans effet — le défaut même qu'on corrige ici.
   *
   * Les trente jours sont ceux de `fetchLoginStats`, et la fenêtre doit montrer ce que la
   * pastille annonce : les deux périodes se suivent, elles ne se choisissent pas séparément.
   *
   * La réponse tardive d'un premier clic ne doit pas s'afficher sous un second : on ne retient
   * le résultat que si la fenêtre porte toujours la même personne.
   */
  const ouvrirDetail = useCallback(async (personne) => {
    setDetailPersonne({ ...personne, actions: [] });
    setChargementActions(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/users/${personne.userId}/actions?days=30`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.success) {
        setDetailPersonne(actuel => (
          actuel && actuel.userId === personne.userId
            ? { ...actuel, actions: data.data.actions, dernierePresence: data.data.dernierePresence }
            : actuel
        ));
      }
    } catch (error) {
      console.error('Erreur chargement du détail des actions:', error);
    } finally {
      setChargementActions(false);
    }
  }, [token]);

  const fetchRecentLogins = useCallback(async (page = 1, planFilter = null, activityFilter = null) => {
    try {
      setRecentLoginsLoading(true);
      /**
       * Cent connexions d'un coup, sans pagination — comme l'écran Statistiques.
       *
       * Le tableau étant groupé par utilisateur, paginer sur les connexions n'avait plus de
       * sens : dix connexions du même visiteur donnaient **une** ligne sous un compteur
       * annonçant « page 1 sur 134 ». Le lecteur croyait manquer cent trente-trois pages
       * d'utilisateurs, là où il n'en manquait aucun.
       *
       * Cent est la profondeur de l'écran de référence : assez pour couvrir les visiteurs
       * récents, assez peu pour tenir dans une réponse.
       */
      let url = `${API_URL}/api/admin/recent-logins?page=1&limit=100`;
      if (planFilter) url += `&planFilter=${planFilter}`;
      if (activityFilter) url += `&activityFilter=${activityFilter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentLogins(response.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des dernières connexions:', error);
    } finally {
      setRecentLoginsLoading(false);
    }
  }, [token]);

  // Fonctions de filtrage avec debounce léger
  const handlePlanFilter = useCallback((plan) => {
    if (loginStatsLoading) return; // Éviter les clics multiples pendant le chargement
    
    const newPlanFilter = selectedPlanFilter === plan ? null : plan;
    setSelectedPlanFilter(newPlanFilter);
    setSelectedActivityFilter(null); // Reset l'autre filtre
    fetchLoginStats(newPlanFilter, null);
    fetchRecentLogins(1, newPlanFilter, null);
  }, [selectedPlanFilter, loginStatsLoading, fetchLoginStats, fetchRecentLogins]);

  const handleActivityFilter = useCallback((activity) => {
    if (loginStatsLoading) return; // Éviter les clics multiples pendant le chargement

    const newActivityFilter = selectedActivityFilter === activity ? null : activity;
    setSelectedActivityFilter(newActivityFilter);
    setSelectedPlanFilter(null); // Reset l'autre filtre
    fetchLoginStats(null, newActivityFilter);
    fetchRecentLogins(1, null, newActivityFilter);
  }, [selectedActivityFilter, loginStatsLoading, fetchLoginStats, fetchRecentLogins]);

  // Fonctions pour le suivi des tâches CRON
  const fetchCronStatus = useCallback(async () => {
    try {
      setCronLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/cron-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCronStatus(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement du statut CRON:', error);
    } finally {
      setCronLoading(false);
    }
  }, [token]);

  const triggerCron = async (taskName) => {
    try {
      setTriggeringCron(true);
      await axios.post(`${API_URL}/api/admin/trigger-cron`,
        { task: taskName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Attendre un peu puis rafraîchir le statut
      setTimeout(() => {
        fetchCronStatus();
        setTriggeringCron(false);
      }, 1000);
    } catch (error) {
      console.error('Erreur lors du déclenchement CRON:', error);
      setTriggeringCron(false);
      alert(error.response?.data?.error || 'Erreur lors du déclenchement');
    }
  };

  const clearFilters = useCallback(() => {
    if (loginStatsLoading) return; // Éviter les clics multiples pendant le chargement

    setSelectedPlanFilter(null);
    setSelectedActivityFilter(null);
    fetchLoginStats();
    fetchRecentLogins(1);
  }, [loginStatsLoading, fetchLoginStats, fetchRecentLogins]);

  // Fetch action logs
  const fetchActionLogs = useCallback(async (page = 1, filters = {}) => {
    try {
      setActionLogsLoading(true);
      const params = new URLSearchParams({ page, limit: 30 });
      if (filters.accountId) params.append('accountId', filters.accountId);
      if (filters.clientId) params.append('clientId', filters.clientId);
      if (filters.actionType) params.append('actionType', filters.actionType);
      if (filters.source) params.append('source', filters.source);

      const url = `${API_URL}/api/admin/action-logs?${params}`;
      console.log('🔍 [FETCH] URL:', url);
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('🔍 [FETCH] Réponse:', response.data.pagination?.total, 'résultats');
      setActionLogs(response.data);
      setActionLogsPage(page);
    } catch (error) {
      console.error('Erreur lors du chargement des action logs:', error);
    } finally {
      setActionLogsLoading(false);
    }
  }, [token]);

  // Fetch action logs filters
  const fetchActionLogsFilters = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/action-logs/filters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActionLogsFilters(response.data.filters);
    } catch (error) {
      console.error('Erreur lors du chargement des filtres:', error);
    }
  }, [token]);

  // Fetch BigQuery stats
  const fetchBigQueryStats = useCallback(async (period = '7d') => {
    try {
      setBigQueryLoading(true);
      const [statsResponse, tablesResponse] = await Promise.all([
        axios.get(`${API_URL}/api/admin/bigquery/stats?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/bigquery/tables`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      // Transformer les données pour correspondre au format attendu par le frontend
      const apiData = statsResponse.data.data;
      const transformedStats = {
        stats: {
          totalCost: apiData.global?.totalCost || '0',
          totalMB: apiData.global?.totalBytesFormatted?.includes('MB')
            ? parseFloat(apiData.global.totalBytesFormatted)
            : apiData.global?.totalBytesFormatted?.includes('GB')
              ? parseFloat(apiData.global.totalBytesFormatted) * 1024
              : 0,
          totalGB: apiData.global?.totalBytesFormatted?.includes('GB')
            ? apiData.global.totalBytesFormatted.replace(' GB', '')
            : (parseFloat(apiData.global?.totalBytesFormatted || '0') / 1024).toFixed(4),
          queryCount: apiData.global?.totalQueries || 0,
        },
        daily: (apiData.daily || []).map(d => ({
          date: d.date,
          cost: d.estimatedCost,
          queries: d.queryCount,
          mb: d.bytesFormatted
        })).reverse(), // Inverser pour avoir l'ordre chronologique
        byQueryType: (apiData.byType || []).map(t => ({
          queryType: t.type,
          _count: t.count,
          _sum: {
            bytesProcessed: t.bytesProcessed,
            estimatedCost: t.estimatedCost
          }
        }))
      };

      setBigQueryStats(transformedStats);

      // Transformer les données des tables
      const tablesData = tablesResponse.data.data;
      const transformedTables = (tablesData.tables || []).map(t => ({
        name: t.name,
        rowCount: parseInt(t.numRows) || 0,
        sizeMB: (parseInt(t.numBytes) / 1024 / 1024).toFixed(2),
        sizeFormatted: t.numBytesFormatted,
        partitioning: t.partitioning?.field || null,
        clustering: t.clustering || null,
      }));

      setBigQueryTables({
        tables: transformedTables,
        totals: tablesData.totals || null,
        dataset: tablesData.dataset
      });
    } catch (error) {
      console.error('Erreur lors du chargement des stats BigQuery:', error);
    } finally {
      setBigQueryLoading(false);
    }
  }, [token]);

  // Fetch quota status
  const fetchQuotaStatus = useCallback(async () => {
    try {
      setQuotaLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/quota-status`, {
        params: { fenetre: fenetreErreurs },
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotaStatus(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement du statut quota:', error);
      setQuotaStatus({
        status: 'ERROR',
        message: error.response?.data?.error || 'Erreur de chargement',
        checkedAt: new Date().toISOString()
      });
    } finally {
      setQuotaLoading(false);
    }
  }, [token, fenetreErreurs]);

  // Consommation d'emails Resend (envois et plafond du plan)
  const fetchResendUsage = useCallback(async () => {
    try {
      setResendLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/resend-usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResendUsage(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de la consommation Resend:', error);
      setResendUsage({
        success: false,
        error: error.response?.data?.error || 'Erreur de chargement',
        releveA: new Date().toISOString()
      });
    } finally {
      setResendLoading(false);
    }
  }, [token]);

  /**
   * Consommation Claude : le relevé d'Anthropic, et la ventilation de nos propres tables.
   *
   * Une seule requête pour les deux, mais **deux issues indépendantes** côté serveur : la
   * ventilation interne répond même quand l'Admin API refuse la clé. L'écran les rend donc
   * séparément au lieu de tout masquer sur le premier échec.
   */
  const fetchClaudeUsage = useCallback(async () => {
    try {
      setClaudeLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/claude-usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaudeUsage(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de la consommation Claude:', error);
      setClaudeUsage({
        success: false,
        error: error.response?.data?.error || 'Erreur de chargement'
      });
    } finally {
      setClaudeLoading(false);
    }
  }, [token]);

  // Fetch quota errors report
  const fetchQuotaErrors = useCallback(async () => {
    try {
      setQuotaErrorsLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/quota-errors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotaErrors(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des erreurs quota:', error);
      setQuotaErrors(null);
    } finally {
      setQuotaErrorsLoading(false);
    }
  }, [token]);

  /**
   * Le périmètre du MCP : ce qu'il lit, ce sur quoi il peut écrire.
   *
   * Deux choses que l'écran confondait. La whitelist d'écriture, seule affichée jusqu'ici, ne
   * disait rien des quatorze types de sources que le MCP sait lire, et n'offrait aucun moyen
   * d'autoriser un compte : la route existait, personne ne l'appelait.
   */
  const [mcpPerimetre, setMcpPerimetre] = useState(null);

  /**
   * Autoriser ou refuser un compte en écriture.
   *
   * Deux chemins selon qu'une entrée existe déjà : la suspendre plutôt que la supprimer garde
   * la trace de qui l'avait accordée, et la rend réversible sans ressaisie.
   */
  const basculerEcritureMcp = async (provider, compte) => {
    try {
      if (compte.entreeId) {
        await axios.patch(`${API_URL}/api/admin/mcp-write-access/${compte.entreeId}`,
          { enabled: !compte.autorise },
          { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API_URL}/api/admin/mcp-write-access`,
          { provider, accountId: compte.accountId, note: compte.nom },
          { headers: { Authorization: `Bearer ${token}` } });
      }
      fetchMcpData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  // Fetch MCP Write Access list and logs
  const fetchMcpData = useCallback(async () => {
    console.log('[MCP] Fetching data...');
    try {
      setMcpLoading(true);
      const [accessRes, logsRes, perimetreRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/mcp-write-access`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/mcp-write-logs?limit=50&offset=${(mcpLogsPage - 1) * 50}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/mcp-perimetre`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      console.log('[MCP] Access list received:', accessRes.data.data?.length, 'items');
      setMcpAccessList(accessRes.data.data || []);
      setMcpPerimetre(perimetreRes.data.data || null);
      setMcpWriteLogs(logsRes.data.logs || []);
      setMcpLogsTotal(logsRes.data.total || 0);
    } catch (error) {
      console.error('Erreur lors du chargement MCP:', error);
    } finally {
      setMcpLoading(false);
    }
  }, [token, mcpLogsPage]);

  // Fetch Tickets
  const fetchTickets = useCallback(async () => {
    try {
      setTicketsLoading(true);
      const params = { page: ticketsPage, limit: 20 };
      if (ticketsFilter) params.status = ticketsFilter;

      const [ticketsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/tickets`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        }),
        axios.get(`${API_URL}/api/tickets/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (ticketsRes.data.success) {
        setTickets(ticketsRes.data.tickets);
        setTicketsTotal(ticketsRes.data.pagination.total);
      }
      if (statsRes.data.success) {
        setTicketsStats(statsRes.data.stats);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des tickets:', error);
    } finally {
      setTicketsLoading(false);
    }
  }, [token, ticketsPage, ticketsFilter]);

  // Filter MCP Access list based on search
  const searchId = mcpSearch.accountId?.trim().toLowerCase() || '';
  const searchName = mcpSearch.accountName?.trim().toLowerCase() || '';

  const filteredMcpAccessList = mcpAccessList
    .filter(access => {
      const accessId = (access.accountId || '').toLowerCase();
      const accessNote = (access.note || '').toLowerCase();

      const matchesId = !searchId || accessId.includes(searchId);
      const matchesName = !searchName || accessNote.includes(searchName);
      return matchesId && matchesName;
    })
    .sort((a, b) => (a.note || '').localeCompare(b.note || ''));

  // Filter MCP Write Logs based on search (same criteria)
  const filteredMcpWriteLogs = mcpWriteLogs.filter(log => {
    const logAccountId = (log.accountId || '').toLowerCase();
    const matchesId = !searchId || logAccountId.includes(searchId);
    return matchesId;
  });

  // Remove MCP Write Access
  const removeMcpAccess = async (id) => {
    if (!window.confirm('Supprimer ce compte de la whitelist ?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/mcp-write-access/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMcpData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  // Toggle MCP Write Access enabled/disabled
  const toggleMcpAccess = async (id, enabled) => {
    const action = enabled ? 'activer' : 'désactiver';
    if (!window.confirm(`Voulez-vous ${action} ce compte ?`)) return;
    try {
      await axios.patch(`${API_URL}/api/admin/mcp-write-access/${id}`, { enabled }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMcpData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  // Sync Google Ads change history for all accounts
  const syncAllGoogleAdsHistory = async () => {
    try {
      setSyncingGoogleAds(true);
      const response = await axios.post(`${API_URL}/api/admin/action-logs/sync-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`✅ Synchronisation terminée: ${response.data.totalSynced} actions synchronisées, ${response.data.totalSkipped} ignorées`);
      fetchActionLogs(1, actionLogsFilter);
    } catch (error) {
      console.error('Erreur lors de la synchronisation:', error);
      alert('❌ Erreur lors de la synchronisation Google Ads');
    } finally {
      setSyncingGoogleAds(false);
    }
  };

  const handleActionLogsFilterChange = (field, value) => {
    console.log(`🔍 [FILTER] ${field} = "${value}"`);
    const newFilter = { ...actionLogsFilter, [field]: value };
    console.log('🔍 [FILTER] newFilter:', newFilter);
    setActionLogsFilter(newFilter);
    setActionLogsPage(1);
    fetchActionLogs(1, newFilter);
  };

  const clearActionLogsFilters = () => {
    const emptyFilter = { accountId: '', clientId: '', actionType: '', source: '' };
    setActionLogsFilter(emptyFilter);
    setActionLogsPage(1);
    fetchActionLogs(1, emptyFilter);
  };

  const isAdmin = user && user.role === 'ADMIN';
  /*
   * L'éditeur de Kokpit. Le verdict vient du serveur (`/api/auth/verify`) : l'adresse n'est
   * écrite que dans `backend/src/lib/editeurKokpit.js`, et le front ne la connaît pas.
   */
  const estEditeur = Boolean(user?.editeurKokpit);

  useEffect(() => {
    if (isAdmin) {
      fetchAdminData();
      fetchCustomers();
      fetchWhitelist();
      fetchLoginStats();
      fetchRecentLogins();
      fetchCronStatus();
    }
  }, [isAdmin, fetchAdminData, fetchCustomers, fetchWhitelist, fetchLoginStats, fetchRecentLogins, fetchCronStatus]);

  /**
   * Le compteur « Actifs maintenant » se rafraîchit tout seul.
   *
   * Une minute : c'est plus fin que le pas d'écriture de la présence (cinq minutes), donc rien
   * n'est manqué, et assez rare pour ne pas peser. Sans cela, le chiffre resterait figé à
   * l'ouverture de l'onglet et vieillirait en silence — le défaut qu'on ne voit jamais.
   */
  useEffect(() => {
    if (activeTab !== 'dashboard' || !isAdmin) return undefined;
    const minuteur = setInterval(() => { fetchAdminData(); }, 60000);
    return () => clearInterval(minuteur);
  }, [activeTab, isAdmin, fetchAdminData]);

  // Rafraîchir le statut CRON toutes les 5 secondes quand l'onglet cron est actif
  useEffect(() => {
    if (activeTab === 'cron' && isAdmin) {
      const interval = setInterval(() => {
        fetchCronStatus();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [activeTab, isAdmin, fetchCronStatus]);

  // Charger les action logs quand l'onglet action-logs est actif (seulement au premier chargement)
  useEffect(() => {
    if (activeTab === 'action-logs' && isAdmin && !actionLogs) {
      fetchActionLogs(1, actionLogsFilter);
      fetchActionLogsFilters();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAdmin]);

  // Charger les stats BigQuery quand l'onglet bigquery est actif
  useEffect(() => {
    if (activeTab === 'bigquery' && isAdmin && !bigQueryStats) {
      fetchBigQueryStats(bigQueryPeriod);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAdmin]);

  // Charger le statut quota quand l'onglet quotas ou errors est actif
  useEffect(() => {
    if ((activeTab === 'quotas' || activeTab === 'errors') && isAdmin && !quotaStatus) {
      fetchQuotaStatus();
    }
    if (activeTab === 'quotas' && isAdmin && !quotaErrors) {
      fetchQuotaErrors();
    }
    if (activeTab === 'quotas' && isAdmin && !resendUsage) {
      fetchResendUsage();
    }
    /*
     * ⚠️ `estEditeur`, et non `isAdmin` : la route est réservée à l'éditeur de Kokpit. Pour un
     * administrateur d'organisation cliente, l'appel repartait en 403 et la carte affichait
     * « État illisible : Réservé à l'éditeur… » — un message d'erreur là où il n'y a pas
     * d'erreur, sur un réglage qui ne le concerne pas.
     */
    if (activeTab === 'quotas' && estEditeur && !generationSurMesure) {
      axios.get(`${API_URL}/api/dashboards-sur-mesure/generation/interrupteur`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(({ data }) => setGenerationSurMesure(data))
        // Un échec de lecture ne doit pas masquer le reste de l'onglet : on le dit dans la
        // carte, et les trois jauges continuent de s'afficher.
        .catch((e) => setGenerationSurMesure({ erreur: e.response?.data?.error || e.message }));
    }
    if (activeTab === 'quotas' && isAdmin && !claudeUsage) {
      fetchClaudeUsage();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAdmin, estEditeur]);

  /**
   * Changer de fenêtre recharge, à la différence du premier affichage.
   *
   * L'effet ci-dessus ne tire qu'en l'absence de données : c'est ce qui évite de réinterroger
   * à chaque va-et-vient entre onglets. Mais il rend le sélecteur inopérant — le segment
   * s'allumait, les chiffres restaient ceux de la veille. Un effet distinct, déclenché par la
   * fenêtre seule, garde les deux comportements.
   */
  const premiereFenetre = useRef(true);
  useEffect(() => {
    if (premiereFenetre.current) { premiereFenetre.current = false; return; }
    if (activeTab === 'errors' && isAdmin) fetchQuotaStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fenetreErreurs]);

  // Charger les données MCP quand l'onglet mcp est actif
  useEffect(() => {
    if (activeTab === 'mcp' && isAdmin) {
      fetchMcpData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAdmin, mcpLogsPage]);

  // Charger les tickets quand l'onglet tickets est actif
  useEffect(() => {
    if (activeTab === 'tickets' && isAdmin) {
      fetchTickets();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAdmin, ticketsPage, ticketsFilter]);

  // Charger et sélectionner un ticket spécifique depuis l'URL (lien email)
  useEffect(() => {
    const loadInitialTicket = async () => {
      if (initialTicketId && activeTab === 'tickets' && isAdmin && !selectedTicket) {
        try {
          const response = await axios.get(`${API_URL}/api/tickets/${initialTicketId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.success) {
            setSelectedTicket(response.data.ticket);
            onTicketSelect?.(response.data.ticket.id);
          }
        } catch (error) {
          console.error('Erreur lors du chargement du ticket:', error);
        }
      }
    };
    loadInitialTicket();
  }, [initialTicketId, activeTab, isAdmin, token, selectedTicket, onTicketSelect]);


  const handleToggleBetaMode = async () => {
    try {
      setWhitelistLoading(true);
      const response = await axios.post(
        `${API_URL}/api/admin/beta-whitelist/toggle`,
        { enabled: !whitelistData.enabled },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setWhitelistData(response.data);
      alert(`Mode beta ${response.data.enabled ? 'activé' : 'désactivé'} avec succès !`);
    } catch (error) {
      console.error('Erreur lors du changement du mode beta:', error);
      // Relayer le message du serveur : c'est lui qui explique le refus anti-verrouillage
      // (code AUTO_VERROUILLAGE), inexploitable sous un message générique.
      alert(error.response?.data?.error || 'Erreur lors du changement du mode beta');
    } finally {
      setWhitelistLoading(false);
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      alert('Veuillez entrer une adresse email valide');
      return;
    }

    try {
      setWhitelistLoading(true);
      const response = await axios.post(
        `${API_URL}/api/admin/beta-whitelist/add`,
        { email: newEmail },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setWhitelistData(response.data);
      setNewEmail('');
      alert('Email ajouté avec succès !');
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'email:', error);
      alert(error.response?.data?.error || 'Erreur lors de l\'ajout de l\'email');
    } finally {
      setWhitelistLoading(false);
    }
  };

  const handleRemoveEmail = async (email) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${email} de la liste blanche ?`)) {
      return;
    }

    try {
      setWhitelistLoading(true);
      const response = await axios.post(
        `${API_URL}/api/admin/beta-whitelist/remove`,
        { email },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setWhitelistData(response.data);
      alert('Email supprimé avec succès !');
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'email:', error);
      alert(error.response?.data?.error || 'Erreur lors de la suppression de l\'email');
    } finally {
      setWhitelistLoading(false);
    }
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0 €';
    return Math.round(amount).toLocaleString('fr-FR') + ' €';
  };

  if (!isAdmin) {
    return (
      <div className="admin-access-denied">
        <AlertCircle size={48} color="#e74c3c" />
        <h2>Accès refusé</h2>
        <p>Vous n'avez pas les droits d'accès à cette section.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Chargement du panel administrateur...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        <AlertCircle size={48} color="#e74c3c" />
        <h2>Erreur de chargement</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Réessayer</button>
      </div>
    );
  }

  return (
    <div className="step-content">
      {/* Header avec breadcrumb */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Shield size={18} style={{ verticalAlign: 'middle' }} />
          {activeTab === 'dashboard' ? (
            'Administration'
          ) : (
            <>
              <span
                style={{ cursor: 'pointer', opacity: 0.7 }}
                onClick={() => onTabChange('dashboard')}
                onMouseEnter={(e) => e.target.style.opacity = 1}
                onMouseLeave={(e) => e.target.style.opacity = 0.7}
              >
                Administration
              </span>
              <span style={{ color: '#9ca3af' }}>&gt;</span>
              <span>
                {activeTab === 'customers' && 'Clients'}
                {activeTab === 'beta' && 'Mode Beta'}
                {activeTab === 'cron' && 'Tâches CRON'}
                {activeTab === 'action-logs' && 'Journal'}
                {activeTab === 'bigquery' && 'BigQuery'}
                {activeTab === 'quotas' && 'Quotas'}
                {activeTab === 'mcp' && 'MCP Write Access'}
                {activeTab === 'tickets' && 'Tickets Support'}
                {activeTab === 'errors' && 'Erreurs API'}
              </span>
            </>
          )}
        </h2>
        {activeTab === 'dashboard' && (
          <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
            Bienvenue {user.name}
          </p>
        )}
      </div>

      {activeTab === 'dashboard' && adminData && (
        <div className="admin-dashboard">
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <div className="stat-icon users">
                <Users size={24} />
              </div>
              <div className="stat-content">
                <h3>Utilisateurs Total</h3>
                <p className="stat-number">{adminData.totalUsers || 0}</p>
              </div>
            </div>

            {/*
              Actifs maintenant, et non « connectés » : rien ne suit les sessions ouvertes — le
              jeton vaut sept jours et personne ne se déconnecte. Ce qu'on sait, c'est qui a émis
              une requête récemment. L'infobulle nomme les personnes, un compteur seul ne disant
              pas s'il s'agit de l'équipe ou d'un client.
            */}
            <div
              className="admin-stat-card"
              title={adminData.presence?.personnes?.length
                ? adminData.presence.personnes.map(p => p.name || p.email).join('\n')
                : 'Personne sur la période'}
            >
              <div className="stat-icon active">
                <Activity size={24} />
              </div>
              <div className="stat-content">
                <h3>Actifs maintenant</h3>
                <p className="stat-number">{adminData.presence?.presents ?? 0}</p>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  {adminData.presence?.fenetreMinutes ?? 15} dernières minutes
                </span>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="stat-icon active">
                <UserCheck size={24} />
              </div>
              <div className="stat-content">
                <h3>Abonnements Actifs</h3>
                <p className="stat-number">{adminData.activeSubscriptions || 0}</p>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="stat-icon revenue">
                <DollarSign size={24} />
              </div>
              <div className="stat-content">
                <h3>Revenus Mensuels</h3>
                <p className="stat-number">{formatCurrency(adminData.monthlyRevenue || 0)}</p>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="stat-icon revenue">
                <TrendingUp size={24} />
              </div>
              <div className="stat-content">
                <h3>Revenus Annuels</h3>
                <p className="stat-number">{formatCurrency((adminData.monthlyRevenue || 0) * 12)}</p>
              </div>
            </div>

            {/*
              Les analyses produites. Le chiffre existait dans la réponse depuis toujours sans
              jamais être affiché ; on y joint désormais leur origine et ce qu'elles consomment.

              « Dashboards » contre « autres » : les analyses de bibliothèque, et celles d'avant
              la mise en place du suivi, tombent dans la seconde colonne — on ne sait pas d'où
              elles viennent, et le dire vaut mieux que les ranger d'office.
            */}
            <div
              className="admin-stat-card"
              title={`${adminData.analyses?.dashboards ?? 0} depuis un dashboard, `
                + `${adminData.analyses?.autres ?? 0} ailleurs (bibliothèque ou origine inconnue)`}
            >
              <div className="stat-icon active">
                <Sparkles size={24} />
              </div>
              <div className="stat-content">
                <h3>Analyses IA</h3>
                <p className="stat-number">{adminData.analyses?.total ?? adminData.totalAnalyses ?? 0}</p>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  {adminData.analyses?.fenetreJours ?? 30} derniers jours
                  {adminData.analyses?.dashboards > 0 && (
                    <> · {adminData.analyses.dashboards} depuis un dashboard</>
                  )}
                  {adminData.analyses?.cout > 0 && (
                    <> · ${adminData.analyses.cout.toFixed(2)}</>
                  )}
                </span>
              </div>
            </div>

          </div>

          <div className="admin-charts">
            <div className="chart-container">
              <h3>Répartition par Plan</h3>
              <div className="plan-distribution plan-distribution-row">
                <div className="plan-item solo">
                  <span className="plan-name">Solo (19€)</span>
                  <span className="plan-count">{adminData.planDistribution?.SOLO || 0}</span>
                </div>
                <div className="plan-item team">
                  <span className="plan-name">Team (69€)</span>
                  <span className="plan-count">{adminData.planDistribution?.TEAM || 0}</span>
                </div>
                <div className="plan-item agency">
                  <span className="plan-name">Agency (179€)</span>
                  <span className="plan-count">{adminData.planDistribution?.AGENCY || 0}</span>
                </div>
              </div>
            </div>

            {/* Graphiques des connexions */}
            <div className={`chart-container ${loginStatsLoading ? 'filtering-indicator' : ''}`}>
              {loginStatsLoading ? (
                <div className="loading">Chargement...</div>
              ) : loginStats ? (
                <div className="login-chart-container">
                  {/* Graphiques */}
                  <div className="statistics-charts">
                    {/* Histogramme connexions par jour */}
                    <div className="statistics-chart-card statistics-chart-large">
                      <h3>
                        <Calendar size={18} />
                        Connexions par jour
                      </h3>
                      {dailyChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={dailyChartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="dateLabel" stroke="#6b7280" tick={{ fontSize: 12 }} />
                            <YAxis stroke="#6b7280" tick={{ fontSize: 12 }} />
                            <Tooltip content={<CustomBarTooltip />} />
                            <Legend />
                            {/* Empilées, comme dans l'écran Statistiques : la hauteur totale
                                reste le nombre de connexions du jour, et la barre en dit une
                                de plus — qui, des membres ou des clients, s'est connecté. */}
                            <Bar dataKey="members" name="Membres" fill={COULEURS_POPULATION.members} stackId="a" />
                            <Bar dataKey="clients" name="Clients" fill={COULEURS_POPULATION.clients} stackId="a" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="statistics-no-data">
                          <p>Aucune donnée sur cette période</p>
                        </div>
                      )}
                    </div>

                    {/* Répartition par activité */}
                    <div className="statistics-chart-card statistics-chart-small">
                      <h3>
                        <Activity size={18} />
                        Répartition par activité
                      </h3>
                      {activityChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <PieChart>
                            <Pie
                              data={activityChartData}
                              cx="50%"
                              cy="45%"
                              innerRadius={50}
                              outerRadius={80}
                              dataKey="value"
                              label={false}
                            >
                              {activityChartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.fill} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomPieTooltip />} />
                            <Legend
                              verticalAlign="bottom"
                              height={36}
                              formatter={(value, entry) => `${value} (${entry.payload.percentage}%)`}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="statistics-no-data">
                          <p>Aucune donnée sur cette période</p>
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              ) : (
                <div className="error">Erreur de chargement des statistiques</div>
              )}
            </div>

            {/* Tableau des dernières connexions avec onglets */}
            <div className="chart-container">
              {/* Onglets Dernières connexions / Membres / Clients */}
              <div className="statistics-tabs-section">
                <div className="statistics-tabs">
                  <button
                    className={`statistics-tab ${loginsTab === 'recent' ? 'active' : ''}`}
                    onClick={() => setLoginsTab('recent')}
                  >
                    <History size={16} />
                    Dernières connexions
                  </button>
                  <button
                    className={`statistics-tab ${loginsTab === 'members' ? 'active' : ''}`}
                    onClick={() => setLoginsTab('members')}
                  >
                    <Users size={16} />
                    Membres ({recensement.membres.length})
                  </button>
                  <button
                    className={`statistics-tab ${loginsTab === 'clients' ? 'active' : ''}`}
                    onClick={() => setLoginsTab('clients')}
                  >
                    <UserCheck size={16} />
                    Clients ({recensement.clients.length})
                  </button>
                </div>
              </div>

              {/**
                * Deux recensements et une liste, et non trois vues du même filtre.
                *
                * Les onglets « Membres » et « Clients » filtraient les cent dernières connexions :
                * une personne absente depuis trois mois n'y figurait pas. Or c'est souvent elle
                * qu'on cherche — un siège payé et inutilisé, un accès client jamais ouvert.
                * Ils listent désormais tout le parc, comme l'écran Statistiques le fait pour une
                * organisation.
                */}
              {loginsTab !== 'recent' ? (
                <div className="recent-logins-container">
                  <div className="recent-logins-table-wrapper">
                    <table className="statistics-table admin-logins-table">
                      <thead>
                        {loginsTab === 'members' ? (
                          <tr>
                            <EnTeteMembres cle="utilisateur" style={{ width: '26%' }}>Membre</EnTeteMembres>
                            <EnTeteMembres cle="plan" style={{ width: '10%' }}>Plan</EnTeteMembres>
                            <EnTeteMembres cle="organisation" style={{ width: '20%' }}>Organisation</EnTeteMembres>
                            <EnTeteMembres cle="role" style={{ width: '10%' }}>Rôle</EnTeteMembres>
                            <EnTeteMembres cle="lastLoginAt" style={{ width: '18%' }}>Dernière connexion</EnTeteMembres>
                            <EnTeteMembres cle="connexions" style={{ width: '8%' }}>Connexions</EnTeteMembres>
                            <EnTeteMembres cle="activites" style={{ width: '8%' }}>
                              Activités <span className="entete-precision">30 j</span>
                            </EnTeteMembres>
                          </tr>
                        ) : (
                          <tr>
                            <EnTeteClients cle="utilisateur" style={{ width: '26%' }}>Client</EnTeteClients>
                            <EnTeteClients cle="compte" style={{ width: '18%' }}>Compte</EnTeteClients>
                            <EnTeteClients cle="invitePar" style={{ width: '18%' }}>Invité par</EnTeteClients>
                            <EnTeteClients cle="lastLoginAt" style={{ width: '18%' }}>Dernière connexion</EnTeteClients>
                            <EnTeteClients cle="connexions" style={{ width: '10%' }}>Connexions</EnTeteClients>
                            <EnTeteClients cle="activites" style={{ width: '10%' }}>
                              Activités <span className="entete-precision">30 j</span>
                            </EnTeteClients>
                          </tr>
                        )}
                      </thead>
                      <tbody>
                        {(() => {
                          const lignes = loginsTab === 'members' ? recensement.membres : recensement.clients;
                          const triees = trierRecensement(lignes, triRecensement[loginsTab]);

                          if (triees.length === 0) {
                            return (
                              <tr>
                                <td colSpan={loginsTab === 'members' ? 7 : 6} style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>
                                  {loginsTab === 'members' ? 'Aucun membre' : 'Aucun accès client'}
                                </td>
                              </tr>
                            );
                          }

                          return triees.map(p => (
                            <tr
                              key={p.userId}
                              className="clickable-row"
                              onClick={() => ouvrirDetail(p)}
                            >
                              <td className="member-cell">
                                {/* Le flex vit sur ce conteneur, jamais sur le `td`.
                                    Un `td` en `display: flex` quitte le modèle des colonnes : sa largeur se
                                    calcule alors sur son propre contenu, et le bloc nom + e-mail, qui porte
                                    `min-width: 0`, se réduisait à zéro — les noms avaient disparu. */}
                                <div className="member-cell-contenu">
                                  {p.picture ? (
                                    <img src={p.picture} alt="" className="member-avatar" />
                                  ) : (
                                    <div className={`member-avatar-placeholder ${p.role === 'CLIENT' ? 'client' : ''}`}>
                                      {(p.name || p.email || '?')[0].toUpperCase()}
                                    </div>
                                  )}
                                  <div className="member-info">
                                    <span className="member-name">{p.name || 'Sans nom'}</span>
                                    <span className="member-email">{p.email}</span>
                                  </div>
                                </div>
                              </td>

                              {loginsTab === 'members' ? (
                                <>
                                  <td>
                                    <span className={`plan-badge ${(p.plan || 'solo').toLowerCase()}`}>
                                      {getPlanDisplayName(p.plan)}
                                    </span>
                                  </td>
                                  <td>
                                    {p.organisation
                                      ? <span className="organization-name">{p.organisation}</span>
                                      : <span className="customer-org-absent">Aucune</span>}
                                  </td>
                                  <td>
                                    <span className={`role-badge role-${(p.organisationRole || 'member').toLowerCase()}`}>
                                      {p.organisationRole === 'OWNER' ? 'Propriétaire'
                                        : p.organisationRole === 'ADMIN' ? 'Admin' : 'Membre'}
                                    </span>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td>
                                    {p.compteClient
                                      ? <span className="client-card-name">{p.compteClient}</span>
                                      : <span className="customer-org-absent">Aucun</span>}
                                  </td>
                                  <td>
                                    <span className="organization-name">{p.invitePar || '-'}</span>
                                  </td>
                                </>
                              )}

                              <td className="customer-date">
                                {/* « Jamais » est une information : une cellule vide se lirait
                                    comme une donnée qui n'a pas chargé. */}
                                {p.lastLoginAt
                                  ? <span className="last-login"><Clock size={14} />{formatFullDate(p.lastLoginAt)}</span>
                                  : <span style={{ color: '#9ca3af' }}>Jamais</span>}
                              </td>

                              <td style={{ textAlign: 'right' }}>{p.totalConnections}</td>

                              <td>
                                <div className="activity-bars">
                                  {(() => {
                                    const pastilles = TYPES_ACTIVITE
                                      .filter(a => (p.activities || {})[a] > 0);

                                    if (pastilles.length === 0) {
                                      const horsFenetre = (() => {
                                        const debut = loginStats?.period?.start;
                                        return p.lastLoginAt && debut
                                          && new Date(p.lastLoginAt) < new Date(debut);
                                      })();
                                      return (
                                        <span
                                          style={{ color: '#9ca3af', fontSize: '12px', cursor: 'help' }}
                                          title={horsFenetre
                                            ? "Dernière visite antérieure aux trente jours mesurés : l'activité de cette personne est hors fenêtre."
                                            : 'Aucune activité mesurée sur les trente derniers jours.'}
                                        >
                                          {horsFenetre ? 'hors fenêtre' : '—'}
                                        </span>
                                      );
                                    }

                                    return pastilles.map(activite => (
                                      <span
                                        key={activite}
                                        className="activity-badge"
                                        style={{ background: ACTIVITY_COLORS[activite] }}
                                        title={`${ACTIVITY_LABELS[activite]} : ${p.activities[activite]}`}
                                      >
                                        {p.activities[activite]}
                                      </span>
                                    ));
                                  })()}
                                </div>
                              </td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : recentLoginsLoading ? (
                <div className="loading">Chargement...</div>
              ) : recentLogins ? (
                <div className="recent-logins-container">
                  <div className="recent-logins-table-wrapper">
                    <table className="statistics-table admin-logins-table">
                      <thead>
                        <tr>
                          <EnTete cle="utilisateur" style={{ width: '25%' }}>Utilisateur</EnTete>
                          <EnTete cle="plan" style={{ width: '10%' }}>Plan</EnTete>
                          <EnTete cle="organisation" style={{ width: '20%' }}>Organisation</EnTete>
                          <EnTete cle="type" style={{ width: '10%' }}>Type</EnTete>
                          <EnTete cle="lastLoginAt" style={{ width: '20%' }}>Dernière connexion</EnTete>
                          {/* La fenêtre est dite, parce qu'elle diffère de celle des lignes :
                              la liste prend les cent dernières connexions **sans borne de date**,
                              les pastilles comptent sur trente jours. Un visiteur plus ancien
                              apparaît donc sans activité — ce qui se lirait comme « n'a rien
                              fait » au lieu de « hors de la fenêtre mesurée ». */}
                          <EnTete cle="activites" style={{ width: '15%' }}>
                            Activités <span className="entete-precision">30 j</span>
                          </EnTete>
                        </tr>
                      </thead>
                      <tbody>
                        {lignesTriees.length > 0 ? (
                          lignesTriees.map(login => (
                            <tr
                              key={login.userId}
                              className="clickable-row"
                              onClick={() => ouvrirDetail({
                                userId: login.userId,
                                // Cette liste nomme les champs autrement que le recensement.
                                name: login.userName,
                                email: login.userEmail,
                                picture: login.userPicture,
                                role: login.userRole
                              })}
                            >
                              <td className="member-cell">
                                {/* Le flex vit sur ce conteneur, jamais sur le `td`.
                                    Un `td` en `display: flex` quitte le modèle des colonnes : sa largeur se
                                    calcule alors sur son propre contenu, et le bloc nom + e-mail, qui porte
                                    `min-width: 0`, se réduisait à zéro — les noms avaient disparu. */}
                                <div className="member-cell-contenu">
                                  {login.userPicture ? (
                                    <img src={login.userPicture} alt="" className="member-avatar" />
                                  ) : (
                                    <div className={`member-avatar-placeholder ${login.userRole === 'CLIENT' ? 'client' : ''}`}>
                                      {(login.userName || login.userEmail || '?')[0].toUpperCase()}
                                    </div>
                                  )}
                                  <div className="member-info">
                                    <span className="member-name">{login.userName || 'Sans nom'}</span>
                                    <span className="member-email">{login.userEmail}</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className={`plan-badge ${(login.planType || 'solo').toLowerCase()}`}>
                                  {getPlanDisplayName(login.planType)}
                                </span>
                              </td>
                              <td>
                                <span className="organization-name">
                                  {login.organizationName || login.accountName || '-'}
                                </span>
                              </td>
                              <td>
                                {login.userRole === 'CLIENT' ? (
                                  <span className="role-badge role-client">Client</span>
                                ) : login.organizationRole === 'OWNER' ? (
                                  <span className="role-badge role-owner">Propriétaire</span>
                                ) : login.organizationRole === 'ADMIN' ? (
                                  <span className="role-badge role-admin">Admin</span>
                                ) : (
                                  <span className="role-badge role-member">Membre</span>
                                )}
                              </td>
                              <td>
                                <span className="last-login">
                                  <Clock size={14} />
                                  {formatFullDate(login.loginAt)}
                                </span>
                              </td>
                              <td>
                                {/* Les totaux de la **période**, et non le décompte de la page
                                    affichée : une pastille qui ne compterait que l'échantillon
                                    dirait moins que ce que la période contient. `LOGIN` est
                                    écarté — ce n'est pas une activité fonctionnelle. */}
                                <div className="activity-bars">
                                  {(() => {
                                    const totaux = totauxParUtilisateur[login.userId] || {};
                                    const pastilles = TYPES_ACTIVITE
                                      .filter(a => totaux[a] > 0);

                                    if (pastilles.length === 0) {
                                      // Deux absences bien distinctes : rien fait ces trente
                                      // jours, ou dernière visite antérieure à la fenêtre.
                                      const horsFenetre = (() => {
                                        const debut = loginStats?.period?.start;
                                        return debut && new Date(login.loginAt) < new Date(debut);
                                      })();
                                      return (
                                        <span
                                          style={{ color: '#9ca3af', fontSize: '12px', cursor: 'help' }}
                                          title={horsFenetre
                                            ? "Dernière visite antérieure aux trente jours mesurés : l'activité de cette personne est hors fenêtre."
                                            : 'Aucune activité mesurée sur les trente derniers jours.'}
                                        >
                                          {horsFenetre ? 'hors fenêtre' : '—'}
                                        </span>
                                      );
                                    }

                                    return pastilles.map(activite => (
                                      <span
                                        key={activite}
                                        className="activity-badge"
                                        style={{ background: ACTIVITY_COLORS[activite] }}
                                        title={`${ACTIVITY_LABELS[activite]} : ${totaux[activite]}`}
                                      >
                                        {totaux[activite]}
                                      </span>
                                    ));
                                  })()}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>
                              {loginsTab === 'recent' ? 'Aucune connexion récente' :
                               loginsTab === 'members' ? 'Aucun membre dans les dernières connexions' :
                               'Aucun client dans les dernières connexions'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                </div>
              ) : (
                <div className="error">Erreur de chargement des dernières connexions</div>
              )}
            </div>

            {/* Boxes d'activité par compte (30 derniers jours) */}
            <div className="activity-details-section">
              {/* Dashboard */}
              <div className="activity-detail-card">
                <h3 style={{ color: ACTIVITY_COLORS.DASHBOARD }}>
                  <BarChart3 size={18} />
                  Dashboard
                </h3>
                {topAccountsByActivity.DASHBOARD?.length > 0 ? (
                  <div className="activity-detail-list">
                    {topAccountsByActivity.DASHBOARD.map((item, idx) => (
                      <div key={idx} className="activity-detail-item">
                        <div className="activity-detail-user">
                          {item.picture ? (
                            <img src={item.picture} alt="" className="activity-detail-avatar" />
                          ) : (
                            <div className="activity-detail-avatar-placeholder">
                              {(item.name || '?')[0].toUpperCase()}
                            </div>
                          )}
                          <span className="activity-detail-name">{item.name}</span>
                        </div>
                        <div className="activity-detail-right">
                          {item.plan && (
                            <span className={`plan-badge-box ${item.plan.toLowerCase()}`}>
                              {getPlanDisplayName(item.plan)}
                            </span>
                          )}
                          <span className="activity-detail-count" style={{ background: ACTIVITY_COLORS.DASHBOARD }}>{item.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="activity-detail-empty">Aucune activité</div>
                )}
              </div>

              {/* Outils */}
              <div className="activity-detail-card">
                <h3 style={{ color: ACTIVITY_COLORS.TOOLS }}>
                  <Activity size={18} />
                  Outils
                </h3>
                {topAccountsByActivity.TOOLS?.length > 0 ? (
                  <div className="activity-detail-list">
                    {topAccountsByActivity.TOOLS.map((item, idx) => (
                      <div key={idx} className="activity-detail-item">
                        <div className="activity-detail-user">
                          {item.picture ? (
                            <img src={item.picture} alt="" className="activity-detail-avatar" />
                          ) : (
                            <div className="activity-detail-avatar-placeholder">
                              {(item.name || '?')[0].toUpperCase()}
                            </div>
                          )}
                          <span className="activity-detail-name">{item.name}</span>
                        </div>
                        <div className="activity-detail-right">
                          {item.plan && (
                            <span className={`plan-badge-box ${item.plan.toLowerCase()}`}>
                              {getPlanDisplayName(item.plan)}
                            </span>
                          )}
                          <span className="activity-detail-count" style={{ background: ACTIVITY_COLORS.TOOLS }}>{item.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="activity-detail-empty">Aucune activité</div>
                )}
              </div>

              {/* MCP */}
              <div className="activity-detail-card">
                <h3 style={{ color: ACTIVITY_COLORS.MCP }}>
                  <Cpu size={18} />
                  MCP
                </h3>
                {loginStats?.mcp?.byUser?.length > 0 ? (
                  <div className="activity-detail-list">
                    {loginStats.mcp.byUser.map((item, idx) => (
                      <div key={idx} className="activity-detail-item">
                        <div className="activity-detail-user">
                          {item.picture ? (
                            <img src={item.picture} alt="" className="activity-detail-avatar" />
                          ) : (
                            <div className="activity-detail-avatar-placeholder">
                              {(item.name || '?')[0].toUpperCase()}
                            </div>
                          )}
                          <span className="activity-detail-name">{item.name}</span>
                        </div>
                        <div className="activity-detail-right">
                          {item.plan && (
                            <span className={`plan-badge-box ${item.plan.toLowerCase()}`}>
                              {getPlanDisplayName(item.plan)}
                            </span>
                          )}
                          <span className="activity-detail-count" style={{ background: ACTIVITY_COLORS.MCP }}>{item.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="activity-detail-empty">Aucune activité</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'customers' && (
        <div className="admin-customers">
          {/**
            * En-tête repensé : ce qu'on lit à gauche, ce qu'on actionne à droite.
            *
            * Le bouton « Actualiser » occupait la première ligne à lui seul et le décompte
            * tombait dessous, en petit, sans titre au-dessus : l'écran s'ouvrait sur une
            * commande plutôt que sur son contenu. Le décompte devient l'information de tête, et
            * se ventile — un total sans répartition ne dit pas grand-chose d'un parc.
            */}
          <div className="customers-header">
            {/**
              * Tout sur une ligne : l'intitulé, le total, puis la répartition en pastilles.
              *
              * Les points médians alignaient trois nombres au même poids visuel, sans dire
              * lesquels composent lequel. Une pastille par rôle rend le rapport lisible d'un
              * coup d'œil — et un rôle absent ne laisse pas de pastille vide derrière lui.
              */}
            <div className="customers-header-info">
              <span className="customers-label">Utilisateurs</span>
              <span className="customers-count-total">{customers.length}</span>
              <span className="customers-roles">
                {(() => {
                  const parRole = customers.reduce((acc, c) => {
                    acc[c.role] = (acc[c.role] || 0) + 1;
                    return acc;
                  }, {});
                  const pastilles = [
                    { cle: 'ADMIN', n: parRole.ADMIN, mot: 'admin' },
                    { cle: 'USER', n: parRole.USER, mot: 'membre' },
                    { cle: 'CLIENT', n: parRole.CLIENT, mot: 'client' }
                  ].filter(p => p.n > 0);

                  if (pastilles.length === 0) {
                    return <span className="customers-role-vide">aucun utilisateur</span>;
                  }

                  return pastilles.map(p => (
                    <span key={p.cle} className={`customers-role customers-role-${p.cle.toLowerCase()}`}>
                      <strong>{p.n}</strong> {p.mot}{p.n > 1 ? 's' : ''}
                    </span>
                  ));
                })()}
              </span>
            </div>
            <button
              className="customers-refresh-btn"
              onClick={refreshCustomers}
              disabled={refreshingCustomers}
              title="Rafraîchir les données"
            >
              <RefreshCw size={14} className={refreshingCustomers ? 'spin-animation' : ''} style={refreshingCustomers ? { animation: 'spin 1s linear infinite' } : {}} />
              {refreshingCustomers ? 'Actualisation…' : 'Actualiser'}
            </button>
          </div>
          
          <div className="customers-table-container">
            <table className="customers-table">
              <thead>
                <tr>
                  <th>Utilisateur</th>
                  <th>Organisation</th>
                  <th>Plan</th>
                  <th>Statut</th>
                  <th>Inscrit le</th>
                  <th>Dernière connexion</th>
                  {/* « Parc » sans précision, parce que la portée change d'une ligne à
                      l'autre : l'organisation pour un membre, le seul périmètre ouvert pour
                      un accès client. Chaque cellule le dit dans son infobulle. */}
                  <th>Parc</th>
                  {/* Le mois civil, pas trente jours glissants : `getUserUsage` borne au
                      premier et au dernier jour du mois. L'ancien intitulé faisait chercher une
                      panne le 2 du mois, quand les compteurs repartent de zéro. */}
                  <th>Usage (mois en cours)</th>
                </tr>
              </thead>
              <tbody>
                {customers.map(customer => (
                  <tr key={customer.id}>
                    <td>
                      <div className="customer-info">
                        <strong>{customer.name || 'Nom non défini'}</strong>
                        {BADGE_DE_ROLE[customer.role] && (
                          <span
                            className={`role-badge ${BADGE_DE_ROLE[customer.role].classe}`}
                            style={{
                              marginLeft: '8px',
                              padding: '2px 6px',
                              backgroundColor: BADGE_DE_ROLE[customer.role].fond,
                              color: BADGE_DE_ROLE[customer.role].texte,
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              borderRadius: '4px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.025em'
                            }}
                          >
                            {BADGE_DE_ROLE[customer.role].libelle}
                          </span>
                        )}
                        <br />
                        <small>{customer.email}</small>
                        {customer.role === 'CLIENT' && customer.clientInfo && (
                          <>
                            <br />
                            {/* Icônes Lucide, comme partout ailleurs : l'émoji 📊 disait
                                « graphique » pour désigner un client, et son rendu change d'un
                                système à l'autre. `Building2` est déjà l'icône du client dans le
                                Gestionnaire — le même objet garde le même signe. */}
                            <small style={{ color: '#6b7280', fontStyle: 'italic', display: 'inline-block', lineHeight: 1.6 }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <Building2 size={12} strokeWidth={2} />
                                Compte : <strong>{customer.clientInfo.clientName}</strong>
                              </span>
                              <br />
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <UserCheck size={12} strokeWidth={2} />
                                Géré par : {customer.clientInfo.adminName || customer.clientInfo.adminEmail}
                              </span>
                            </small>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      {/* L'organisation qui porte l'abonnement — celle dont l'utilisateur est
                          propriétaire —, sinon celle où il est rattaché, avec son rôle. Un accès
                          client n'est membre d'aucune organisation : le serveur lui rend celle de
                          son gestionnaire, avec « client » pour statut. Un utilisateur sans
                          organisation est dit tel, pas laissé vide : une cellule blanche se lit
                          comme une donnée qui n'a pas chargé. */}
                      {customer.organisation ? (
                        <div className="customer-org">
                          <span className="customer-org-name" title={`Identifiant : ${customer.organisation.id}`}>
                            {customer.organisation.nom}
                          </span>
                          <span className="customer-org-role">
                            {customer.organisation.role === 'OWNER' ? 'propriétaire'
                              : customer.organisation.role === 'ADMIN' ? 'administrateur'
                                : customer.organisation.role === 'CLIENT' ? 'client'
                                  : 'membre'}
                            {customer.organisation.autres > 0 && ` · +${customer.organisation.autres}`}
                          </span>
                        </div>
                      ) : (
                        <span className="customer-org-absent">Aucune</span>
                      )}
                    </td>
                    <td>
                      <span className={`plan-badge ${(customer.subscription?.plan || 'solo').toLowerCase()}`}>
                        {getPlanDisplayName(customer.subscription?.plan)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${(customer.subscription?.status || 'INACTIVE').toLowerCase()}`}>
                        {customer.subscription?.status || 'INACTIVE'}
                      </span>
                    </td>
                    {/* Les deux dates au corps des autres mentions secondaires de la ligne :
                        à taille pleine, elles pesaient autant que le nom du compte, alors
                        qu'on ne les lit qu'en second. */}
                    <td className="customer-date">{formatDate(customer.createdAt)}</td>
                    <td className="customer-date">
                      {/* La date de création en repli se lisait comme une connexion. On distingue
                          les deux : « jamais » est une information, une date d'inscription
                          déguisée en visite n'en est pas une. */}
                      {customer.lastLoginAt
                        ? formatDate(customer.lastLoginAt)
                        : <span style={{ color: '#9ca3af' }}>Jamais</span>}
                    </td>
                    <td>
                      {(() => {
                        /**
                         * Deux portées sous le même intitulé, et il faut qu'on les distingue
                         * d'un coup d'œil : un accès client ne voit qu'une part du parc de
                         * l'agence, ses nombres sont les siens ; un membre porte ceux de son
                         * organisation sans en répondre, d'où l'estompage — sans quoi on
                         * additionnerait deux fois le même parc en lisant la colonne.
                         */
                        const parc = customer.parc || {};
                        const estClient = Boolean(parc.perimetreClient);
                        const nombre = (n, singulier, pluriel) =>
                          `${n ?? 0} ${(n ?? 0) > 1 ? pluriel : singulier}`;
                        const infobulles = estClient
                          ? {
                            utilisateurs: "Personnes disposant d'un accès à ce même périmètre, celle-ci comprise.",
                            connecteurs: 'Connecteurs dont au moins un compte lui est ouvert.',
                            comptes: 'Comptes rattachés aux sites de ses clients — ce qu\'il peut réellement ouvrir, et non le parc de l\'agence.',
                            clients: 'Clients auxquels son accès a été ouvert.'
                          }
                          : {
                            utilisateurs: "Membres de l'organisation. Les invitations en attente n'y sont pas : un siège réservé n'est pas un utilisateur.",
                            connecteurs: 'Connexions actives aux régies et aux outils de mesure (Google, Microsoft, Meta, GA4, Matomo…).',
                            comptes: "Comptes actifs de la bibliothèque, tous types confondus — c'est le décompte que le quota d'abonnement compare.",
                            clients: 'Clients créés dans le Gestionnaire, indépendamment du nombre d\'accès ouverts à chacun.'
                          };
                        const herite = !estClient && !parc.estProprietaire;

                        return (
                          <div
                            className={`parc-info${herite ? ' parc-info-herite' : ''}`}
                            title={estClient
                              ? 'Périmètre ouvert à cet accès client.'
                              : herite
                                ? "Parc de l'organisation, dont cette personne n'est pas propriétaire."
                                : "Parc de l'organisation."}
                          >
                            <small title={infobulles.utilisateurs}>
                              <Users size={12} strokeWidth={2} /> {nombre(parc.utilisateurs, 'utilisateur', 'utilisateurs')}
                            </small>
                            <small title={infobulles.connecteurs}>
                              <Database size={12} strokeWidth={2} /> {nombre(parc.connecteurs, 'connecteur', 'connecteurs')}
                            </small>
                            <small title={infobulles.comptes}>
                              <Package size={12} strokeWidth={2} /> {nombre(parc.comptes, 'compte', 'comptes')}
                            </small>
                            <small title={infobulles.clients}>
                              <Building2 size={12} strokeWidth={2} /> {nombre(parc.clients, 'client', 'clients')}
                            </small>
                          </div>
                        );
                      })()}
                    </td>
                    <td>
                      <div className="usage-info">
                        {/* Séances de consultation, dédoublonnées sur cinq minutes par
                            `loginTracker` : dix allers-retours dans la même minute comptent
                            pour un. L'infobulle le dit, pour qu'on ne lise pas « ouvertures ». */}
                        <small title="Séances de consultation d'un tableau de bord. Deux ouvertures à moins de cinq minutes d'intervalle comptent pour une.">
                          Dashboards: {customer.currentUsage?.dashboardViews || 0}
                        </small><br />
                        {/* Le seul compteur de cette colonne qui consomme un quota :
                            `canAccessFeature('AI_ANALYSIS')` le compare à `analysisLimit`.
                            Il additionne les analyses simples et multi-connecteurs, comme
                            le fait la limite — les séparer ferait deux chiffres dont aucun
                            ne correspondrait au plafond. */}
                        <small title="Analyses IA du mois, simples et multi-connecteurs confondues. C'est ce total que la limite du plan compare.">
                          Analyses IA: {customer.currentUsage?.aiAnalyses || 0}
                        </small><br />
                        <small>Outils: {customer.currentUsage?.toolUsage || 0}</small><br />
                        {/* Compté à part des outils : cette colonne-là sert aux quotas
                            d'abonnement, et interroger ses données n'en consomme pas. */}
                        <small>MCP: {customer.currentUsage?.mcpRequests || 0}</small>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'beta' && (
        <div className="admin-beta">
          <div className="beta-header">
            <p>Contrôlez l'accès à l'application pendant la phase beta</p>
          </div>

          <div className="beta-status-card">
            <div className="beta-status-content">
              <Shield size={32} className={whitelistData.enabled ? 'beta-active' : 'beta-inactive'} />
              <div>
                <h3>Mode Beta</h3>
                <p>{whitelistData.enabled ? 'Activé - Seuls les emails autorisés peuvent se connecter' : 'Désactivé - Tous les utilisateurs peuvent se connecter'}</p>
              </div>
            </div>
            <button
              className={`beta-toggle-btn ${whitelistData.enabled ? 'active' : ''}`}
              onClick={handleToggleBetaMode}
              disabled={whitelistLoading}
            >
              {whitelistData.enabled ? 'Désactiver' : 'Activer'}
            </button>
          </div>

          <div className="whitelist-section">
            <h3>Liste Blanche ({whitelistData.emails.length} emails)</h3>

            <div className="add-email-form">
              <input
                type="email"
                placeholder="Ajouter un email..."
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
                disabled={whitelistLoading}
              />
              <button
                onClick={handleAddEmail}
                disabled={whitelistLoading || !newEmail}
                className="add-email-btn"
              >
                + Ajouter
              </button>
            </div>

            <div className="whitelist-emails">
              {whitelistData.emails.length === 0 ? (
                <div className="no-emails">
                  <Mail size={48} />
                  <p>Aucun email dans la liste blanche</p>
                  <small>Ajoutez des emails pour restreindre l'accès</small>
                </div>
              ) : (
                whitelistData.emails.map((email, index) => (
                  <div key={index} className="whitelist-email-item">
                    <Mail size={16} />
                    <span>{email}</span>
                    <button
                      onClick={() => handleRemoveEmail(email)}
                      disabled={whitelistLoading}
                      className="remove-email-btn"
                      title="Supprimer"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="beta-info">
            <AlertCircle size={16} />
            <div>
              <h4>Important</h4>
              <ul>
                <li>Les changements sont appliqués immédiatement</li>
                <li>Les emails sont insensibles à la casse</li>
                <li>Les utilisateurs déjà connectés ne sont pas affectés</li>
                <li>Cette restriction ne s'applique qu'à la connexion à l'app, pas aux connecteurs</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'cron' && (
        <div className="admin-cron">
          <div className="cron-header">
            <p>Surveillez et déclenchez manuellement les tâches planifiées</p>
          </div>

          {cronLoading && !cronStatus ? (
            <div className="cron-loading">
              <Loader2 size={32} className="spin" />
              <p>Chargement du statut...</p>
            </div>
          ) : cronStatus ? (
            <>
              {/* Tâche Recommandations */}
              <div className={`cron-task-card ${expandedCronTasks.recommendations ? 'expanded' : 'collapsed'}`}>
                <div className="cron-task-header" onClick={() => toggleCronTask('recommendations')} style={{ cursor: 'pointer' }}>
                  <div className="cron-task-title">
                    {expandedCronTasks.recommendations ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <div>
                      <h3>Recommandations Quotidiennes</h3>
                      <p className="cron-schedule">
                        Planifié tous les jours à 3h00
                      </p>
                    </div>
                  </div>
                  <div className="cron-task-status">
                    {cronStatus.tasks?.recommendations?.isRunning ? (
                      <span className="status-badge running">
                        <Loader2 size={14} className="spin" />
                        En cours
                      </span>
                    ) : estEnEchec(cronStatus.tasks?.recommendations) ? (
                      <span className="status-badge error">
                        <XCircle size={14} />
                        Erreur
                      </span>
                    ) : estPartielle(cronStatus.tasks?.recommendations) ? (
                      <span
                        className="status-badge error"
                        style={{ background: '#fef3c7', color: '#b45309' }}
                        title="La dernière exécution est allée au bout, mais une étape n'a traité qu'une partie du parc"
                      >
                        <AlertCircle size={14} />
                        Partiel
                      </span>
                    ) : cronStatus.tasks?.recommendations?.lastSuccess ? (
                      <span className="status-badge success">
                        <CheckCircle size={14} />
                        OK
                      </span>
                    ) : (
                      <span className="status-badge idle">
                        <Clock size={14} />
                        En attente
                      </span>
                    )}
                  </div>
                </div>

                <div className="cron-task-details">
                  <div className="cron-detail">
                    <span className="label">Dernière exécution :</span>
                    <span className="value">
                      {cronStatus.tasks?.recommendations?.lastRun
                        ? new Date(cronStatus.tasks?.recommendations.lastRun).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Dernier succès :</span>
                    <span className="value success">
                      {cronStatus.tasks?.recommendations?.lastSuccess
                        ? new Date(cronStatus.tasks?.recommendations.lastSuccess).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  {cronStatus.tasks?.recommendations?.lastError && (
                    <div className="cron-detail">
                      <span className="label">Dernière erreur :</span>
                      <span className="value error">
                        {new Date(cronStatus.tasks?.recommendations.lastError).toLocaleString('fr-FR')}
                      </span>
                    </div>
                  )}
                </div>

                <div className="cron-task-actions">
                  <button
                    className="trigger-cron-btn"
                    onClick={() => triggerCron('recommendations')}
                    disabled={triggeringCron || cronStatus.tasks?.recommendations?.isRunning}
                  >
                    {triggeringCron || cronStatus.tasks?.recommendations?.isRunning ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        Exécution en cours...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Exécuter maintenant
                      </>
                    )}
                  </button>
                </div>

                {/* Historique des exécutions */}
                {cronStatus.tasks?.recommendations?.history && cronStatus.tasks?.recommendations.history.length > 0 && (
                  <div className="cron-history">
                    <h4>Historique des 10 dernières exécutions</h4>
                    <table className="cron-history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Déclenché par</th>
                          <th>Durée</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cronStatus.tasks?.recommendations.history.map((entry, index) => {
                          const sousTaches = entry.tasks || [];
                          const depliee = expandedCronRuns[`reco-${index}`];
                          // Une exécution allée au bout mais dont une étape n'a servi qu'une
                          // partie du parc n'est pas un succès franc : le dire sur la ligne,
                          // sinon l'information reste enfouie sous un pli que personne n'ouvre.
                          //
                          // Le backend porte désormais `partial` sur l'exécution elle-même ; les
                          // sous-tâches restent consultées pour les exécutions plus anciennes, qui
                          // ne l'ont que sur leurs étapes.
                          const partielle = entry.status === 'partial'
                            || (entry.status === 'success' && sousTaches.some(t => t.status === 'partial'));
                          return (
                          <React.Fragment key={index}>
                          <tr
                            className={entry.status === 'success' || partielle ? 'row-success' : 'row-error'}
                            onClick={() => sousTaches.length > 0 && toggleCronRun(`reco-${index}`)}
                            style={{ cursor: sousTaches.length > 0 ? 'pointer' : 'default' }}
                          >
                            <td>
                              {/* Le chevron n'apparaît que s'il y a un détail à montrer : une
                                  exécution interrompue avant la première sous-tâche n'en a pas. */}
                              {sousTaches.length > 0 && (
                                depliee
                                  ? <ChevronDown size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                                  : <ChevronRight size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                              )}
                              {new Date(entry.startTime).toLocaleString('fr-FR')}
                            </td>
                            <td>
                              <span className={`trigger-badge ${entry.triggeredBy}`}>
                                {estDeclenchementAuto(entry.triggeredBy) ? 'Auto' : 'Manuel'}
                              </span>
                            </td>
                            <td>{formaterDuree(entry.duration)}</td>
                            <td>
                              {partielle ? (
                                <span className="status-badge error" style={{ background: '#fef3c7', color: '#b45309' }} title="Une étape n'a traité qu'une partie du parc">
                                  <AlertCircle size={12} />
                                  Partiel
                                </span>
                              ) : entry.status === 'success' ? (
                                <span className="status-badge success">
                                  <CheckCircle size={12} />
                                  Succès
                                </span>
                              ) : entry.status === 'running' ? (
                                <span className="status-badge running">
                                  <Loader2 size={12} className="spin" />
                                  En cours
                                </span>
                              ) : (
                                <span className="status-badge error" title={entry.error || ''}>
                                  <XCircle size={12} />
                                  Erreur
                                </span>
                              )}
                            </td>
                          </tr>
                          {depliee && sousTaches.length > 0 && (
                            <tr>
                              <td colSpan={4} style={{ padding: '0 0 10px 26px', background: '#fafafa' }}>
                                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                                  <tbody>
                                    {sousTaches.map((sousTache, i) => (
                                      <tr key={i}>
                                        <td style={{ padding: '5px 8px', color: '#4b5563' }}>
                                          {/* L'ordre du tableau est celui de l'enchaînement réel */}
                                          {i + 1}. {sousTache.name || sousTache.worker || 'Étape'}
                                          {/* L'unité vient du backend : les tâches de cache comptent
                                              des caches écrits — un par utilisateur, plus un par
                                              client —, les recommandations des comptes analysés.
                                              `caches` est l'ancienne forme, conservée pour les
                                              exécutions déjà enregistrées en base. */}
                                          {(() => {
                                            const couverture = sousTache.couverture
                                              || (sousTache.caches ? { ...sousTache.caches, unite: 'cache' } : null);
                                            if (!couverture) return null;
                                            const pluriel = couverture.reussis > 1 ? 's' : '';
                                            const libelle = couverture.unite === 'compte'
                                              ? `${couverture.reussis} compte${pluriel} analysé${pluriel}`
                                              : `${couverture.reussis} cache${pluriel} écrit${pluriel}`;
                                            return (
                                              <span style={{ color: '#6b7280', marginLeft: '8px' }}>
                                                — {libelle}
                                                {couverture.echecs > 0 && (
                                                  <strong style={{ color: '#b45309' }}>, {couverture.echecs} en échec</strong>
                                                )}
                                              </span>
                                            );
                                          })()}
                                        </td>
                                        <td style={{ padding: '5px 8px', textAlign: 'right', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                          {formaterDuree(sousTache.duration)}
                                        </td>
                                        <td style={{ padding: '5px 8px', width: '90px' }}>
                                          {sousTache.status === 'partial' ? (
                                            <span style={{ color: '#b45309', display: 'inline-flex', alignItems: 'center', gap: '4px' }} title="Une partie du parc n'a pas été traitée">
                                              <AlertCircle size={12} /> Partiel
                                            </span>
                                          ) : sousTache.status === 'success' ? (
                                            <span style={{ color: '#059669', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                              <CheckCircle size={12} /> OK
                                            </span>
                                          ) : sousTache.error ? (
                                            <span style={{ color: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: '4px' }} title={sousTache.error}>
                                              <XCircle size={12} /> Erreur
                                            </span>
                                          ) : (
                                            <span style={{ color: '#9ca3af' }}>—</span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Tâche Flux XML */}
              <div className={`cron-task-card ${expandedCronTasks.feedUpdates ? 'expanded' : 'collapsed'}`}>
                <div className="cron-task-header" onClick={() => toggleCronTask('feedUpdates')} style={{ cursor: 'pointer' }}>
                  <div className="cron-task-title">
                    {expandedCronTasks.feedUpdates ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <div>
                      <h3>Mise à jour Flux XML</h3>
                      <p className="cron-schedule">
                        Planifié toutes les minutes
                      </p>
                    </div>
                  </div>
                  <div className="cron-task-status">
                    {cronStatus.tasks?.feedUpdates?.isRunning ? (
                      <span className="status-badge running">
                        <Loader2 size={14} className="spin" />
                        En cours
                      </span>
                    ) : estEnEchec(cronStatus.tasks?.feedUpdates) ? (
                      <span className="status-badge error">
                        <XCircle size={14} />
                        Erreur
                      </span>
                    ) : cronStatus.tasks?.feedUpdates?.lastSuccess ? (
                      <span className="status-badge success">
                        <CheckCircle size={14} />
                        OK
                      </span>
                    ) : (
                      <span className="status-badge idle">
                        <Clock size={14} />
                        En attente
                      </span>
                    )}
                  </div>
                </div>

                <div className="cron-task-details">
                  <div className="cron-detail">
                    <span className="label">Dernière exécution :</span>
                    <span className="value">
                      {cronStatus.tasks?.feedUpdates?.lastRun
                        ? new Date(cronStatus.tasks?.feedUpdates.lastRun).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Dernier succès :</span>
                    <span className="value success">
                      {cronStatus.tasks?.feedUpdates?.lastSuccess
                        ? new Date(cronStatus.tasks?.feedUpdates.lastSuccess).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Flux traités :</span>
                    <span className="value">
                      {cronStatus.tasks?.feedUpdates?.feedsProcessed || 0}
                    </span>
                  </div>
                  {cronStatus.tasks?.feedUpdates?.lastError && (
                    <div className="cron-detail">
                      <span className="label">Dernière erreur :</span>
                      <span className="value error">
                        {cronStatus.tasks?.feedUpdates.lastError.message || 'Erreur inconnue'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="cron-task-actions">
                  <button
                    className="trigger-cron-btn"
                    onClick={() => triggerCron('feedUpdates')}
                    disabled={triggeringCron || cronStatus.tasks?.feedUpdates?.isRunning}
                  >
                    {triggeringCron || cronStatus.tasks?.feedUpdates?.isRunning ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        Exécution en cours...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Exécuter maintenant
                      </>
                    )}
                  </button>
                </div>

                {/* Historique des exécutions */}
                {cronStatus.tasks?.feedUpdates?.history && cronStatus.tasks?.feedUpdates.history.length > 0 && (
                  <div className="cron-history">
                    <h4>Historique des dernières exécutions</h4>
                    <table className="cron-history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Déclenché par</th>
                          <th>Flux</th>
                          <th>Durée</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cronStatus.tasks?.feedUpdates.history.map((entry, index) => (
                          <tr key={index} className={entry.status === 'success' ? 'row-success' : 'row-error'}>
                            <td>{new Date(entry.startTime).toLocaleString('fr-FR')}</td>
                            <td>
                              <span className={`trigger-badge ${entry.triggeredBy}`}>
                                {estDeclenchementAuto(entry.triggeredBy) ? 'Auto' : 'Manuel'}
                              </span>
                            </td>
                            <td>{entry.feedsProcessed || 0}</td>
                            <td>{entry.duration ? `${Math.round(entry.duration / 1000)}s` : '-'}</td>
                            <td>
                              {entry.status === 'success' ? (
                                <span className="status-badge success">
                                  <CheckCircle size={12} />
                                  OK
                                </span>
                              ) : entry.status === 'running' ? (
                                <span className="status-badge running">
                                  <Loader2 size={12} className="spin" />
                                  ...
                                </span>
                              ) : (
                                <span className="status-badge error" title={entry.error || ''}>
                                  <XCircle size={12} />
                                  Err
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Tâche BigQuery Sync */}
              <div className={`cron-task-card ${expandedCronTasks.bigQuerySync ? 'expanded' : 'collapsed'}`}>
                <div className="cron-task-header" onClick={() => toggleCronTask('bigQuerySync')} style={{ cursor: 'pointer' }}>
                  <div className="cron-task-title">
                    {expandedCronTasks.bigQuerySync ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <div>
                      <h3>Synchronisation BigQuery</h3>
                      <p className="cron-schedule">
                        Planifié tous les jours à 2h00 + dimanches à 1h00
                      </p>
                    </div>
                  </div>
                  <div className="cron-task-status">
                    {cronStatus.tasks?.bigQuerySync?.isRunning ? (
                      <span className="status-badge running">
                        <Loader2 size={14} className="spin" />
                        En cours
                      </span>
                    ) : estEnEchec(cronStatus.tasks?.bigQuerySync) ? (
                      <span className="status-badge error">
                        <XCircle size={14} />
                        Erreur
                      </span>
                    ) : cronStatus.tasks?.bigQuerySync?.lastSuccess ? (
                      <span className="status-badge success">
                        <CheckCircle size={14} />
                        OK
                      </span>
                    ) : (
                      <span className="status-badge idle">
                        <Clock size={14} />
                        En attente
                      </span>
                    )}
                  </div>
                </div>

                <div className="cron-task-details">
                  <div className="cron-detail">
                    <span className="label">Dernière exécution :</span>
                    <span className="value">
                      {cronStatus.tasks?.bigQuerySync?.lastRun
                        ? new Date(cronStatus.tasks?.bigQuerySync.lastRun).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Dernier succès :</span>
                    <span className="value success">
                      {cronStatus.tasks?.bigQuerySync?.lastSuccess
                        ? new Date(cronStatus.tasks?.bigQuerySync.lastSuccess).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Comptes synchronisés :</span>
                    <span className="value">
                      {cronStatus.tasks?.bigQuerySync?.accountsSynced || 0}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Lignes synchronisées :</span>
                    <span className="value">
                      {(cronStatus.tasks?.bigQuerySync?.rowsSynced || 0).toLocaleString('fr-FR')}
                    </span>
                  </div>
                  {cronStatus.tasks?.bigQuerySync?.lastError && (
                    <div className="cron-detail">
                      <span className="label">Dernière erreur :</span>
                      <span className="value error">
                        {cronStatus.tasks?.bigQuerySync.lastError.message || 'Erreur inconnue'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="cron-task-actions">
                  <button
                    className="trigger-cron-btn"
                    onClick={() => triggerCron('bigQuerySync')}
                    disabled={triggeringCron || cronStatus.tasks?.bigQuerySync?.isRunning}
                  >
                    {triggeringCron || cronStatus.tasks?.bigQuerySync?.isRunning ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        Sync en cours...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Sync 3 jours
                      </>
                    )}
                  </button>
                  <button
                    className="trigger-cron-btn secondary"
                    onClick={() => triggerCron('bigQuerySyncWeekly')}
                    disabled={triggeringCron || cronStatus.tasks?.bigQuerySync?.isRunning}
                  >
                    {triggeringCron || cronStatus.tasks?.bigQuerySync?.isRunning ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        Sync en cours...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Sync 32 jours
                      </>
                    )}
                  </button>
                </div>

                {/* Historique des exécutions */}
                {cronStatus.tasks?.bigQuerySync?.history && cronStatus.tasks?.bigQuerySync.history.length > 0 && (
                  <div className="cron-history">
                    <h4>Historique des dernières exécutions</h4>
                    <table className="cron-history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Jours</th>
                          <th>Comptes</th>
                          <th>Lignes</th>
                          <th>Durée</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cronStatus.tasks?.bigQuerySync.history.map((entry, index) => (
                          <tr key={index}>
                            <td>{new Date(entry.startTime).toLocaleString('fr-FR')}</td>
                            <td>{entry.triggeredBy}</td>
                            <td>{entry.days}j</td>
                            <td>{entry.accountsSynced || 0}</td>
                            <td>{(entry.rowsSynced || 0).toLocaleString('fr-FR')}</td>
                            <td>{entry.duration ? `${(entry.duration / 1000).toFixed(1)}s` : '-'}</td>
                            <td>
                              {entry.status === 'success' ? (
                                <span className="status-badge success">
                                  <CheckCircle size={12} />
                                  OK
                                </span>
                              ) : entry.status === 'running' ? (
                                <span className="status-badge running">
                                  <Loader2 size={12} className="spin" />
                                  ...
                                </span>
                              ) : (
                                <span className="status-badge error" title={entry.error || ''}>
                                  <XCircle size={12} />
                                  Err
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Tâche Diagnostic */}
              <div className={`cron-task-card ${expandedCronTasks.diagnostic ? 'expanded' : 'collapsed'}`}>
                <div className="cron-task-header" onClick={() => toggleCronTask('diagnostic')} style={{ cursor: 'pointer' }}>
                  <div className="cron-task-title">
                    {expandedCronTasks.diagnostic ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <div>
                      <h3>Scan Diagnostic Comptes</h3>
                      <p className="cron-schedule">
                        Planifié tous les jours à 3h00
                      </p>
                    </div>
                  </div>
                  <div className="cron-task-status">
                    {cronStatus.tasks?.diagnostic?.isRunning ? (
                      <span className="status-badge running">
                        <Loader2 size={14} className="spin" />
                        En cours
                      </span>
                    ) : estEnEchec(cronStatus.tasks?.diagnostic) ? (
                      <span className="status-badge error">
                        <XCircle size={14} />
                        Erreur
                      </span>
                    ) : cronStatus.tasks?.diagnostic?.lastSuccess ? (
                      <span className="status-badge success">
                        <CheckCircle size={14} />
                        OK
                      </span>
                    ) : (
                      <span className="status-badge idle">
                        <Clock size={14} />
                        En attente
                      </span>
                    )}
                  </div>
                </div>

                <div className="cron-task-details">
                  <div className="cron-detail">
                    <span className="label">Dernière exécution :</span>
                    <span className="value">
                      {cronStatus.tasks?.diagnostic?.lastRun
                        ? new Date(cronStatus.tasks?.diagnostic.lastRun).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Dernier succès :</span>
                    <span className="value success">
                      {cronStatus.tasks?.diagnostic?.lastSuccess
                        ? new Date(cronStatus.tasks?.diagnostic.lastSuccess).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Comptes scannés :</span>
                    <span className="value">
                      {cronStatus.tasks?.diagnostic?.accountsScanned || 0}
                    </span>
                  </div>
                  {cronStatus.tasks?.diagnostic?.lastError && (
                    <div className="cron-detail">
                      <span className="label">Dernière erreur :</span>
                      <span className="value error">
                        {cronStatus.tasks?.diagnostic.lastError.message || 'Erreur inconnue'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="cron-task-actions">
                  <button
                    className="trigger-cron-btn"
                    onClick={() => triggerCron('diagnostic')}
                    disabled={triggeringCron || cronStatus.tasks?.diagnostic?.isRunning}
                  >
                    {triggeringCron || cronStatus.tasks?.diagnostic?.isRunning ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        Scan en cours...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Scanner maintenant
                      </>
                    )}
                  </button>
                </div>

                {/* Historique des exécutions */}
                {cronStatus.tasks?.diagnostic?.history && cronStatus.tasks?.diagnostic.history.length > 0 && (
                  <div className="cron-history">
                    <h4>Historique des dernières exécutions</h4>
                    <table className="cron-history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Déclenché par</th>
                          <th>Comptes</th>
                          <th>Durée</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cronStatus.tasks?.diagnostic.history.map((entry, index) => (
                          <tr key={index} className={entry.status === 'success' ? 'row-success' : 'row-error'}>
                            <td>{new Date(entry.startTime).toLocaleString('fr-FR')}</td>
                            <td>
                              <span className={`trigger-badge ${entry.triggeredBy}`}>
                                {estDeclenchementAuto(entry.triggeredBy) ? 'Auto' : 'Manuel'}
                              </span>
                            </td>
                            <td>{entry.accountsScanned || 0}</td>
                            <td>{entry.duration ? `${Math.round(entry.duration / 1000)}s` : '-'}</td>
                            <td>
                              {entry.status === 'success' ? (
                                <span className="status-badge success">
                                  <CheckCircle size={12} />
                                  OK
                                </span>
                              ) : entry.status === 'running' ? (
                                <span className="status-badge running">
                                  <Loader2 size={12} className="spin" />
                                  ...
                                </span>
                              ) : (
                                <span className="status-badge error" title={entry.error || ''}>
                                  <XCircle size={12} />
                                  Err
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Tâche Emails Diagnostic */}
              <div className={`cron-task-card ${expandedCronTasks.diagnosticEmails ? 'expanded' : 'collapsed'}`}>
                <div className="cron-task-header" onClick={() => toggleCronTask('diagnosticEmails')} style={{ cursor: 'pointer' }}>
                  <div className="cron-task-title">
                    {expandedCronTasks.diagnosticEmails ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <div>
                      <h3>Envoi Emails Diagnostic</h3>
                      <p className="cron-schedule">
                        Planifié tous les jours à 6h00
                      </p>
                    </div>
                  </div>
                  <div className="cron-task-status">
                    {cronStatus.tasks?.diagnosticEmails?.isRunning ? (
                      <span className="status-badge running">
                        <Loader2 size={14} className="spin" />
                        En cours
                      </span>
                    ) : estEnEchec(cronStatus.tasks?.diagnosticEmails) ? (
                      <span className="status-badge error">
                        <XCircle size={14} />
                        Erreur
                      </span>
                    ) : cronStatus.tasks?.diagnosticEmails?.lastSuccess ? (
                      <span className="status-badge success">
                        <CheckCircle size={14} />
                        OK
                      </span>
                    ) : (
                      <span className="status-badge idle">
                        <Clock size={14} />
                        En attente
                      </span>
                    )}
                  </div>
                </div>

                <div className="cron-task-details">
                  <div className="cron-detail">
                    <span className="label">Dernière exécution :</span>
                    <span className="value">
                      {cronStatus.tasks?.diagnosticEmails?.lastRun
                        ? new Date(cronStatus.tasks?.diagnosticEmails.lastRun).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Dernier succès :</span>
                    <span className="value success">
                      {cronStatus.tasks?.diagnosticEmails?.lastSuccess
                        ? new Date(cronStatus.tasks?.diagnosticEmails.lastSuccess).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Emails envoyés :</span>
                    <span className="value">
                      {cronStatus.tasks?.diagnosticEmails?.emailsSent || 0}
                    </span>
                  </div>
                  {cronStatus.tasks?.diagnosticEmails?.lastError && (
                    <div className="cron-detail">
                      <span className="label">Dernière erreur :</span>
                      <span className="value error">
                        {cronStatus.tasks?.diagnosticEmails.lastError.message || 'Erreur inconnue'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="cron-task-actions">
                  <button
                    className="trigger-cron-btn"
                    onClick={() => triggerCron('diagnosticEmails')}
                    disabled={triggeringCron || cronStatus.tasks?.diagnosticEmails?.isRunning}
                  >
                    {triggeringCron || cronStatus.tasks?.diagnosticEmails?.isRunning ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Envoyer maintenant
                      </>
                    )}
                  </button>
                </div>

                {/* Historique des exécutions */}
                {cronStatus.tasks?.diagnosticEmails?.history && cronStatus.tasks?.diagnosticEmails.history.length > 0 && (
                  <div className="cron-history">
                    <h4>Historique des dernières exécutions</h4>
                    <table className="cron-history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Déclenché par</th>
                          <th>Emails</th>
                          <th>Durée</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cronStatus.tasks?.diagnosticEmails.history.map((entry, index) => (
                          <tr key={index} className={entry.status === 'success' ? 'row-success' : 'row-error'}>
                            <td>{new Date(entry.startTime).toLocaleString('fr-FR')}</td>
                            <td>
                              <span className={`trigger-badge ${entry.triggeredBy}`}>
                                {estDeclenchementAuto(entry.triggeredBy) ? 'Auto' : 'Manuel'}
                              </span>
                            </td>
                            <td>{entry.emailsSent || 0}</td>
                            <td>{entry.duration ? `${Math.round(entry.duration / 1000)}s` : '-'}</td>
                            <td>
                              {entry.status === 'success' ? (
                                <span className="status-badge success">
                                  <CheckCircle size={12} />
                                  OK
                                </span>
                              ) : entry.status === 'running' ? (
                                <span className="status-badge running">
                                  <Loader2 size={12} className="spin" />
                                  ...
                                </span>
                              ) : (
                                <span className="status-badge error" title={entry.error || ''}>
                                  <XCircle size={12} />
                                  Err
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Tâche 7: Digest Quotidien */}
              <div className={`cron-task-card ${expandedCronTasks.digestEmails ? 'expanded' : 'collapsed'}`}>
                <div className="cron-task-header" onClick={() => toggleCronTask('digestEmails')} style={{ cursor: 'pointer' }}>
                  <div className="cron-task-title">
                    {expandedCronTasks.digestEmails ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <div>
                      <h3>Digest Quotidien</h3>
                      <p className="cron-schedule">
                        Collecte 5h00 / Envoi 6h00
                      </p>
                    </div>
                  </div>
                  <div className="cron-task-status">
                    {cronStatus.tasks?.digestEmails?.isRunning ? (
                      <span className="status-badge running">
                        <Loader2 size={14} className="spin" />
                        En cours
                      </span>
                    ) : estEnEchec(cronStatus.tasks?.digestEmails) ? (
                      <span className="status-badge error">
                        <XCircle size={14} />
                        Erreur
                      </span>
                    ) : cronStatus.tasks?.digestEmails?.lastSuccess ? (
                      <span className="status-badge success">
                        <CheckCircle size={14} />
                        OK
                      </span>
                    ) : (
                      <span className="status-badge idle">
                        <Clock size={14} />
                        En attente
                      </span>
                    )}
                  </div>
                </div>
                <div className="cron-task-details">
                  <div className="cron-detail">
                    <span className="label">Dernière exécution:</span>
                    <span className="value">
                      {cronStatus.tasks?.digestEmails?.lastRun
                        ? new Date(cronStatus.tasks?.digestEmails.lastRun).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Dernier succès:</span>
                    <span className="value">
                      {cronStatus.tasks?.digestEmails?.lastSuccess
                        ? new Date(cronStatus.tasks?.digestEmails.lastSuccess).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Emails envoyés:</span>
                    <span className="value">
                      {cronStatus.tasks?.digestEmails?.emailsSent || 0}
                    </span>
                  </div>
                  {cronStatus.tasks?.digestEmails?.lastError && (
                    <div className="cron-detail">
                      <span className="label">Dernière erreur:</span>
                      <span className="value error">
                        {cronStatus.tasks?.digestEmails.lastError.message || 'Erreur inconnue'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="cron-task-actions">
                  <button
                    className="trigger-cron-btn"
                    onClick={() => triggerCron('digestEmails')}
                    disabled={triggeringCron || cronStatus.tasks?.digestEmails?.isRunning}
                  >
                    {triggeringCron || cronStatus.tasks?.digestEmails?.isRunning ? (
                      <>
                        <Loader2 size={14} className="spin" />
                        En cours...
                      </>
                    ) : (
                      <>
                        <Play size={14} />
                        Déclencher
                      </>
                    )}
                  </button>
                </div>
                {cronStatus.tasks?.digestEmails?.history && cronStatus.tasks?.digestEmails.history.length > 0 && (
                  <div className="cron-history">
                    <h4>Historique récent</h4>
                    <table className="cron-history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Déclenchement</th>
                          <th>Emails</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cronStatus.tasks?.digestEmails.history.map((entry, index) => (
                          <tr key={index}>
                            <td>{new Date(entry.timestamp).toLocaleString('fr-FR')}</td>
                            <td>
                              {estDeclenchementAuto(entry.triggeredBy) ? 'Auto' : 'Manuel'}
                            </td>
                            <td>{entry.emailsSent || 0}</td>
                            <td>
                              {entry.success ? (
                                <span className="status-success">
                                  <CheckCircle size={14} />
                                  Succès
                                </span>
                              ) : (
                                <span className="status-error">
                                  <XCircle size={14} />
                                  {entry.error || 'Erreur'}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Tâche 8: Diagnostic Flux Merchant */}
              <div className={`cron-task-card ${expandedCronTasks.feedDiagnostic ? 'expanded' : 'collapsed'}`}>
                <div className="cron-task-header" onClick={() => toggleCronTask('feedDiagnostic')} style={{ cursor: 'pointer' }}>
                  <div className="cron-task-title">
                    {expandedCronTasks.feedDiagnostic ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <div>
                      <h3>Diagnostic Flux Merchant</h3>
                      <p className="cron-schedule">
                        Planifié tous les jours à 3h00
                      </p>
                    </div>
                  </div>
                  <div className="cron-task-status">
                    {cronStatus.tasks?.feedDiagnostic?.isRunning ? (
                      <span className="status-badge running">
                        <Loader2 size={14} className="spin" />
                        En cours
                      </span>
                    ) : estEnEchec(cronStatus.tasks?.feedDiagnostic) ? (
                      <span className="status-badge error">
                        <XCircle size={14} />
                        Erreur
                      </span>
                    ) : cronStatus.tasks?.feedDiagnostic?.lastSuccess ? (
                      <span className="status-badge success">
                        <CheckCircle size={14} />
                        OK
                      </span>
                    ) : (
                      <span className="status-badge idle">
                        <Clock size={14} />
                        En attente
                      </span>
                    )}
                  </div>
                </div>

                <div className="cron-task-details">
                  <div className="cron-detail">
                    <span className="label">Dernière exécution :</span>
                    <span className="value">
                      {cronStatus.tasks?.feedDiagnostic?.lastRun
                        ? new Date(cronStatus.tasks?.feedDiagnostic.lastRun).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Dernier succès :</span>
                    <span className="value success">
                      {cronStatus.tasks?.feedDiagnostic?.lastSuccess
                        ? new Date(cronStatus.tasks?.feedDiagnostic.lastSuccess).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Comptes scannés :</span>
                    <span className="value">
                      {cronStatus.tasks?.feedDiagnostic?.accountsScanned || 0}
                    </span>
                  </div>
                  {cronStatus.tasks?.feedDiagnostic?.lastError && (
                    <div className="cron-detail error">
                      <span className="label">Dernière erreur :</span>
                      <span className="value error">
                        {cronStatus.tasks?.feedDiagnostic.lastError.message || 'Erreur inconnue'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="cron-task-actions">
                  <button
                    className="trigger-cron-btn"
                    onClick={() => triggerCron('feedDiagnostic')}
                    disabled={triggeringCron || cronStatus.tasks?.feedDiagnostic?.isRunning}
                  >
                    {triggeringCron || cronStatus.tasks?.feedDiagnostic?.isRunning ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        Scan en cours...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Scanner maintenant
                      </>
                    )}
                  </button>
                </div>

                {/* Historique des exécutions */}
                {cronStatus.tasks?.feedDiagnostic?.history && cronStatus.tasks?.feedDiagnostic.history.length > 0 && (
                  <div className="cron-history">
                    <h4>Historique des dernières exécutions</h4>
                    <table className="cron-history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Déclenché par</th>
                          <th>Comptes</th>
                          <th>Durée</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cronStatus.tasks?.feedDiagnostic.history.map((entry, index) => (
                          <tr key={index} className={entry.status === 'success' ? 'row-success' : 'row-error'}>
                            <td>{new Date(entry.startTime).toLocaleString('fr-FR')}</td>
                            <td>
                              <span className={`trigger-badge ${entry.triggeredBy}`}>
                                {estDeclenchementAuto(entry.triggeredBy) ? 'Auto' : 'Manuel'}
                              </span>
                            </td>
                            <td>{entry.accountsScanned || 0}</td>
                            <td>{entry.duration ? `${Math.round(entry.duration / 1000)}s` : '-'}</td>
                            <td>
                              {entry.status === 'success' ? (
                                <span className="status-badge success">
                                  <CheckCircle size={12} />
                                  OK
                                </span>
                              ) : entry.status === 'running' ? (
                                <span className="status-badge running">
                                  <Loader2 size={12} className="spin" />
                                  ...
                                </span>
                              ) : (
                                <span className="status-badge error" title={entry.error || ''}>
                                  <XCircle size={12} />
                                  Err
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Tâche 8: Nettoyage Données Anciennes */}
              <div className={`cron-task-card ${expandedCronTasks.cleanup ? 'expanded' : 'collapsed'}`}>
                <div className="cron-task-header" onClick={() => toggleCronTask('cleanup')} style={{ cursor: 'pointer' }}>
                  <div className="cron-task-title">
                    {expandedCronTasks.cleanup ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <div>
                      <h3>Nettoyage Données Anciennes</h3>
                      <p className="cron-schedule">
                        Planifié tous les dimanches à 5h00
                      </p>
                    </div>
                  </div>
                  <div className="cron-task-status">
                    {cronStatus.tasks?.cleanup?.isRunning ? (
                      <span className="status-badge running">
                        <Loader2 size={14} className="spin" />
                        En cours
                      </span>
                    ) : estEnEchec(cronStatus.tasks?.cleanup) ? (
                      <span className="status-badge error">
                        <XCircle size={14} />
                        Erreur
                      </span>
                    ) : cronStatus.tasks?.cleanup?.lastSuccess ? (
                      <span className="status-badge success">
                        <CheckCircle size={14} />
                        OK
                      </span>
                    ) : (
                      <span className="status-badge idle">
                        <Clock size={14} />
                        En attente
                      </span>
                    )}
                  </div>
                </div>

                <div className="cron-task-details">
                  <div className="cron-detail">
                    <span className="label">Dernière exécution :</span>
                    <span className="value">
                      {cronStatus.tasks?.cleanup?.lastRun
                        ? new Date(cronStatus.tasks?.cleanup.lastRun).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Dernier succès :</span>
                    <span className="value success">
                      {cronStatus.tasks?.cleanup?.lastSuccess
                        ? new Date(cronStatus.tasks?.cleanup.lastSuccess).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Lignes supprimées :</span>
                    <span className="value">
                      {cronStatus.tasks?.cleanup?.rowsDeleted?.toLocaleString('fr-FR') || 0}
                    </span>
                  </div>
                  {cronStatus.tasks?.cleanup?.lastError && (
                    <div className="cron-detail error">
                      <span className="label">Dernière erreur :</span>
                      <span className="value error">
                        {cronStatus.tasks?.cleanup.lastError.message || 'Erreur inconnue'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="cron-task-actions">
                  <button
                    className="trigger-cron-btn"
                    onClick={() => triggerCron('cleanup')}
                    disabled={triggeringCron || cronStatus.tasks?.cleanup?.isRunning}
                  >
                    {triggeringCron || cronStatus.tasks?.cleanup?.isRunning ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        Nettoyage en cours...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Nettoyer maintenant
                      </>
                    )}
                  </button>
                </div>

                {/* Historique des exécutions */}
                {cronStatus.tasks?.cleanup?.history && cronStatus.tasks?.cleanup.history.length > 0 && (
                  <div className="cron-history">
                    <h4>Historique des dernières exécutions</h4>
                    <table className="cron-history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Déclenché par</th>
                          <th>Lignes supprimées</th>
                          <th>Durée</th>
                          <th>Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cronStatus.tasks?.cleanup.history.map((entry, index) => (
                          <tr key={index} className={entry.status === 'success' ? 'row-success' : 'row-error'}>
                            <td>{new Date(entry.startTime).toLocaleString('fr-FR')}</td>
                            <td>
                              <span className={`trigger-badge ${entry.triggeredBy}`}>
                                {estDeclenchementAuto(entry.triggeredBy) ? 'Auto' : 'Manuel'}
                              </span>
                            </td>
                            <td>{entry.rowsDeleted?.toLocaleString('fr-FR') || 0}</td>
                            <td>{entry.duration ? `${Math.round(entry.duration / 1000)}s` : '-'}</td>
                            <td>
                              {entry.status === 'success' ? (
                                <span className="status-badge success">
                                  <CheckCircle size={12} />
                                  OK
                                </span>
                              ) : entry.status === 'running' ? (
                                <span className="status-badge running">
                                  <Loader2 size={12} className="spin" />
                                  ...
                                </span>
                              ) : (
                                <span className="status-badge error" title={entry.error || ''}>
                                  <XCircle size={12} />
                                  Err
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Tâche Competitor Ads Sync */}
              <div className={`cron-task-card ${expandedCronTasks.competitorAdsSync ? 'expanded' : 'collapsed'}`}>
                <div className="cron-task-header" onClick={() => toggleCronTask('competitorAdsSync')} style={{ cursor: 'pointer' }}>
                  <div className="cron-task-title">
                    {expandedCronTasks.competitorAdsSync ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <div>
                      <h3>Social Ads Intelligence - Sync Publicités</h3>
                      <p className="cron-schedule">
                        Planifié tous les jours à 2h00
                      </p>
                    </div>
                  </div>
                  <div className="cron-task-status">
                    {cronStatus.tasks?.competitorAdsSync?.isRunning ? (
                      <span className="status-badge running">
                        <Loader2 size={14} className="spin" />
                        En cours
                      </span>
                    ) : estEnEchec(cronStatus.tasks?.competitorAdsSync) ? (
                      <span className="status-badge error">
                        <XCircle size={14} />
                        Erreur
                      </span>
                    ) : cronStatus.tasks?.competitorAdsSync?.lastSuccess ? (
                      <span className="status-badge success">
                        <CheckCircle size={14} />
                        OK
                      </span>
                    ) : (
                      <span className="status-badge idle">
                        <Clock size={14} />
                        En attente
                      </span>
                    )}
                  </div>
                </div>

                <div className="cron-task-details">
                  <div className="cron-detail">
                    <span className="label">Dernière exécution :</span>
                    <span className="value">
                      {cronStatus.tasks?.competitorAdsSync?.lastRun
                        ? new Date(cronStatus.tasks?.competitorAdsSync.lastRun).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Dernier succès :</span>
                    <span className="value success">
                      {cronStatus.tasks?.competitorAdsSync?.lastSuccess
                        ? new Date(cronStatus.tasks?.competitorAdsSync.lastSuccess).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Pages / Nouvelles pubs :</span>
                    <span className="value">
                      {cronStatus.tasks?.competitorAdsSync?.pagesProcessed || 0} / {cronStatus.tasks?.competitorAdsSync?.newAds || 0}
                    </span>
                  </div>
                  {cronStatus.tasks?.competitorAdsSync?.lastError && (
                    <div className="cron-detail">
                      {/* Une erreur qu'un succès plus récent a déjà dépassée est un souvenir,
                          pas un état : on la garde — elle renseigne — mais en gris, et nommée
                          comme telle. */}
                      <span className="label">
                        {estEnEchec(cronStatus.tasks?.competitorAdsSync) ? 'Erreur :' : 'Dernière erreur (résolue depuis) :'}
                      </span>
                      <span
                        className={estEnEchec(cronStatus.tasks?.competitorAdsSync) ? 'value error' : 'value'}
                        style={estEnEchec(cronStatus.tasks?.competitorAdsSync) ? undefined : { color: '#9ca3af' }}
                      >
                        {cronStatus.tasks?.competitorAdsSync.lastError.message || 'Erreur inconnue'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="cron-task-actions">
                  <button
                    className="trigger-cron-btn"
                    onClick={() => triggerCron('competitorAdsSync')}
                    disabled={triggeringCron || cronStatus.tasks?.competitorAdsSync?.isRunning}
                  >
                    {triggeringCron || cronStatus.tasks?.competitorAdsSync?.isRunning ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        Exécution en cours...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Exécuter maintenant
                      </>
                    )}
                  </button>
                </div>

                {rendreHistoriqueCron('competitorAdsSync', [
                  { titre: 'Pages / Pubs', valeur: e => `${e.pagesProcessed ?? 0} / ${e.newAds ?? 0}` }
                ])}
              </div>

              {/* Tâche Competitor Followers Sync */}
              <div className={`cron-task-card ${expandedCronTasks.competitorFollowersSync ? 'expanded' : 'collapsed'}`}>
                <div className="cron-task-header" onClick={() => toggleCronTask('competitorFollowersSync')} style={{ cursor: 'pointer' }}>
                  <div className="cron-task-title">
                    {expandedCronTasks.competitorFollowersSync ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <div>
                      <h3>Social Ads Intelligence - Sync Followers</h3>
                      <p className="cron-schedule">
                        Planifié tous les jours à 4h00
                      </p>
                    </div>
                  </div>
                  <div className="cron-task-status">
                    {cronStatus.tasks?.competitorFollowersSync?.isRunning ? (
                      <span className="status-badge running">
                        <Loader2 size={14} className="spin" />
                        En cours
                      </span>
                    ) : estEnEchec(cronStatus.tasks?.competitorFollowersSync) ? (
                      <span className="status-badge error">
                        <XCircle size={14} />
                        Erreur
                      </span>
                    ) : cronStatus.tasks?.competitorFollowersSync?.lastSuccess ? (
                      <span className="status-badge success">
                        <CheckCircle size={14} />
                        OK
                      </span>
                    ) : (
                      <span className="status-badge idle">
                        <Clock size={14} />
                        En attente
                      </span>
                    )}
                  </div>
                </div>

                <div className="cron-task-details">
                  <div className="cron-detail">
                    <span className="label">Dernière exécution :</span>
                    <span className="value">
                      {cronStatus.tasks?.competitorFollowersSync?.lastRun
                        ? new Date(cronStatus.tasks?.competitorFollowersSync.lastRun).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Dernier succès :</span>
                    <span className="value success">
                      {cronStatus.tasks?.competitorFollowersSync?.lastSuccess
                        ? new Date(cronStatus.tasks?.competitorFollowersSync.lastSuccess).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Pages / Followers mis à jour :</span>
                    <span className="value">
                      {cronStatus.tasks?.competitorFollowersSync?.pagesProcessed || 0} / {cronStatus.tasks?.competitorFollowersSync?.followersUpdated || 0}
                    </span>
                  </div>
                  {cronStatus.tasks?.competitorFollowersSync?.lastError && (
                    <div className="cron-detail">
                      {/* Une erreur qu'un succès plus récent a déjà dépassée est un souvenir,
                          pas un état : on la garde — elle renseigne — mais en gris, et nommée
                          comme telle. */}
                      <span className="label">
                        {estEnEchec(cronStatus.tasks?.competitorFollowersSync) ? 'Erreur :' : 'Dernière erreur (résolue depuis) :'}
                      </span>
                      <span
                        className={estEnEchec(cronStatus.tasks?.competitorFollowersSync) ? 'value error' : 'value'}
                        style={estEnEchec(cronStatus.tasks?.competitorFollowersSync) ? undefined : { color: '#9ca3af' }}
                      >
                        {cronStatus.tasks?.competitorFollowersSync.lastError.message || 'Erreur inconnue'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="cron-task-actions">
                  <button
                    className="trigger-cron-btn"
                    onClick={() => triggerCron('competitorFollowersSync')}
                    disabled={triggeringCron || cronStatus.tasks?.competitorFollowersSync?.isRunning}
                  >
                    {triggeringCron || cronStatus.tasks?.competitorFollowersSync?.isRunning ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        Exécution en cours...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Exécuter maintenant
                      </>
                    )}
                  </button>
                </div>

                {rendreHistoriqueCron('competitorFollowersSync', [
                  { titre: 'Pages / Followers', valeur: e => `${e.pagesProcessed ?? 0} / ${e.followersUpdated ?? 0}` }
                ])}
              </div>

              {/* Tâche Trafic Awin */}
              <div className={`cron-task-card ${expandedCronTasks.awinSync ? 'expanded' : 'collapsed'}`}>
                <div className="cron-task-header" onClick={() => toggleCronTask('awinSync')} style={{ cursor: 'pointer' }}>
                  <div className="cron-task-title">
                    {expandedCronTasks.awinSync ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <div>
                      <h3>Trafic Awin (impressions, clics)</h3>
                      <p className="cron-schedule">
                        Planifié tous les jours à 3h30
                      </p>
                    </div>
                  </div>
                  <div className="cron-task-status">
                    {cronStatus.tasks?.awinSync?.isRunning ? (
                      <span className="status-badge running">
                        <Loader2 size={14} className="spin" />
                        En cours
                      </span>
                    ) : estEnEchec(cronStatus.tasks?.awinSync) ? (
                      <span className="status-badge error">
                        <XCircle size={14} />
                        Erreur
                      </span>
                    ) : cronStatus.tasks?.awinSync?.lastSuccess ? (
                      <span className="status-badge success">
                        <CheckCircle size={14} />
                        OK
                      </span>
                    ) : (
                      <span className="status-badge idle">
                        <Clock size={14} />
                        En attente
                      </span>
                    )}
                  </div>
                </div>

                <div className="cron-task-details">
                  <div className="cron-detail">
                    <span className="label">Dernière exécution :</span>
                    <span className="value">
                      {cronStatus.tasks?.awinSync?.lastRun
                        ? new Date(cronStatus.tasks?.awinSync.lastRun).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Dernier succès :</span>
                    <span className="value success">
                      {cronStatus.tasks?.awinSync?.lastSuccess
                        ? new Date(cronStatus.tasks?.awinSync.lastSuccess).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Comptes Awin :</span>
                    <span className="value">{cronStatus.tasks?.awinSync?.accountsSynced || 0}</span>
                  </div>
                  <div className="cron-detail">
                    {/* Le reliquat n'est pas un échec : le budget d'appels de la nuit est
                        atteint, la nuit suivante reprend là où celle-ci s'est arrêtée. */}
                    <span className="label">Journées rapatriées / en attente :</span>
                    <span className="value">
                      {cronStatus.tasks?.awinSync?.journeesEcrites ?? 0} / {cronStatus.tasks?.awinSync?.journeesRestantes ?? 0}
                    </span>
                  </div>
                  {cronStatus.tasks?.awinSync?.lastError && (
                    <div className="cron-detail">
                      <span className="label">
                        {estEnEchec(cronStatus.tasks?.awinSync) ? 'Erreur :' : 'Dernière erreur (résolue depuis) :'}
                      </span>
                      <span
                        className={estEnEchec(cronStatus.tasks?.awinSync) ? 'value error' : 'value'}
                        style={estEnEchec(cronStatus.tasks?.awinSync) ? undefined : { color: '#9ca3af' }}
                      >
                        {cronStatus.tasks?.awinSync.lastError.message || 'Erreur inconnue'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="cron-task-actions">
                  <button
                    className="trigger-cron-btn"
                    onClick={() => triggerCron('awinSync')}
                    disabled={triggeringCron || cronStatus.tasks?.awinSync?.isRunning}
                  >
                    {triggeringCron || cronStatus.tasks?.awinSync?.isRunning ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        Exécution en cours...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Exécuter maintenant
                      </>
                    )}
                  </button>
                  {/* Voir la base, et non l'exécution : la tâche dit ce qu'elle a écrit cette
                      nuit, pas ce qui est stocké. */}
                  <button
                    className="trigger-cron-btn"
                    onClick={ouvrirCacheAwin}
                    style={{ backgroundColor: '#475569' }}
                  >
                    <Database size={16} />
                    Voir les données en base
                  </button>
                </div>

                {rendreHistoriqueCron('awinSync', [
                  { titre: 'Comptes', valeur: e => e.comptes ?? 0 },
                  { titre: 'Journées', valeur: e => `${e.journeesEcrites ?? 0} / ${e.journeesRestantes ?? 0}` }
                ])}
              </div>

              {/* Tâche Sondage des accès */}
              <div className={`cron-task-card ${expandedCronTasks.accessProbe ? 'expanded' : 'collapsed'}`}>
                <div className="cron-task-header" onClick={() => toggleCronTask('accessProbe')} style={{ cursor: 'pointer' }}>
                  <div className="cron-task-title">
                    {expandedCronTasks.accessProbe ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <div>
                      <h3>Sondage des accès aux comptes</h3>
                      <p className="cron-schedule">
                        Planifié tous les jours à 8h00
                      </p>
                    </div>
                  </div>
                  <div className="cron-task-status">
                    {cronStatus.tasks?.accessProbe?.isRunning ? (
                      <span className="status-badge running">
                        <Loader2 size={14} className="spin" />
                        En cours
                      </span>
                    ) : estEnEchec(cronStatus.tasks?.accessProbe) ? (
                      <span className="status-badge error">
                        <XCircle size={14} />
                        Erreur
                      </span>
                    ) : cronStatus.tasks?.accessProbe?.lastSuccess ? (
                      <span className="status-badge success">
                        <CheckCircle size={14} />
                        OK
                      </span>
                    ) : (
                      <span className="status-badge idle">
                        <Clock size={14} />
                        En attente
                      </span>
                    )}
                  </div>
                </div>

                <div className="cron-task-details">
                  <div className="cron-detail">
                    <span className="label">Dernière exécution :</span>
                    <span className="value">
                      {cronStatus.tasks?.accessProbe?.lastRun
                        ? new Date(cronStatus.tasks?.accessProbe.lastRun).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Dernier succès :</span>
                    <span className="value success">
                      {cronStatus.tasks?.accessProbe?.lastSuccess
                        ? new Date(cronStatus.tasks?.accessProbe.lastSuccess).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Comptes sondés :</span>
                    <span className="value">{cronStatus.tasks?.accessProbe?.comptesSondes ?? 0}</span>
                  </div>
                  <div className="cron-detail">
                    {/* Un accès refusé n'est pas une panne de la tâche : c'est son résultat. */}
                    <span className="label">Accès refusés / rétablis :</span>
                    <span className="value">
                      {cronStatus.tasks?.accessProbe?.accesRefuses ?? 0} / {cronStatus.tasks?.accessProbe?.accesRetablis ?? 0}
                    </span>
                  </div>
                  {(cronStatus.tasks?.accessProbe?.indetermines ?? 0) > 0 && (
                    <div className="cron-detail">
                      {/* Ni ouvert ni refusé : la sonde n'a pas pu conclure (listing indisponible,
                          quota, réseau). À distinguer d'un parc intact. */}
                      <span className="label">Indéterminés :</span>
                      <span className="value" style={{ color: '#f59e0b' }}>
                        {cronStatus.tasks.accessProbe.indetermines}
                      </span>
                    </div>
                  )}
                  {Object.keys(cronStatus.tasks?.accessProbe?.parConnecteur || {}).length > 0 && (
                    <div className="cron-detail" style={{ alignItems: 'flex-start' }}>
                      <span className="label">Par connecteur :</span>
                      <span className="value" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {Object.entries(cronStatus.tasks.accessProbe.parConnecteur)
                          .sort((a, b) => b[1].comptes - a[1].comptes)
                          .map(([type, detail]) => (
                            <span
                              key={type}
                              title={`${detail.comptes} compte(s) — ${detail.ouverts} ouvert(s), ${detail.refuses} refusé(s), ${detail.indetermines} indéterminé(s)`}
                              style={{
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '11px',
                                backgroundColor: detail.refuses > 0 ? '#fee2e2' : detail.indetermines > 0 ? '#fef3c7' : '#f1f5f9',
                                color: detail.refuses > 0 ? '#b91c1c' : detail.indetermines > 0 ? '#92400e' : '#475569'
                              }}
                            >
                              {libellePlateforme(type)} {detail.ouverts}/{detail.comptes}
                            </span>
                          ))}
                      </span>
                    </div>
                  )}
                  {cronStatus.tasks?.accessProbe?.lastError && (
                    <div className="cron-detail">
                      <span className="label">
                        {estEnEchec(cronStatus.tasks?.accessProbe) ? 'Erreur :' : 'Dernière erreur (résolue depuis) :'}
                      </span>
                      <span
                        className={estEnEchec(cronStatus.tasks?.accessProbe) ? 'value error' : 'value'}
                        style={estEnEchec(cronStatus.tasks?.accessProbe) ? undefined : { color: '#9ca3af' }}
                      >
                        {cronStatus.tasks?.accessProbe.lastError.message || 'Erreur inconnue'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="cron-task-actions">
                  <button
                    className="trigger-cron-btn"
                    onClick={() => triggerCron('accessProbe')}
                    disabled={triggeringCron || cronStatus.tasks?.accessProbe?.isRunning}
                  >
                    {triggeringCron || cronStatus.tasks?.accessProbe?.isRunning ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        Exécution en cours...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Exécuter maintenant
                      </>
                    )}
                  </button>
                </div>

                {rendreHistoriqueCron('accessProbe', [
                  { titre: 'Sondés', valeur: e => e.comptes ?? 0 },
                  { titre: 'Refusés', valeur: e => e.refuses ?? 0 },
                  { titre: 'Rétablis', valeur: e => e.retablis ?? 0 },
                  { titre: 'Indéterminés', valeur: e => e.indetermines ?? 0 }
                ])}
              </div>

              {/* Tâche Analyse des flux (Optimiseur de flux) */}
              <div className={`cron-task-card ${expandedCronTasks.feedAnalyses ? 'expanded' : 'collapsed'}`}>
                <div className="cron-task-header" onClick={() => toggleCronTask('feedAnalyses')} style={{ cursor: 'pointer' }}>
                  <div className="cron-task-title">
                    {expandedCronTasks.feedAnalyses ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <div>
                      <h3>Analyse des flux (Optimiseur de flux)</h3>
                      <p className="cron-schedule">
                        Planifié tous les jours à 6h30
                      </p>
                    </div>
                  </div>
                  <div className="cron-task-status">
                    {cronStatus.tasks?.feedAnalyses?.isRunning ? (
                      <span className="status-badge running">
                        <Loader2 size={14} className="spin" />
                        En cours
                      </span>
                    ) : estEnEchec(cronStatus.tasks?.feedAnalyses) ? (
                      <span className="status-badge error">
                        <XCircle size={14} />
                        Erreur
                      </span>
                    ) : cronStatus.tasks?.feedAnalyses?.lastSuccess ? (
                      <span className="status-badge success">
                        <CheckCircle size={14} />
                        OK
                      </span>
                    ) : (
                      <span className="status-badge idle">
                        <Clock size={14} />
                        En attente
                      </span>
                    )}
                  </div>
                </div>

                <div className="cron-task-details">
                  <div className="cron-detail">
                    <span className="label">Dernière exécution :</span>
                    <span className="value">
                      {cronStatus.tasks?.feedAnalyses?.lastRun
                        ? new Date(cronStatus.tasks?.feedAnalyses.lastRun).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Dernier succès :</span>
                    <span className="value success">
                      {cronStatus.tasks?.feedAnalyses?.lastSuccess
                        ? new Date(cronStatus.tasks?.feedAnalyses.lastSuccess).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    {/* Les deux chiffres, parce qu'un compte sert souvent plusieurs flux : les
                        confondre ferait croire à un parc deux fois plus grand. */}
                    <span className="label">Flux analysés / comptes lus :</span>
                    <span className="value">
                      {cronStatus.tasks?.feedAnalyses?.fluxAnalyses ?? 0} / {cronStatus.tasks?.feedAnalyses?.comptesLus ?? 0}
                    </span>
                  </div>
                  {(cronStatus.tasks?.feedAnalyses?.fluxEnEchec ?? 0) > 0 && (
                    <div className="cron-detail">
                      {/* Un flux en échec — accès Merchant révoqué, quota — n'empêche pas les
                          autres : c'est un résultat partiel, pas une panne de la tâche. */}
                      <span className="label">Flux en échec :</span>
                      <span className="value" style={{ color: '#f59e0b' }}>
                        {cronStatus.tasks.feedAnalyses.fluxEnEchec}
                      </span>
                    </div>
                  )}
                  {cronStatus.tasks?.feedAnalyses?.lastError && (
                    <div className="cron-detail">
                      <span className="label">
                        {estEnEchec(cronStatus.tasks?.feedAnalyses) ? 'Erreur :' : 'Dernière erreur (résolue depuis) :'}
                      </span>
                      <span
                        className={estEnEchec(cronStatus.tasks?.feedAnalyses) ? 'value error' : 'value'}
                        style={estEnEchec(cronStatus.tasks?.feedAnalyses) ? undefined : { color: '#9ca3af' }}
                      >
                        {cronStatus.tasks?.feedAnalyses.lastError.message || 'Erreur inconnue'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="cron-task-actions">
                  <button
                    className="trigger-cron-btn"
                    onClick={() => triggerCron('feedAnalyses')}
                    disabled={triggeringCron || cronStatus.tasks?.feedAnalyses?.isRunning}
                  >
                    {triggeringCron || cronStatus.tasks?.feedAnalyses?.isRunning ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        Exécution en cours...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Exécuter maintenant
                      </>
                    )}
                  </button>
                </div>

                {rendreHistoriqueCron('feedAnalyses', [
                  { titre: 'Flux', valeur: e => e.flux ?? 0 },
                  { titre: 'Comptes', valeur: e => e.comptes ?? 0 },
                  { titre: 'Échecs', valeur: e => e.echecs ?? 0 }
                ])}
              </div>

              {/* Tâche Alertes de dépassement */}
              <div className={`cron-task-card ${expandedCronTasks.metricAlerts ? 'expanded' : 'collapsed'}`}>
                <div className="cron-task-header" onClick={() => toggleCronTask('metricAlerts')} style={{ cursor: 'pointer' }}>
                  <div className="cron-task-title">
                    {expandedCronTasks.metricAlerts ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    <div>
                      <h3>Alertes de dépassement (clics, budget)</h3>
                      <p className="cron-schedule">
                        Planifié toutes les heures
                      </p>
                    </div>
                  </div>
                  <div className="cron-task-status">
                    {estEnEchec(cronStatus.tasks?.metricAlerts) ? (
                      <span className="status-badge error">
                        <XCircle size={14} />
                        Erreur
                      </span>
                    ) : cronStatus.tasks?.metricAlerts?.lastSuccess ? (
                      <span className="status-badge success">
                        <CheckCircle size={14} />
                        OK
                      </span>
                    ) : (
                      <span className="status-badge idle">
                        <Clock size={14} />
                        En attente
                      </span>
                    )}
                  </div>
                </div>

                <div className="cron-task-details">
                  <div className="cron-detail">
                    <span className="label">Dernière exécution :</span>
                    <span className="value">
                      {cronStatus.tasks?.metricAlerts?.lastRun
                        ? new Date(cronStatus.tasks?.metricAlerts.lastRun).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Dernier succès :</span>
                    <span className="value success">
                      {cronStatus.tasks?.metricAlerts?.lastSuccess
                        ? new Date(cronStatus.tasks?.metricAlerts.lastSuccess).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  <div className="cron-detail">
                    <span className="label">Comptes analysés :</span>
                    <span className="value">
                      {cronStatus.tasks?.metricAlerts?.accountsScanned || 0}
                    </span>
                  </div>
                  {/* L'historique porte le détail utile : combien d'alertes ont été
                      ouvertes et fermées au dernier passage. */}
                  {cronStatus.tasks?.metricAlerts?.history?.[0] && (
                    <div className="cron-detail">
                      <span className="label">Dernier passage :</span>
                      <span className="value">
                        {cronStatus.tasks.metricAlerts.history[0].alertsOpened || 0} ouverte(s),{' '}
                        {cronStatus.tasks.metricAlerts.history[0].alertsClosed || 0} fermée(s)
                      </span>
                    </div>
                  )}
                  {cronStatus.tasks?.metricAlerts?.lastError && (
                    <div className="cron-detail">
                      {/* Une erreur qu'un succès plus récent a déjà dépassée est un souvenir,
                          pas un état : on la garde — elle renseigne — mais en gris, et nommée
                          comme telle. */}
                      <span className="label">
                        {estEnEchec(cronStatus.tasks?.metricAlerts) ? 'Erreur :' : 'Dernière erreur (résolue depuis) :'}
                      </span>
                      <span
                        className={estEnEchec(cronStatus.tasks?.metricAlerts) ? 'value error' : 'value'}
                        style={estEnEchec(cronStatus.tasks?.metricAlerts) ? undefined : { color: '#9ca3af' }}
                      >
                        {cronStatus.tasks?.metricAlerts.lastError.message || 'Erreur inconnue'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="cron-task-actions">
                  <button
                    className="trigger-cron-btn"
                    onClick={() => triggerCron('metricAlerts')}
                    disabled={triggeringCron}
                  >
                    {triggeringCron ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        Exécution en cours...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Exécuter maintenant
                      </>
                    )}
                  </button>
                </div>

                {rendreHistoriqueCron('metricAlerts', [
                  { titre: 'Comptes', valeur: e => e.accountsScanned ?? '-' },
                  { titre: 'Alertes', valeur: e => `${e.alertsOpened ?? 0} ouverte(s), ${e.alertsClosed ?? 0} fermée(s)` }
                ])}
              </div>

              {/* Info box */}
              <div className="cron-info">
                <AlertCircle size={16} />
                <div>
                  <h4>Informations</h4>
                  <ul>
                    <li>Les tâches CRON calculent les recommandations pour tous les comptes actifs</li>
                    <li>L'exécution peut prendre plusieurs minutes selon le nombre de comptes</li>
                    <li>Le statut est actualisé automatiquement toutes les 5 secondes</li>
                    <li>Le déclenchement manuel exécute exactement les mêmes tâches que le CRON</li>
                  </ul>
                </div>

                {rendreHistoriqueCron('digestEmails')}
              </div>
            </>
          ) : (
            <div className="cron-error">
              <XCircle size={32} />
              <p>Impossible de charger le statut des tâches CRON</p>
              <button onClick={fetchCronStatus}>Réessayer</button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'action-logs' && (
        <div className="admin-action-logs">
          <div className="action-logs-header">
            <p>Historique de toutes les actions effectuées via Kokpit et Google Ads</p>
            <button
              className="sync-google-ads-btn"
              onClick={syncAllGoogleAdsHistory}
              disabled={syncingGoogleAds}
            >
              {syncingGoogleAds ? (
                <>
                  <Loader2 size={16} className="spin" />
                  Synchronisation en cours...
                </>
              ) : (
                <>
                  <RefreshCw size={16} />
                  Synchroniser Google Ads
                </>
              )}
            </button>
          </div>

          {/* Filtres */}
          <div className="action-logs-filters">
            <div className="filter-group">
              <label><Filter size={14} /> Filtres</label>
              <select
                value={actionLogsFilter.accountId}
                onChange={(e) => handleActionLogsFilterChange('accountId', e.target.value)}
              >
                <option value="">Tous les comptes</option>
                {actionLogsFilters?.accounts?.map(account => (
                  <option key={account.id} value={account.id}>{account.name}</option>
                ))}
              </select>

              <select
                value={actionLogsFilter.source}
                onChange={(e) => handleActionLogsFilterChange('source', e.target.value)}
              >
                <option value="">Toutes les sources</option>
                {actionLogsFilters?.sources?.map(source => (
                  <option key={source.value} value={source.value}>
                    {source.label} ({source.count})
                  </option>
                ))}
              </select>

              <select
                value={actionLogsFilter.actionType}
                onChange={(e) => handleActionLogsFilterChange('actionType', e.target.value)}
              >
                <option value="">Tous les types</option>
                {actionLogsFilters?.actionTypes?.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label} ({type.count})
                  </option>
                ))}
              </select>

              {(actionLogsFilter.accountId || actionLogsFilter.source || actionLogsFilter.actionType) && (
                <button className="clear-filters-btn" onClick={clearActionLogsFilters}>
                  <X size={14} /> Effacer
                </button>
              )}
            </div>
          </div>

          {/* Table des logs */}
          <div className="action-logs-table-container">
            {actionLogsLoading ? (
              <div className="loading-container">
                <Loader2 size={32} className="spin" />
                <p>Chargement des actions...</p>
              </div>
            ) : actionLogs?.logs && actionLogs.logs.length > 0 ? (
              <>
                <table className="action-logs-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Source</th>
                      <th>Utilisateur</th>
                      <th>Compte</th>
                      <th>Action</th>
                      <th>Entité</th>
                      <th>Détails</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actionLogs.logs.map(log => (
                      <tr key={log.id}>
                        <td className="date-cell">
                          <div className="date-display">
                            <strong>{new Date(log.performedAt).toLocaleDateString('fr-FR')}</strong>
                            <small>{new Date(log.performedAt).toLocaleTimeString('fr-FR')}</small>
                          </div>
                        </td>
                        <td>
                          <span className={`source-badge ${log.source.toLowerCase()}`}>
                            {log.source === 'KOKPEET' ? '🚀 Kokpit' : '📊 Google Ads'}
                          </span>
                        </td>
                        <td>
                          <div className="user-cell">
                            <small>{log.userEmail}</small>
                          </div>
                        </td>
                        <td>
                          <div className="account-cell">
                            <strong>{log.accountName}</strong>
                            {log.clientName && <small>{log.clientName}</small>}
                          </div>
                        </td>
                        <td>
                          <span className="action-type-badge">
                            {actionLogsFilters?.actionTypes?.find(t => t.value === log.actionType)?.label || log.actionType}
                          </span>
                        </td>
                        <td>
                          <div className="entity-cell">
                            {log.entityName && <strong>{log.entityName}</strong>}
                            {log.entityType && <small>{log.entityType}</small>}
                          </div>
                        </td>
                        <td>
                          <div className="details-cell">
                            {/* Opération */}
                            {log.details?.operation && (
                              <span className="detail-tag">{log.details.operation}</span>
                            )}
                            {/* Utilisateur Google Ads */}
                            {log.details?.userEmail && log.details.userEmail !== log.userEmail && (
                              <span className="detail-tag">Par: {log.details.userEmail}</span>
                            )}
                            {/* Mot-clé depuis newValue (structure Google Ads) */}
                            {log.newValue?.campaign_criterion?.keyword?.text && (
                              <span className="detail-tag">"{log.newValue.campaign_criterion.keyword.text}"</span>
                            )}
                            {log.newValue?.ad_group_criterion?.keyword?.text && (
                              <span className="detail-tag">"{log.newValue.ad_group_criterion.keyword.text}"</span>
                            )}
                            {/* Mot-clé depuis newValue (structure Kokpit) */}
                            {log.newValue?.keyword && typeof log.newValue.keyword === 'string' && (
                              <span className="detail-tag">"{log.newValue.keyword}"</span>
                            )}
                            {/* Match type */}
                            {log.newValue?.matchType && (
                              <span className="detail-tag">Match: {log.newValue.matchType}</span>
                            )}
                            {log.details?.matchType && (
                              <span className="detail-tag">Match: {log.details.matchType}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {actionLogs.pagination && (
                  <div className="action-logs-pagination">
                    <span className="pagination-info">
                      Page {actionLogs.pagination.page} sur {actionLogs.pagination.totalPages} ({actionLogs.pagination.total} actions)
                    </span>
                    <div className="pagination-controls">
                      <button
                        onClick={() => fetchActionLogs(1, actionLogsFilter)}
                        disabled={actionLogsPage === 1 || actionLogsLoading}
                      >
                        ««
                      </button>
                      <button
                        onClick={() => fetchActionLogs(actionLogsPage - 1, actionLogsFilter)}
                        disabled={actionLogsPage === 1 || actionLogsLoading}
                      >
                        «
                      </button>
                      <span className="page-number">{actionLogsPage}</span>
                      <button
                        onClick={() => fetchActionLogs(actionLogsPage + 1, actionLogsFilter)}
                        disabled={actionLogsPage >= actionLogs.pagination.totalPages || actionLogsLoading}
                      >
                        »
                      </button>
                      <button
                        onClick={() => fetchActionLogs(actionLogs.pagination.totalPages, actionLogsFilter)}
                        disabled={actionLogsPage >= actionLogs.pagination.totalPages || actionLogsLoading}
                      >
                        »»
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="no-logs">
                <FileText size={48} />
                <h3>Aucune action enregistrée</h3>
                <p>Les actions seront enregistrées ici au fur et à mesure de leur réalisation.</p>
                <p>Cliquez sur "Synchroniser Google Ads" pour importer l'historique des modifications.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'bigquery' && (
        <div className="admin-bigquery">
          <div className="bigquery-header">
            <p>Monitoring des requêtes et des coûts Google BigQuery</p>
            <div className="bigquery-period-selector">
              <label>Période :</label>
              <select
                value={bigQueryPeriod}
                onChange={(e) => {
                  setBigQueryPeriod(e.target.value);
                  fetchBigQueryStats(e.target.value);
                }}
              >
                <option value="24h">Dernières 24h</option>
                <option value="7d">7 derniers jours</option>
                <option value="30d">30 derniers jours</option>
              </select>
              <button
                className="refresh-btn"
                onClick={() => fetchBigQueryStats(bigQueryPeriod)}
                disabled={bigQueryLoading}
              >
                {bigQueryLoading ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
                Actualiser
              </button>
            </div>
          </div>

          {bigQueryLoading && !bigQueryStats ? (
            <div className="bigquery-loading">
              <Loader2 size={32} className="spin" />
              <p>Chargement des statistiques...</p>
            </div>
          ) : bigQueryStats ? (
            <>
              {/* Cartes de statistiques */}
              <div className="bigquery-stats-grid">
                <div className="bigquery-stat-card">
                  <div className="stat-icon cost">
                    <DollarSign size={24} />
                  </div>
                  <div className="stat-content">
                    <h3>Coût Estimé</h3>
                    <p className="stat-number">${parseFloat(bigQueryStats.stats?.totalCost || 0).toFixed(4)}</p>
                    <small className="stat-detail">{bigQueryStats.stats?.queryCount || 0} requêtes</small>
                  </div>
                </div>

                <div className="bigquery-stat-card">
                  <div className="stat-icon data">
                    <HardDrive size={24} />
                  </div>
                  <div className="stat-content">
                    <h3>Données Scannées</h3>
                    <p className="stat-number">{bigQueryStats.stats?.totalGB || '0.00'} GB</p>
                    <small className="stat-detail">{bigQueryStats.stats?.totalMB || '0'} MB</small>
                  </div>
                </div>

                <div className="bigquery-stat-card">
                  <div className="stat-icon trend">
                    <TrendingUp size={24} />
                  </div>
                  <div className="stat-content">
                    <h3>Moyenne / Requête</h3>
                    <p className="stat-number">
                      {bigQueryStats.stats?.queryCount > 0
                        ? (parseFloat(bigQueryStats.stats?.totalMB || 0) / bigQueryStats.stats.queryCount).toFixed(2)
                        : '0'} MB
                    </p>
                    <small className="stat-detail">
                      ~${bigQueryStats.stats?.queryCount > 0
                        ? (parseFloat(bigQueryStats.stats?.totalCost || 0) / bigQueryStats.stats.queryCount).toFixed(6)
                        : '0'}/req
                    </small>
                  </div>
                </div>
              </div>

              {/* Histogramme quotidien */}
              {bigQueryStats.daily && bigQueryStats.daily.length > 0 && (
                <div className="bigquery-chart-container">
                  <h3>Coûts Quotidiens</h3>
                  <div className="bigquery-chart">
                    {(() => {
                      const maxCost = Math.max(...bigQueryStats.daily.map(d => parseFloat(d.cost)));
                      const maxHeight = 120;

                      return (
                        <div className="bigquery-bars">
                          {bigQueryStats.daily.map((day) => {
                            const heightPx = maxCost > 0 ? (parseFloat(day.cost) / maxCost) * maxHeight : 0;
                            const formattedDate = new Date(day.date).toLocaleDateString('fr-FR', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short'
                            });

                            return (
                              <div key={day.date} className="bigquery-bar-wrapper" title={`${formattedDate}: $${parseFloat(day.cost).toFixed(4)} (${day.queries} req)`}>
                                <div className="bigquery-bar" style={{ height: `${heightPx}px` }}>
                                  <span className="bar-value">${parseFloat(day.cost).toFixed(3)}</span>
                                </div>
                                <div className="bar-label">{new Date(day.date).getDate()}</div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Répartition par type de requête */}
              {bigQueryStats.byQueryType && bigQueryStats.byQueryType.length > 0 && (
                <div className="bigquery-breakdown">
                  <h3>Répartition par Type de Requête</h3>
                  <table className="bigquery-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Requêtes</th>
                        <th>Données</th>
                        <th>Coût</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bigQueryStats.byQueryType.map(type => {
                        const typeLabels = {
                          'sync': '🔄 Synchronisation',
                          'dashboard': '📊 Dashboard',
                          'expensive_query': '💰 Coûteuse',
                          'session': '📋 Session',
                          'timeline': '📈 Timeline',
                          'campaigns': '📢 Campagnes',
                          'keywords': '🔑 Mots-clés',
                          'audiences': '👥 Audiences',
                          'geography': '🌍 Géographie',
                          'search_terms': '🔍 Termes recherche',
                          'devices': '📱 Appareils',
                          'products': '🛍️ Produits',
                          'ad_groups': '📁 Groupes annonces',
                          'demographics': '👤 Démographie',
                          'age_ranges': '🎂 Tranches âge',
                          'gender': '⚧ Genre',
                          'weekdays': '📅 Jours semaine',
                          'asset_groups': '🎨 Groupes assets',
                          'sync_status': '📋 Statut sync',
                          'sync_delete': '🗑️ Nettoyage sync',
                          'sync_check': '✅ Vérif sync',
                          'landing_pages': '🔗 Pages destination',
                          'unknown': '❓ Autre',
                        };
                        return (
                        <tr key={type.queryType}>
                          <td>
                            <span className="query-type-badge">
                              {typeLabels[type.queryType] || type.queryType}
                            </span>
                          </td>
                          <td>{type._count || 0}</td>
                          <td>{(parseFloat(type._sum?.bytesProcessed || 0) / 1024 / 1024).toFixed(2)} MB</td>
                          <td>${parseFloat(type._sum?.estimatedCost || 0).toFixed(4)}</td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tables BigQuery */}
              {bigQueryTables && bigQueryTables.tables && bigQueryTables.tables.length > 0 && (
                <div className="bigquery-tables">
                  <h3>Tables BigQuery <span className="dataset-badge">{bigQueryTables.dataset}</span></h3>
                  <table className="bigquery-table">
                    <thead>
                      <tr>
                        <th>Table</th>
                        <th style={{ textAlign: 'right' }}>Taille</th>
                        <th style={{ textAlign: 'right' }}>Lignes</th>
                        <th>Partitionnement</th>
                        <th>Clustering</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bigQueryTables.tables.map(table => (
                        <tr key={table.name}>
                          <td>
                            <strong>{table.name}</strong>
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{table.sizeFormatted}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{table.rowCount?.toLocaleString('fr-FR') || '0'}</td>
                          <td>
                            {table.partitioning ? (
                              <span className="partitioning-badge active">
                                <CheckCircle size={12} />
                                {table.partitioning}
                              </span>
                            ) : (
                              <span className="partitioning-badge inactive">
                                <XCircle size={12} />
                                Non
                              </span>
                            )}
                          </td>
                          <td>
                            {table.clustering ? (
                              <span className="clustering-badge">
                                {table.clustering.join(', ')}
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {bigQueryTables.totals && (
                      <tfoot>
                        <tr className="totals-row">
                          <td><strong>TOTAL ({bigQueryTables.totals.tableCount} tables)</strong></td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                            <strong>{bigQueryTables.totals.totalBytesFormatted}</strong>
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                            <strong>{bigQueryTables.totals.totalRowsFormatted}</strong>
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}

              {/* Info box */}
              <div className="bigquery-info">
                <AlertCircle size={16} />
                <div>
                  <h4>Informations Tarification</h4>
                  <ul>
                    <li>BigQuery facture $6.25 par TB de données scannées (tarif on-demand)</li>
                    <li>Le partitionnement par date réduit les coûts en limitant les données scannées</li>
                    <li>Le clustering améliore les performances des requêtes filtrées</li>
                    <li>Les statistiques sont mises à jour à chaque requête significative (&gt;10 MB)</li>
                  </ul>
                </div>
              </div>
            </>
          ) : (
            <div className="bigquery-no-data">
              <Database size={48} />
              <h3>Aucune donnée de coût disponible</h3>
              <p>Les statistiques de coût apparaîtront ici après les premières requêtes BigQuery.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'quotas' && (
        <div className="admin-quotas">
          <div className="quotas-header">
            {/*
              Ni « temps réel », ni « suivi » : cet onglet ne se rafraîchit pas tout seul. Le
              promettre laissait croire qu'une page ouverte depuis une heure montrait l'heure
              qu'il est, alors qu'elle montre l'heure du dernier clic sur Actualiser.
            */}
            <p>Consommation relevée au dernier rafraîchissement</p>
            <button
              className="refresh-btn"
              onClick={() => { fetchQuotaStatus(); fetchResendUsage(); fetchClaudeUsage(); }}
              disabled={quotaLoading || resendLoading || claudeLoading}
            >
              {(quotaLoading || resendLoading || claudeLoading) ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
              Actualiser
            </button>
          </div>

          {/*
            Les trois quotas côte à côte : une vision synthétique immédiate, puis le détail au
            clic. Les trois détails étaient empilés, soit plusieurs écrans de défilement avant
            de savoir si quelque chose allait mal.
          */}
          <JaugesQuotas
            resendUsage={resendUsage}
            resendLoading={resendLoading}
            claudeUsage={claudeUsage}
            claudeLoading={claudeLoading}
            quotaStatus={quotaStatus}
            quotaLoading={quotaLoading}
            selection={detailQuota}
            onSelectionner={ouvrirDetailQuota}
          />

          {!detailQuota && (
            /* Ni une erreur ni un vide : l'état normal de cet écran est de tenir en trois
               jauges. La ligne dit où est le reste, sans en occuper la place. */
            <p className="quotas-invite">
              Cliquez sur une jauge pour en déplier le détail — historique, ventilation et erreurs.
            </p>
          )}

          {/*
            L'interrupteur de la génération de dashboards sur mesure.

            Il est ici, et non dans l'onglet MCP, parce que ce qu'il commande est une **dépense** :
            une génération coûte une dizaine d'analyses, et c'est juste au-dessus que se lit ce
            qu'elle a coûté ce mois-ci. L'onglet MCP, lui, autorise des comptes en écriture — une
            autre question.

            ⚠️ Il est **fermé par défaut** : un interrupteur dont le défaut est « ouvert » n'en
            est pas un, la fonctionnalité serait en service dès le déploiement sans que personne
            l'ait décidé, et la facture est chez l'éditeur.

            ⚠️ **Et il ne s'affiche que pour l'éditeur** : c'est lui seul que la route laisse
            passer. Montrer l'interrupteur à l'administrateur d'une organisation cliente
            promettrait un réglage qu'il ne peut ni lire ni actionner.
          */}
          {estEditeur && generationSurMesure && (
            <div className="quota-card generation-sur-mesure">
              <div className="quota-info">
                <h3>Génération de dashboards sur mesure</h3>
                {generationSurMesure.erreur ? (
                  <p className="quota-status-text">
                    État illisible : {generationSurMesure.erreur}
                  </p>
                ) : (
                  <>
                    <p className="quota-status-text">
                      {generationSurMesure.ouverte
                        ? `Ouverte — l'éditeur peut générer un dashboard en conversant avec ${generationSurMesure.modele}.`
                        : 'Fermée — le bouton « Générer avec Claude » n\'apparaît pas, et la route refuse en 403.'}
                    </p>
                    <p className="quota-detail-ligne">
                      Plafonds mensuels : {generationSurMesure.plafonds?.generations} générations,
                      {' '}{generationSurMesure.plafonds?.dollars} $ de dépense. Le plafond en
                      dollars est le seul qui borne vraiment la facture — il ne dépend d'aucune
                      estimation par génération.
                    </p>
                  </>
                )}
              </div>
              {!generationSurMesure.erreur && (
                <button
                  type="button"
                  className={generationSurMesure.ouverte ? 'refresh-btn' : 'refresh-btn primaire'}
                  disabled={generationEnBascule}
                  onClick={async () => {
                    setGenerationEnBascule(true);
                    try {
                      const { data } = await axios.put(
                        `${API_URL}/api/dashboards-sur-mesure/generation/interrupteur`,
                        { ouverte: !generationSurMesure.ouverte },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      setGenerationSurMesure(prev => ({ ...prev, ouverte: data.ouverte }));
                    } catch (e) {
                      setGenerationSurMesure(prev => ({
                        ...prev, erreur: e.response?.data?.error || e.message
                      }));
                    } finally {
                      setGenerationEnBascule(false);
                    }
                  }}
                >
                  {generationEnBascule
                    ? <Loader2 size={16} className="spin" />
                    : (generationSurMesure.ouverte ? 'Fermer' : 'Ouvrir')}
                </button>
              )}
            </div>
          )}

          {/*
            Emails Resend.

            Posé **hors** du bloc conditionnel qui suit : celui-ci est tout entier suspendu à
            `quotaStatus.quotaStats`, si bien qu'une panne du côté Google Ads effacerait aussi
            les emails, qui n'y sont pour rien.
          */}
          {detailQuota === 'resend' && (() => {
            if (resendLoading && !resendUsage) {
              return (
                <div className="quota-card resend ok">
                  <div className="quota-icon"><Loader2 size={48} className="spin" /></div>
                  <div className="quota-info">
                    <h3>Resend — emails transactionnels</h3>
                    <p className="quota-status-text">Lecture de la consommation…</p>
                  </div>
                </div>
              );
            }
            if (!resendUsage) return null;

            if (resendUsage.success === false) {
              return (
                <div className="quota-card resend error">
                  <div className="quota-icon"><AlertCircle size={48} /></div>
                  <div className="quota-info">
                    <h3>Resend — emails transactionnels</h3>
                    <p className="quota-status-text">{resendUsage.error}</p>
                  </div>
                </div>
              );
            }

            if (!resendUsage.configure) {
              return (
                <div className="quota-card resend warning">
                  <div className="quota-icon"><Mail size={48} /></div>
                  <div className="quota-info">
                    <h3>Resend — emails transactionnels</h3>
                    <p className="quota-status-text">
                      Aucune clé <code>RESEND_API_KEY</code> sur ce serveur : les emails sont
                      journalisés, pas envoyés. C'est le cas en développement.
                    </p>
                  </div>
                </div>
              );
            }

            const { mois, jour, debit } = resendUsage;
            const pct = mois?.pourcentage ?? 0;
            const etat = mois?.illimite ? 'ok' : pct >= 90 ? 'exhausted' : pct >= 75 ? 'warning' : 'ok';
            const remplissage = pct >= 90 ? 'critical' : pct >= 75 ? 'warning' : 'ok';
            const dateCourte = (iso) => iso
              ? new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
              : null;

            return (
              <div className={`quota-card resend ${etat}`}>
                <div className="quota-icon"><Mail size={48} /></div>
                <div className="quota-info">
                  <h3>Resend — emails transactionnels</h3>
                  <p className="quota-status-text">
                    {etat === 'ok' && 'Plafond d\'envoi disponible'}
                    {etat === 'warning' && 'Attention — plus de 75 % du forfait mensuel consommé'}
                    {etat === 'exhausted' && 'Critique — plus de 90 % du forfait mensuel consommé'}
                  </p>

                  {mois && !mois.illimite ? (
                    <div className="quota-progress-container">
                      <div className="quota-progress-bar">
                        <div
                          className={`quota-progress-fill ${remplissage}`}
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                      <div className="quota-progress-text">
                        <span>
                          {mois.utilises.toLocaleString('fr-FR')} / {mois.plafond.toLocaleString('fr-FR')} emails
                          {' '}
                          {/*
                            « période de facturation », et non « ce mois-ci » : Resend compte
                            depuis l'anniversaire de l'abonnement. Relevé un 15 octobre, la
                            remise à zéro tombait le 16 à 17 h 41, pas le 1er novembre.
                          */}
                          <span className="stat-fenetre">sur la période de facturation</span>
                        </span>
                        <span>{pct} %</span>
                      </div>
                    </div>
                  ) : mois?.illimite ? (
                    <div className="quota-unlimited-badge">Envois illimités</div>
                  ) : null /* pas de compteur mensuel rendu : ne rien affirmer */}

                  <div className="quota-stats-row">
                    <div className="quota-stat">
                      <span className="stat-value">{(mois?.envoyes ?? 0).toLocaleString('fr-FR')}</span>
                      <span className="stat-label">Emails adressés <span className="stat-fenetre">(période)</span></span>
                    </div>
                    <div className="quota-stat">
                      <span className="stat-value">
                        {(jour?.envoyes ?? 0).toLocaleString('fr-FR')}
                        {jour && !jour.illimite && (
                          <span className="stat-fenetre"> / {jour.plafond.toLocaleString('fr-FR')}</span>
                        )}
                      </span>
                      <span className="stat-label">Emails adressés <span className="stat-fenetre">(aujourd'hui)</span></span>
                    </div>
                    <div className="quota-stat">
                      <span className="stat-value">
                        {mois && !mois.illimite ? (mois.plafond - mois.utilises).toLocaleString('fr-FR') : '∞'}
                      </span>
                      <span className="stat-label">Restants <span className="stat-fenetre">(période)</span></span>
                    </div>
                  </div>

                  {/*
                    ⚠️ `used` n'est pas `sent` : la réception compte aussi dans le quota. Les
                    deux sont égaux tant que le domaine n'accepte pas de courrier entrant — le
                    dire seulement quand ils divergent, sinon c'est du bruit permanent.
                  */}
                  {mois && mois.recus > 0 && (
                    <small className="quota-checked">
                      Dont {mois.recus.toLocaleString('fr-FR')} email{mois.recus > 1 ? 's' : ''} reçu{mois.recus > 1 ? 's' : ''}, qui comptent aussi dans le forfait.
                    </small>
                  )}

                  <small className="quota-checked">
                    {mois?.reinitialisationLe && <>Forfait remis à zéro le {dateCourte(mois.reinitialisationLe)}. </>}
                    {jour?.reinitialisationLe && <>Plafond du jour le {dateCourte(jour.reinitialisationLe)}. </>}
                    {debit?.limit && <>Débit : {debit.limit} envois / {debit.duration}. </>}
                    {resendUsage.releveA && <>Relevé à {new Date(resendUsage.releveA).toLocaleTimeString('fr-FR')}.</>}
                  </small>
                </div>
              </div>
            );
          })()}


          {/*
            Consommation Claude.

            Hors du bloc conditionnel du quota Google Ads, pour la même raison que Resend :
            celui-ci est suspendu à `quotaStatus.quotaStats`, et une panne de la régie
            effacerait un relevé qui n'y est pour rien.

            ⚠️ **Aucune barre de progression, et c'est volontaire.** Anthropic facture à l'usage :
            il n'y a pas de plafond à lire, donc rien à remplir. Une barre supposerait un
            maximum que personne ne nous a donné.
          */}
          {detailQuota === 'claude' && (() => {
            if (claudeLoading && !claudeUsage) {
              return (
                <div className="quota-card claude ok">
                  <div className="quota-icon"><Loader2 size={48} className="spin" /></div>
                  <div className="quota-info">
                    <h3>Claude — jetons et dépense</h3>
                    <p className="quota-status-text">Lecture de la consommation…</p>
                  </div>
                </div>
              );
            }
            if (!claudeUsage) return null;

            if (claudeUsage.success === false) {
              return (
                <div className="quota-card claude error">
                  <div className="quota-icon"><AlertCircle size={48} /></div>
                  <div className="quota-info">
                    <h3>Claude — jetons et dépense</h3>
                    <p className="quota-status-text">{claudeUsage.error}</p>
                  </div>
                </div>
              );
            }

            const { anthropic, interne } = claudeUsage;
            const nombre = (valeur) => (valeur || 0).toLocaleString('fr-FR');
            const releve = anthropic?.configure && anthropic?.success !== false ? anthropic : null;

            /*
              Trois états de non-relevé, et trois messages différents : sans clé, avec une clé
              qui n'ouvre pas l'Admin API, et en panne. Les fondre en « indisponible »
              laisserait chercher une panne là où il n'y a qu'un réglage à faire.
            */
            const carteAnthropic = () => {
              if (releve) {
                return (
                  <div className="quota-card claude ok">
                    <div className="quota-icon"><Sparkles size={48} /></div>
                    <div className="quota-info">
                      <h3>Claude — relevé d'Anthropic</h3>
                      {/*
                        ⚠️ **Le périmètre se dit, il ne se devine pas.** Sans
                        `ANTHROPIC_WORKSPACE_ID`, ce relevé compte aussi les essais, Claude Code
                        et tout autre projet de la même organisation — un chiffre faux qui reste
                        plausible, donc qui ne se trahit pas.
                      */}
                      <p className="quota-status-text">
                        {releve.espaceDeTravail
                          ? `Consommation facturée de l'espace de travail Kokpit, sur ${releve.jours} jours`
                          : `Consommation facturée de toute l'organisation — essais et autres projets compris — sur ${releve.jours} jours`}
                      </p>

                      <div className="quota-stats-row">
                        <div className="quota-stat">
                          <span className="stat-value">{nombre(releve.jetons.total)}</span>
                          <span className="stat-label">Jetons <span className="stat-fenetre">({releve.jours} j)</span></span>
                        </div>
                        <div className="quota-stat">
                          <span className="stat-value">
                            {/*
                              Une dépense qu'on n'a pas trouvée n'est pas une dépense nulle. Le
                              tiret se lit comme une absence ; « 0,00 » se lirait comme un fait.
                            */}
                            {releve.depense.introuvable ? '—' : (
                              <>
                                {releve.depense.montant.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                <span className="stat-fenetre"> {releve.depense.devise}</span>
                              </>
                            )}
                          </span>
                          <span className="stat-label">Dépense <span className="stat-fenetre">({releve.jours} j)</span></span>
                        </div>
                        <div className="quota-stat">
                          <span className="stat-value">{nombre(releve.jetons.sortie)}</span>
                          <span className="stat-label">Jetons de sortie</span>
                        </div>
                      </div>

                      {/*
                        Les trois familles d'entrée séparément : un jeton lu dans le cache vaut
                        un dixième d'un jeton d'entrée, et l'écriture du cache un peu plus qu'un
                        jeton plein. Un total unique cacherait le levier le moins coûteux.
                      */}
                      <div className="quota-stats-row">
                        <div className="quota-stat">
                          <span className="stat-value">{nombre(releve.jetons.entree)}</span>
                          <span className="stat-label">Entrée <span className="stat-fenetre">(hors cache)</span></span>
                        </div>
                        <div className="quota-stat">
                          <span className="stat-value">{nombre(releve.jetons.creationCache)}</span>
                          <span className="stat-label">Écriture du cache</span>
                        </div>
                        <div className="quota-stat">
                          <span className="stat-value">{nombre(releve.jetons.lectureCache)}</span>
                          <span className="stat-label">Lecture du cache</span>
                        </div>
                        {releve.jetons.rechercheWeb > 0 && (
                          <div className="quota-stat">
                            <span className="stat-value">{nombre(releve.jetons.rechercheWeb)}</span>
                            <span className="stat-label">Recherches web</span>
                          </div>
                        )}
                      </div>

                      {/*
                        La dépense par nature n'est montrée que si elle a plus d'un poste : sur un
                        compte qui ne fait que des messages, répéter « jetons : tout » est du bruit.
                      */}
                      {releve.parNature.length > 1 && (
                        <small className="quota-checked">
                          Dont {releve.parNature.map((n) => (
                            `${n.nature} ${n.montant.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${releve.depense.devise}`
                          )).join(' · ')}.
                        </small>
                      )}

                      {releve.depense.introuvable && (
                        <small className="quota-checked">
                          ⚠️ Des jetons ont été consommés, mais aucune ligne de coût ne porte
                          l'espace de travail <code>{releve.espaceDeTravail}</code>. Vérifiez
                          que <code>ANTHROPIC_WORKSPACE_ID</code> désigne bien l'espace où
                          travaille la clé de l'application : la dépense est introuvable, et
                          non nulle.
                        </small>
                      )}

                      <small className="quota-checked">
                        La dépense vient du rapport de coûts d'Anthropic, jamais d'une grille de
                        prix tenue ici : elle suit les tarifs sans qu'on y touche.
                        {releve.releveA && <> Relevé à {new Date(releve.releveA).toLocaleTimeString('fr-FR')}.</>}
                      </small>
                    </div>
                  </div>
                );
              }

              if (anthropic?.configure === false) {
                return (
                  <div className="quota-card claude warning">
                    <div className="quota-icon"><Sparkles size={48} /></div>
                    <div className="quota-info">
                      <h3>Claude — relevé d'Anthropic</h3>
                      <p className="quota-status-text">
                        Aucune clé <code>ANTHROPIC_ADMIN_KEY</code> sur ce serveur : les jetons
                        facturés ne sont pas lisibles. C'est le cas en développement.
                      </p>
                    </div>
                  </div>
                );
              }

              return (
                <div className="quota-card claude warning">
                  <div className="quota-icon"><AlertCircle size={48} /></div>
                  <div className="quota-info">
                    <h3>Claude — relevé d'Anthropic</h3>
                    {anthropic?.espaceInvalide ? (
                      <>
                        {/*
                          ⚠️ Les deux identifiants se ressemblent et portent le même nom dans
                          la Console. Le préfixe est le seul discriminant, et c'est lui qu'on
                          montre.
                        */}
                        <p className="quota-status-text">
                          <code>ANTHROPIC_WORKSPACE_ID</code> ne désigne pas un espace de travail.
                        </p>
                        <small className="quota-checked">
                          Réponse d'Anthropic : {anthropic?.error}
                        </small>
                        <small className="quota-checked">
                          Un identifiant d'espace de travail commence par
                          <code> wrkspc_</code>. <code>svac_</code> est un <strong>compte de
                          service</strong> et <code>wrkspc_</code> l'espace : dans la Console
                          ils portent souvent le même nom, et c'est la confusion la plus facile
                          à faire. Ouvrez l'espace Kokpit dans la Console — son identifiant
                          figure dans l'URL.
                        </small>
                      </>
                    ) : anthropic?.droitsInsuffisants ? (
                      <>
                        {/*
                          403 et non 401 : la clé **est** de portée Organisation et
                          s'authentifie. C'est le compte auquel elle est liée qui n'a pas le
                          droit de lire ce rapport. Renvoyer ici le conseil du 401 — « changez
                          la portée de la clé » — ferait défaire un réglage qui est juste.
                        */}
                        <p className="quota-status-text">
                          La clé est reconnue, mais le compte auquel elle est liée n'a pas le
                          droit de lire la consommation de l'organisation.
                        </p>
                        <small className="quota-checked">
                          Réponse d'Anthropic : {anthropic?.error}
                        </small>
                        {/*
                          ⚠️ **Une clé personnelle de portée Organisation n'est pas une clé
                          d'administration.** Les deux s'authentifient, mais l'API « Usage and
                          Cost » veut la seconde — créée sur une page distincte de « Clés API »,
                          et reconnaissable à son préfixe. C'est ce qui sépare le 401, qu'on
                          avait corrigé, du 403 qui a suivi.
                        */}
                        <small className="quota-checked">
                          Le geste : créer une <strong>clé d'administration</strong> sur
                          <code> platform.claude.com/settings/admin-keys</code> — « Create admin
                          key » —, dont le secret commence par <code>sk-ant-admin01-</code>.
                          Une clé personnelle de portée Organisation
                          (<code>sk-ant-usr-…</code>) ne suffit pas : elle ne porte que les
                          droits de son compte.
                        </small>
                        <small className="quota-checked">
                          Si cette page refuse la création, la réponse est là : seuls les
                          membres <strong>administrateurs</strong> peuvent en créer. Et si
                          l'organisation est un <strong>compte individuel</strong>, l'Admin API
                          n'y existe pas du tout — la ventilation ci-dessous reste alors la
                          seule mesure disponible, et elle suffit à répondre « qui consomme ».
                        </small>
                      </>
                    ) : anthropic?.cleInsuffisante ? (
                      <>
                        <p className="quota-status-text">
                          La clé en place n'ouvre pas l'Admin API d'Anthropic.
                        </p>
                        {/*
                          ⚠️ **Le message d'Anthropic est repris tel quel**, et pas seulement
                          notre conseil. Un 401 ne dit pas toujours « clé d'espace de travail » :
                          une clé d'administration **expirée** rend le même code, et notre texte
                          seul enverrait chercher une clé qu'on a déjà créée. La Console propose
                          30 jours par défaut — le cas arrivera.
                        */}
                        <small className="quota-checked">
                          Réponse d'Anthropic : {anthropic?.error}
                        </small>
                        {/*
                          ⚠️ **La variable est nommée dans TOUS les cas, jamais seulement
                          quand elle manque.** Elle ne l'était que pour le repli, et un
                          diagnostic muet dans l'autre branche est indistinguable d'un
                          déploiement qui n'est pas encore passé — vécu : la correction était
                          en ligne côté Vercel et pas encore côté Railway, et l'absence de la
                          ligne ne permettait pas de le savoir. Une sonde doit parler même
                          quand elle n'a rien d'anormal à dire.
                        */}
                        <small className="quota-checked">
                          {anthropic?.variable === 'ANTHROPIC_API_KEY' && (
                            <>
                              <strong>Aucune <code>ANTHROPIC_ADMIN_KEY</code> sur ce serveur</strong> :
                              la clé essayée est celle de l'application
                              (<code>ANTHROPIC_API_KEY</code>, par repli), et une clé d'espace de
                              travail ne peut pas ouvrir l'Admin API. C'est la variable qui
                              manque, pas la clé qui est mauvaise.
                            </>
                          )}
                          {anthropic?.variable === 'ANTHROPIC_ADMIN_KEY' && (
                            <>
                              <strong>La clé vient bien d'<code>ANTHROPIC_ADMIN_KEY</code></strong>,
                              et elle est refusée : c'est donc son contenu qui est en cause —
                              une clé rattachée à un espace de travail, là où il faut une portée
                              Organisation.
                            </>
                          )}
                          {!anthropic?.variable && (
                            <>
                              Le serveur n'a pas dit d'où venait la clé : c'est une version
                              antérieure à ce diagnostic. Attendez la fin du déploiement Railway
                              et actualisez.
                            </>
                          )}
                        </small>
                        <small className="quota-checked">
                          Une clé d'espace de travail est refusée. Il faut une clé de portée
                          <strong> Organisation</strong> (Console Anthropic &gt; Clés API &gt;
                          Créer une clé), posée dans <code>ANTHROPIC_ADMIN_KEY</code> — et
                          jamais à la place d'<code>ANTHROPIC_API_KEY</code>, qu'elle ferait
                          tomber faute d'en-tête <code>anthropic-workspace-id</code>. La
                          ventilation ci-dessous ne dépend pas d'elle.
                        </small>
                      </>
                    ) : (
                      <p className="quota-status-text">{anthropic?.error || 'Relevé indisponible'}</p>
                    )}
                  </div>
                </div>
              );
            };

            return (
              <div className="quotas-bloc-claude">
                {carteAnthropic()}

                {/* Répartition par modèle — seulement ce qu'Anthropic a réellement servi */}
                {releve?.parModele?.length > 0 && (
                  <div className="quota-breakdown">
                    <h4>Jetons par modèle</h4>
                    <div className="breakdown-grid libelles-longs">
                      {releve.parModele.map((ligne) => (
                        <div key={ligne.modele} className="breakdown-item">
                          <span className="breakdown-type">{ligne.modele}</span>
                          <span className="breakdown-calls">{nombre(ligne.jetons.total)} jetons</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/*
                  Historique quotidien. L'échelle se prend sur le jour le plus chargé de la
                  fenêtre, et non sur un maximum inventé : il n'y a pas de plafond à rapporter.

                  ⚠️ **La plus récente en tête, et sur une copie.** Anthropic rend ses tranches
                  en ordre chronologique ; la liste commençait donc par un 18 septembre dont
                  personne n'a besoin, et il fallait dérouler trente lignes pour voir hier.
                  L'historique Google Ads, juste à côté, trie déjà en DESC côté SQL : les deux
                  listes du même écran se lisaient dans deux sens opposés. Et la **copie** n'est
                  pas une précaution de style — `reverse()` modifie le tableau en place, et ce
                  même tableau alimente la jauge de tête, dont les barres doivent rester
                  chronologiques pour que la dernière soit le jour en cours.
                */}
                {releve?.historique?.length > 0 && (() => {
                  const sommet = Math.max(...releve.historique.map((j) => j.jetons.total), 1);
                  const duPlusRecent = [...releve.historique].reverse();
                  const depenseParJour = new Map(
                    (releve.depenseParJour || []).map((j) => [j.date, j.montant])
                  );
                  return (
                    <div className="quota-history">
                      <h4>
                        Historique ({releve.jours} derniers jours)
                        {' — '}
                        <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.875rem' }}>
                          échelle sur le jour le plus chargé
                        </span>
                      </h4>
                      <div className="history-grid">
                        {duPlusRecent.map((jour) => (
                          <div key={jour.date} className="history-item">
                            <span className="history-date">
                              {new Date(jour.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                            </span>
                            <div className="history-bar-container">
                              <div
                                className="history-bar"
                                style={{ width: `${Math.min((jour.jetons.total / sommet) * 100, 100)}%` }}
                              />
                            </div>
                            <span className="history-ops">
                              {nombre(jour.jetons.total)}
                              {depenseParJour.has(jour.date) && (
                                <span className="stat-fenetre">
                                  {' '}· {depenseParJour.get(jour.date).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {releve.depense.devise}
                                </span>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/*
                  Ventilation interne.

                  ⚠️ **Elle ne boucle pas avec le relevé d'Anthropic, et ne doit pas chercher à
                  le faire.** Quatre fonctionnalités appellent Claude sans rien consigner : la
                  part manquante est nommée sous le tableau plutôt que répartie au prorata, qui
                  fabriquerait une attribution fausse.
                */}
                {interne?.success !== false && interne && (
                  <div className="quota-breakdown">
                    <h4>
                      Ce que Kokpit a su attribuer ({interne.jours} derniers jours)
                      {' — '}
                      <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.875rem' }}>
                        {nombre(interne.jetons)} jetons
                      </span>
                    </h4>
                    <div className="breakdown-grid libelles-longs">
                      {interne.parUsage.map((ligne) => (
                        <div key={ligne.usage} className="breakdown-item">
                          <span className="breakdown-type">{ligne.usage}</span>
                          <span className="breakdown-calls">
                            {nombre(ligne.jetons)} jetons <span className="stat-fenetre">· {nombre(ligne.appels)} appels</span>
                          </span>
                        </div>
                      ))}
                    </div>
                    <small className="quota-checked" style={{ display: 'block', marginTop: '0.75rem' }}>
                      Anthropic ne peut pas dire qui consomme : toutes nos requêtes partent sous
                      une seule clé. Ce total est donc attribué depuis nos tables, et il est
                      <strong> inférieur au relevé facturé</strong> — {interne.usagesNonConsignes.join(', ')}
                      {' '}appellent Claude sans consigner leurs jetons.
                    </small>
                  </div>
                )}

                {interne?.parUtilisateur?.length > 0 && (
                  <div className="quota-users">
                    <h4>Jetons attribués par utilisateur ({interne.jours} derniers jours)</h4>
                    <div className="users-list">
                      {interne.parUtilisateur.slice(0, 10).map((ligne, idx) => (
                        <div key={ligne.userId} className="user-item">
                          <span className="user-rank">#{idx + 1}</span>
                          <span className="user-email">{ligne.email || ligne.userId}</span>
                          <span className="user-ops">{nombre(ligne.jetons)} jetons</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {detailQuota === 'google' && (quotaLoading && !quotaStatus ? (
            <div className="quotas-loading">
              <Loader2 size={32} className="spin" />
              <p>Chargement des statistiques de quota...</p>
            </div>
          ) : quotaStatus?.quotaStats ? (
            <div className="quotas-content">
              {/* Carte principale de quota */}
              <div className={`quota-card ${quotaStatus.quotaStats.status === 'OK' ? 'ok' : quotaStatus.quotaStats.status === 'WARNING' ? 'warning' : 'exhausted'}`}>
                <div className="quota-icon">
                  {quotaStatus.quotaStats.status === 'OK' ? (
                    <CheckCircle size={48} />
                  ) : quotaStatus.quotaStats.status === 'WARNING' ? (
                    <AlertCircle size={48} />
                  ) : (
                    <Clock size={48} />
                  )}
                </div>
                <div className="quota-info">
                  <h3>Google Ads API — {quotaStatus.quotaStats.accessLevel}</h3>
                  <p className="quota-status-text">
                    {quotaStatus.quotaStats.status === 'OK' && 'Quota disponible — API opérationnelle'}
                    {quotaStatus.quotaStats.status === 'WARNING' && 'Attention — Plus de 75% du quota consommé'}
                    {quotaStatus.quotaStats.status === 'CRITICAL' && 'Critique — Plus de 90% du quota consommé'}
                  </p>

                  {/* Barre de progression (seulement si quota limité) */}
                  {!quotaStatus.quotaStats.isUnlimited ? (
                    <div className="quota-progress-container">
                      <div className="quota-progress-bar">
                        <div
                          className={`quota-progress-fill ${quotaStatus.quotaStats.status.toLowerCase()}`}
                          style={{ width: `${Math.min(quotaStatus.quotaStats.today.percentUsed, 100)}%` }}
                        />
                      </div>
                      <div className="quota-progress-text">
                        <span>
                          {quotaStatus.quotaStats.today.operations.toLocaleString()} / {quotaStatus.quotaStats.dailyQuota.toLocaleString()} opérations
                          {' '}
                          <span className="stat-fenetre">sur 24 h glissantes</span>
                        </span>
                        <span>{quotaStatus.quotaStats.today.percentUsed}%</span>
                      </div>
                    </div>
                  ) : (
                    <div className="quota-unlimited-badge">
                      Quota illimité
                    </div>
                  )}

                  {/*
                    « 24 h » sur chaque compteur, et sur la barre.
                    
                    Ce sont des **24 heures glissantes**, pas la journée en cours — c'est ainsi que
                    Google compte, et c'est aussi ce qui rend ces chiffres incomparables à
                    l'historique juste en dessous, qui est par jour calendaire. Relevé un 23
                    septembre à 15 h : 6 195 opérations sur la fenêtre glissante, 841 depuis
                    minuit. Sept fois l'écart, sans que rien à l'écran ne l'explique.
                  */}
                  <div className="quota-stats-row">
                    <div className="quota-stat">
                      <span className="stat-value">{quotaStatus.quotaStats.today.operations.toLocaleString()}</span>
                      <span className="stat-label">Opérations <span className="stat-fenetre">(24 h)</span></span>
                    </div>
                    <div className="quota-stat">
                      <span className="stat-value">{quotaStatus.quotaStats.today.calls}</span>
                      <span className="stat-label">Appels API <span className="stat-fenetre">(24 h)</span></span>
                    </div>
                    <div className="quota-stat">
                      <span className="stat-value">{quotaStatus.quotaStats.today.errors}</span>
                      <span className="stat-label">Erreurs <span className="stat-fenetre">(24 h)</span></span>
                    </div>
                  </div>

                  {quotaStatus.checkedAt && (
                    <small className="quota-checked">
                      Dernière mise à jour: {new Date(quotaStatus.checkedAt).toLocaleTimeString('fr-FR')}
                    </small>
                  )}
                </div>
              </div>

              {/* Répartition par type d'opération */}
              {quotaStatus.quotaStats.operationBreakdown?.length > 0 && (
                <div className="quota-breakdown">
                  <h4>Répartition par type d'opération</h4>
                  <div className="breakdown-grid">
                    {quotaStatus.quotaStats.operationBreakdown.map((op, idx) => (
                      <div key={idx} className="breakdown-item">
                        <span className="breakdown-type">{op.type}</span>
                        <span className="breakdown-calls">{op.calls || op.operations} appels</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/*
                Historique quotidien. La profondeur vient du serveur : l'écran ne la devine pas,
                sans quoi le titre annoncerait trente jours là où la sonde n'en a que huit.
              */}
              {quotaStatus.quotaStats.history?.length > 0 && (
                <div className="quota-history">
                  <h4>
                    Historique ({quotaStatus.quotaStats.joursHistorique || 30} derniers jours)
                    {' — '}
                    <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.875rem' }}>
                      {quotaStatus.quotaStats.history.length} jour{quotaStatus.quotaStats.history.length > 1 ? 's' : ''} relevé{quotaStatus.quotaStats.history.length > 1 ? 's' : ''}
                    </span>
                  </h4>
                  <div className="history-grid">
                    {quotaStatus.quotaStats.history.map((day, idx) => (
                      <div key={idx} className="history-item">
                        <span className="history-date">
                          {new Date(day.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </span>
                        <div className="history-bar-container">
                          <div
                            className="history-bar"
                            style={{ width: `${Math.min((day.operations / quotaStatus.quotaStats.dailyQuota) * 100, 100)}%` }}
                          />
                        </div>
                        <span className="history-ops">{day.operations.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top utilisateurs */}
              {quotaStatus.quotaStats.userBreakdown?.length > 0 && (
                <div className="quota-users">
                  <h4>Consommation par utilisateur (aujourd'hui)</h4>
                  <div className="users-list">
                    {quotaStatus.quotaStats.userBreakdown.slice(0, 10).map((u, idx) => (
                      <div key={idx} className="user-item">
                        <span className="user-rank">#{idx + 1}</span>
                        <span className="user-email">{u.user?.email || 'Unknown'}</span>
                        <span className="user-ops">{u.operations} ops</span>
                        <span className="user-calls">{u.calls} appels</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Erreurs de quota (RESOURCE_EXHAUSTED / RATE_EXCEEDED) */}
              <div className="quota-errors-section">
                <div className="quota-errors-header">
                  <h4>Erreurs de quota Google Ads</h4>
                  <button
                    className="refresh-btn-small"
                    onClick={fetchQuotaErrors}
                    disabled={quotaErrorsLoading}
                  >
                    {quotaErrorsLoading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
                  </button>
                </div>

                {quotaErrorsLoading && !quotaErrors ? (
                  <div className="quota-errors-loading">
                    <Loader2 size={20} className="spin" />
                  </div>
                ) : quotaErrors?.quotaErrors ? (
                  <>
                    <div className="quota-errors-grid">
                      <div className="quota-error-card">
                        <span className="period">24h</span>
                        <div className="error-counts">
                          <span className="error-type resource" title="RESOURCE_EXHAUSTED (quota journalier)">
                            {quotaErrors.quotaErrors.last24h.resourceExhausted}
                          </span>
                          <span className="error-type rate" title="RATE_EXCEEDED (requêtes/sec)">
                            {quotaErrors.quotaErrors.last24h.rateExceeded}
                          </span>
                        </div>
                      </div>
                      <div className="quota-error-card">
                        <span className="period">7 jours</span>
                        <div className="error-counts">
                          <span className="error-type resource" title="RESOURCE_EXHAUSTED">
                            {quotaErrors.quotaErrors.last7days.resourceExhausted}
                          </span>
                          <span className="error-type rate" title="RATE_EXCEEDED">
                            {quotaErrors.quotaErrors.last7days.rateExceeded}
                          </span>
                        </div>
                      </div>
                      <div className="quota-error-card">
                        <span className="period">30 jours</span>
                        <div className="error-counts">
                          <span className="error-type resource" title="RESOURCE_EXHAUSTED">
                            {quotaErrors.quotaErrors.last30days.resourceExhausted}
                          </span>
                          <span className="error-type rate" title="RATE_EXCEEDED">
                            {quotaErrors.quotaErrors.last30days.rateExceeded}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="quota-errors-legend">
                      <span className="legend-item"><span className="dot resource"></span> RESOURCE_EXHAUSTED (quota)</span>
                      <span className="legend-item"><span className="dot rate"></span> RATE_EXCEEDED (débit)</span>
                    </div>
                    {quotaErrors.quotaErrors.context && (
                      <div className="quota-errors-context" style={{ marginTop: '0.75rem', padding: '0.5rem', backgroundColor: 'var(--beige-light)', borderRadius: '6px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        <span style={{ marginRight: '1rem' }}>30j: {quotaErrors.quotaErrors.context.totalCalls30d.toLocaleString()} appels API</span>
                        <span style={{ marginRight: '1rem' }}>✓ {quotaErrors.quotaErrors.context.totalSuccess30d.toLocaleString()} OK</span>
                        <span>✗ {quotaErrors.quotaErrors.context.totalErrors30d.toLocaleString()} erreurs</span>
                      </div>
                    )}
                    {quotaErrors.quotaErrors.recentErrors?.length > 0 && (
                      <details className="quota-errors-details">
                        <summary>{quotaErrors.quotaErrors.recentErrors.length} erreur(s) récente(s)</summary>
                        <div className="quota-errors-list">
                          {quotaErrors.quotaErrors.recentErrors.map((err, idx) => (
                            <div key={err.id || idx} className="quota-error-item">
                              <span className="error-date">
                                {new Date(err.createdAt).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </span>
                              <span className="error-op">{err.operationType}</span>
                              <span className="error-account">{err.customerId}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </>
                ) : (
                  <p className="quota-errors-empty">Aucune erreur de quota enregistrée</p>
                )}
              </div>

              <div className="quotas-info">
                <AlertCircle size={16} />
                <div>
                  <h4>Informations sur les quotas Google Ads</h4>
                  <ul>
                    <li>Niveau d'accès {quotaStatus.quotaStats.accessLevel}: {quotaStatus.quotaStats.isUnlimited ? 'Illimité' : `${quotaStatus.quotaStats.dailyQuota.toLocaleString()} opérations/jour`}</li>
                    <li>Le quota se réinitialise chaque jour (période glissante de 24h)</li>
                    <li>Ces statistiques sont basées sur le tracking interne des appels</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="quotas-no-data">
              <Gauge size={48} />
              <h3>Statistiques non disponibles</h3>
              <p>Cliquez sur "Actualiser" pour charger les statistiques de quota.</p>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'mcp' && (
        <div className="admin-mcp">
          <div className="mcp-header">
            {/* L'interrupteur ne gouverne plus seulement le MCP : depuis que Radar et le
                Diagnostic corrigent les constats, il commande aussi les écritures faites
                depuis les écrans. Le dire ici, sinon on croit ne couper qu'un canal sur deux. */}
            <p>
              Comptes autorisés en écriture — par le MCP (Model Context Protocol) <strong>et</strong>{' '}
              par les corrections lancées depuis Radar et le Diagnostic.
            </p>
            <button
              className="refresh-btn"
              onClick={fetchMcpData}
              disabled={mcpLoading}
            >
              {mcpLoading ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
              Actualiser
            </button>
          </div>

          {mcpLoading && mcpAccessList.length === 0 ? (
            <div className="mcp-loading">
              <Loader2 size={32} className="spin" />
              <p>Chargement des données MCP...</p>
            </div>
          ) : (
            <div className="mcp-content">
              {/* Ce que le MCP lit. Informatif : la lecture ne se règle pas ici mais sur
                  chaque compte, dans Comptes › Destinations. */}
              <div className="mcp-section">
                <h3><Database size={18} /> Sources lisibles</h3>
                <p className="mcp-section-desc">
                  Les comptes que le connecteur MCP peut consulter. Se règle compte par compte,
                  dans Comptes, sous « Destinations ».
                </p>
                {mcpPerimetre ? (
                  <>
                    <p className="mcp-lecture-total">
                      <strong>{mcpPerimetre.lecture.total}</strong> compte
                      {mcpPerimetre.lecture.total > 1 ? 's' : ''} sur{' '}
                      {Object.keys(mcpPerimetre.lecture.parType).length} type
                      {Object.keys(mcpPerimetre.lecture.parType).length > 1 ? 's' : ''}
                      {mcpPerimetre.lecture.debrayes > 0 && (
                        <span className="mcp-debraye">
                          {' '}— {mcpPerimetre.lecture.debrayes} débrayé
                          {mcpPerimetre.lecture.debrayes > 1 ? 's' : ''} du MCP
                        </span>
                      )}
                    </p>
                    <div className="mcp-types">
                      {Object.entries(mcpPerimetre.lecture.parType)
                        .sort((a, b) => b[1] - a[1])
                        .map(([type, n]) => (
                          <span key={type} className="mcp-type">
                            {LIBELLES_TYPE_MCP[type] || type.replace(/_/g, ' ')}
                            <strong>{n}</strong>
                          </span>
                        ))}
                    </div>
                  </>
                ) : (
                  <div className="mcp-empty"><p>Périmètre indisponible</p></div>
                )}
              </div>

              {/* Ce sur quoi le MCP peut écrire. Tous les comptes des providers concernés sont
                  listés, autorisés ou non : c'est la seule façon de voir ce qui manque. */}
              <div className="mcp-section">
                <h3><Shield size={18} /> Écriture autorisée</h3>
                <p className="mcp-section-desc">
                  Seuls ces {(mcpPerimetre?.ecriture || []).length} connecteurs acceptent des
                  écritures. Un compte refusé reste lisible ; seule la modification lui est
                  fermée — pour un agent MCP comme pour un utilisateur qui clique
                  « Corriger » dans Radar.
                </p>
                {(mcpPerimetre?.ecriture || []).map(groupe => {
                  const autorises = groupe.comptes.filter(c => c.autorise).length;
                  return (
                    <div key={groupe.provider} className="mcp-groupe">
                      <h4>
                        {groupe.libelle}
                        <span className="mcp-groupe-compteur">
                          {autorises} / {groupe.comptes.length}
                        </span>
                      </h4>
                      {groupe.comptes.length === 0 ? (
                        <p className="mcp-groupe-vide">Aucun compte de ce type dans le parc.</p>
                      ) : (
                        <table className="mcp-table">
                          <tbody>
                            {groupe.comptes.map(compte => (
                              <tr key={compte.libraryItemId} className={compte.autorise ? '' : 'disabled'}>
                                <td className="note">{compte.nom}</td>
                                <td className="account-id">{compte.accountId}</td>
                                <td>
                                  <button
                                    className={`status-toggle ${compte.autorise ? 'active' : 'inactive'}`}
                                    onClick={() => basculerEcritureMcp(groupe.provider, compte)}
                                    title={compte.autorise
                                      ? 'Cliquer pour refuser l’écriture'
                                      : 'Cliquer pour autoriser l’écriture'}
                                  >
                                    {compte.autorise ? 'Autorisé' : (compte.suspendu ? 'Suspendu' : 'Refusé')}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Les autorisations qui ne portent sur rien. Les taire donnerait l'illusion
                  d'un droit accordé — quatre d'entre elles visaient un connecteur sur lequel
                  le MCP n'écrit pas. */}
              {mcpPerimetre?.inertes?.length > 0 && (
                <div className="mcp-section">
                  <h3><AlertCircle size={18} /> Autorisations sans effet</h3>
                  <p className="mcp-section-desc">
                    Ces entrées ne donnent aucun droit aujourd'hui — mais celles qui sont
                    <strong> actives le reprendraient</strong> dès que la condition manquante
                    serait comblée : une route d'écriture ajoutée pour ce connecteur, un compte
                    rattaché à la bibliothèque. Les supprimer évite qu'un droit se réveille sans
                    que personne l'ait décidé.
                  </p>
                  <table className="mcp-table">
                    <thead>
                      <tr><th>Connecteur</th><th>Identifiant</th><th>Pourquoi</th><th>État</th><th /></tr>
                    </thead>
                    <tbody>
                      {mcpPerimetre.inertes.map(e => (
                        <tr key={e.id} className="disabled">
                          <td>
                            <span className={`provider-badge ${e.provider.toLowerCase().replace('_', '-')}`}>
                              {e.provider.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="account-id">{e.accountId}</td>
                          <td className="note">{e.raison}</td>
                          <td>
                            {/* « Dormant » plutôt qu'« actif » : le droit existe mais ne
                                s'applique pas encore, et c'est ce décalage qui justifie de
                                supprimer l'entrée. */}
                            <span className={`status-toggle ${e.enabled ? 'active' : 'inactive'}`}>
                              {e.enabled ? 'Dormant' : 'Suspendu'}
                            </span>
                          </td>
                          <td>
                            <button
                              className="status-toggle inactive"
                              onClick={() => removeMcpAccess(e.id)}
                              title={e.enabled
                                ? 'Retirer ce droit avant qu’il ne se réveille'
                                : 'Retirer cette entrée devenue inutile'}
                            >
                              Supprimer
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Journal des opérations */}
              <div className="mcp-section">
                <h3><History size={18} /> Journal des opérations</h3>
                <p className="mcp-section-desc">
                  Toutes les écritures faites sur le parc, quelle qu'en soit l'origine : un agent
                  MCP, ou une correction lancée depuis Radar ou le Diagnostic.
                </p>

                {filteredMcpWriteLogs.length === 0 ? (
                  <div className="mcp-empty">
                    <FileText size={32} />
                    <p>{mcpWriteLogs.length === 0 ? 'Aucune opération enregistrée' : 'Aucun résultat'}</p>
                  </div>
                ) : (
                  <>
                    <table className="mcp-logs-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Utilisateur</th>
                          <th>Compte</th>
                          <th>Opération</th>
                          <th>Statut</th>
                          <th>Détails</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMcpWriteLogs.map((log) => (
                          <tr key={log.id} className={log.success ? 'success' : 'error'}>
                            <td className="date">
                              {new Date(log.executedAt).toLocaleString('fr-FR', {
                                day: '2-digit', month: '2-digit', year: '2-digit',
                                hour: '2-digit', minute: '2-digit'
                              })}
                            </td>
                            <td className="user">{log.user?.email || log.userId}</td>
                            <td className="account">{log.accountId}</td>
                            <td className="operation">
                              <code>{log.operation}</code>
                            </td>
                            <td>
                              {log.success ? (
                                <span className="status-badge success"><CheckCircle size={14} /> OK</span>
                              ) : (
                                <span className="status-badge error"><XCircle size={14} /> Erreur</span>
                              )}
                            </td>
                            <td className="details">
                              {log.success ? (
                                <details>
                                  <summary>Voir params</summary>
                                  <pre>{JSON.stringify(log.params, null, 2)}</pre>
                                </details>
                              ) : (
                                <span className="error-msg">{log.errorMessage}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    {mcpLogsTotal > 50 && (
                      <div className="mcp-pagination">
                        <button
                          disabled={mcpLogsPage === 1}
                          onClick={() => setMcpLogsPage(p => Math.max(1, p - 1))}
                        >
                          Précédent
                        </button>
                        <span>Page {mcpLogsPage} / {Math.ceil(mcpLogsTotal / 50)}</span>
                        <button
                          disabled={mcpLogsPage >= Math.ceil(mcpLogsTotal / 50)}
                          onClick={() => setMcpLogsPage(p => p + 1)}
                        >
                          Suivant
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Informations */}
              <div className="mcp-info">
                <AlertCircle size={16} />
                <div>
                  <h4>Ce que cet écran commande</h4>
                  <ul>
                    <li>
                      L'autorisation vaut pour <strong>toute</strong> écriture dans le compte :
                      celles d'un agent MCP comme celles lancées depuis Radar ou le Diagnostic.
                      Un compte refusé ou suspendu n'accepte ni les unes ni les autres.
                    </li>
                    <li>Toutes les opérations sont journalisées, réussies comme échouées</li>
                    <li>
                      Un compte ajouté à la bibliothèque arrive ici <strong>suspendu</strong> :
                      l'activer reste un geste d'administrateur
                    </li>
                    <li>
                      Les corrections lancées depuis les écrans relisent l'état du compte avant
                      d'écrire, et conservent de quoi être annulées
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'tickets' && (
        <div className="admin-tickets">
          {selectedTicket ? (
            <TicketDetail
              ticket={selectedTicket}
              token={token}
              user={user}
              onBack={() => {
                setSelectedTicket(null);
                onTicketSelect?.(null);
              }}
              onTicketUpdated={(updatedTicket) => {
                setSelectedTicket(updatedTicket);
                fetchTickets();
              }}
            />
          ) : (
            <>
          {/* Stats */}
          {ticketsStats && (
            <div className="admin-stats-row" style={{ marginBottom: '24px' }}>
              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-icon" style={{ background: '#fffbeb' }}>
                    <Clock size={20} style={{ color: '#f59e0b' }} />
                  </span>
                  <div>
                    <span className="stat-value">{ticketsStats.byStatus.inProgress}</span>
                    <span className="stat-label">En cours</span>
                  </div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-icon" style={{ background: '#ecfdf5' }}>
                    <CheckCircle size={20} style={{ color: '#10b981' }} />
                  </span>
                  <div>
                    <span className="stat-value">{ticketsStats.byStatus.resolved}</span>
                    <span className="stat-label">Résolus</span>
                  </div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-icon" style={{ background: '#f3f4f6' }}>
                    <XCircle size={20} style={{ color: '#6b7280' }} />
                  </span>
                  <div>
                    <span className="stat-value">{ticketsStats.byStatus.closed}</span>
                    <span className="stat-label">Fermés</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Filter size={16} style={{ color: '#6b7280' }} />
              <select
                value={ticketsFilter}
                onChange={(e) => {
                  setTicketsFilter(e.target.value);
                  setTicketsPage(1);
                }}
                style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '0.875rem' }}
              >
                <option value="">Tous les statuts</option>
                <option value="IN_PROGRESS">En cours</option>
                <option value="RESOLVED">Résolu</option>
                <option value="CLOSED">Fermé</option>
              </select>
            </div>
            <button
              onClick={fetchTickets}
              disabled={ticketsLoading}
              className="refresh-btn"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <RefreshCw size={16} className={ticketsLoading ? 'spinning' : ''} />
              Actualiser
            </button>
          </div>

          {/* Loading */}
          {ticketsLoading && tickets.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              <Loader2 size={24} className="spinning" style={{ marginBottom: '8px' }} />
              <p>Chargement des tickets...</p>
            </div>
          )}

          {/* Empty */}
          {!ticketsLoading && tickets.length === 0 && (
            <div style={{ padding: '60px 20px', textAlign: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px' }}>
              <MessageSquare size={48} style={{ color: '#d1d5db', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px', color: '#374151' }}>Aucun ticket</h3>
              <p style={{ margin: 0, color: '#6b7280' }}>Il n'y a pas de ticket de support pour le moment.</p>
            </div>
          )}

          {/* Tickets List */}
          {tickets.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {tickets.map(ticket => {
                const statusColors = {
                  IN_PROGRESS: { bg: '#fffbeb', color: '#f59e0b' },
                  RESOLVED: { bg: '#ecfdf5', color: '#10b981' },
                  CLOSED: { bg: '#f3f4f6', color: '#6b7280' }
                };
                const statusLabels = {
                  IN_PROGRESS: 'En cours',
                  RESOLVED: 'Résolu',
                  CLOSED: 'Fermé'
                };
                const categoryIcons = {
                  BUG: Bug,
                  QUESTION: HelpCircle,
                  SUGGESTION: Lightbulb,
                  OTHER: MoreHorizontal
                };
                const categoryColors = {
                  BUG: '#ef4444',
                  QUESTION: '#3b82f6',
                  SUGGESTION: '#f59e0b',
                  OTHER: '#6b7280'
                };
                const priorityColors = {
                  LOW: '#6b7280',
                  MEDIUM: '#3b82f6',
                  HIGH: '#f59e0b',
                  CRITICAL: '#ef4444'
                };
                const CategoryIcon = categoryIcons[ticket.category] || MoreHorizontal;
                const statusStyle = statusColors[ticket.status] || statusColors.IN_PROGRESS;

                return (
                  <div
                    key={ticket.id}
                    onClick={() => {
                      setSelectedTicket(ticket);
                      onTicketSelect?.(ticket.id);
                    }}
                    style={{
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '20px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#4f46e5';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 70, 229, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#6b7280' }}>
                        #{String(ticket.number).padStart(4, '0')}
                      </span>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        backgroundColor: statusStyle.bg,
                        color: statusStyle.color
                      }}>
                        {statusLabels[ticket.status] || 'En cours'}
                      </span>
                    </div>

                    <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>
                      {ticket.subject}
                    </h3>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: categoryColors[ticket.category] }}>
                        <CategoryIcon size={14} />
                        {ticket.category === 'BUG' && 'Bug'}
                        {ticket.category === 'QUESTION' && 'Question'}
                        {ticket.category === 'SUGGESTION' && 'Suggestion'}
                        {ticket.category === 'OTHER' && 'Autre'}
                      </span>
                      <span style={{ fontSize: '0.8rem', color: priorityColors[ticket.priority] }}>
                        Priorité {ticket.priority === 'LOW' && 'Basse'}
                        {ticket.priority === 'MEDIUM' && 'Moyenne'}
                        {ticket.priority === 'HIGH' && 'Haute'}
                        {ticket.priority === 'CRITICAL' && 'Critique'}
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.8rem', color: '#6b7280' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Users size={14} />
                        {ticket.user?.name || ticket.user?.email}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock size={14} />
                        {formatFullDate(ticket.createdAt)}
                      </span>
                    </div>

                    {ticket.lastMessage && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        color: '#6b7280'
                      }}>
                        <span style={{ fontWeight: 500, color: '#374151', marginRight: '6px' }}>
                          {ticket.lastMessage.isFromAdmin ? 'Support:' : `${ticket.lastMessage.author}:`}
                        </span>
                        {ticket.lastMessage.content}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {ticketsTotal > 20 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginTop: '24px' }}>
              <button
                disabled={ticketsPage === 1}
                onClick={() => setTicketsPage(p => Math.max(1, p - 1))}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  cursor: ticketsPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: ticketsPage === 1 ? 0.5 : 1
                }}
              >
                Précédent
              </button>
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                Page {ticketsPage} sur {Math.ceil(ticketsTotal / 20)}
              </span>
              <button
                disabled={ticketsPage >= Math.ceil(ticketsTotal / 20)}
                onClick={() => setTicketsPage(p => p + 1)}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  cursor: ticketsPage >= Math.ceil(ticketsTotal / 20) ? 'not-allowed' : 'pointer',
                  opacity: ticketsPage >= Math.ceil(ticketsTotal / 20) ? 0.5 : 1
                }}
              >
                Suivant
              </button>
            </div>
          )}
            </>
          )}
        </div>
      )}

      {/* Section Erreurs API */}
      {activeTab === 'errors' && (
        <div className="admin-errors">
          <div className="errors-header">
            <h3>Erreurs API</h3>
            <div className="errors-header-actions">
              {/* La fenêtre est un choix, pas un décor : le titre ne doit plus annoncer une
                  profondeur qu'on ne pouvait pas changer. */}
              <div className="errors-window-picker">
                {[
                  { cle: '24h', titre: '24 h' },
                  { cle: '7j', titre: '7 jours' },
                  { cle: '30j', titre: '30 jours' }
                ].map(f => (
                  <button
                    key={f.cle}
                    className={fenetreErreurs === f.cle ? 'active' : ''}
                    onClick={() => setFenetreErreurs(f.cle)}
                    disabled={quotaLoading}
                  >
                    {f.titre}
                  </button>
                ))}
              </div>
              <button
                className="refresh-btn"
                onClick={fetchQuotaStatus}
                disabled={quotaLoading}
              >
                <RefreshCw size={16} className={quotaLoading ? 'spinning' : ''} />
                Actualiser
              </button>
            </div>
          </div>

          {quotaLoading && !quotaStatus ? (
            <div className="errors-loading">
              <Loader2 size={24} className="spinning" />
              <p>Chargement des erreurs...</p>
            </div>
          ) : quotaStatus?.quotaStats?.recentErrors?.length > 0 ? (
            <div className="errors-content">
              <div className="errors-summary">
                <AlertCircle size={20} />
                {/* Deux nombres, parce qu'ils ne disent pas la même chose : combien de causes
                    distinctes, et combien de fois elles se sont produites. Vingt-deux lignes
                    identiques, c'est souvent trois problèmes. */}
                <span>
                  {(quotaStatus.quotaStats.erreursGroupees || quotaStatus.quotaStats.recentErrors).length} cause(s)
                  {' · '}
                  {quotaStatus.quotaStats.fenetre?.examinees
                    ?? quotaStatus.quotaStats.totalOccurrences
                    ?? quotaStatus.quotaStats.recentErrors.length} occurrence(s)
                  {quotaStatus.quotaStats.fenetre
                    ? ` sur ${quotaStatus.quotaStats.fenetre.jours === 1 ? '24 h' : `${quotaStatus.quotaStats.fenetre.jours} jours`}`
                    : ' dans les dernières 24h'}
                </span>
              </div>

              {/* Dit quand le plafond a coupé : sans cet avertissement, un compteur
                  d'occurrences tronqué se lirait comme exact. */}
              {quotaStatus.quotaStats.fenetre?.tronquee && (
                <div className="errors-truncated">
                  <AlertCircle size={16} />
                  <span>
                    {quotaStatus.quotaStats.fenetre.examinees.toLocaleString('fr-FR')} occurrences sur la période,
                    dont les {quotaStatus.quotaStats.fenetre.plafond.toLocaleString('fr-FR')} plus récentes sont
                    analysées ici. Les compteurs ci-dessous portent sur cet échantillon, pas sur le total.
                  </span>
                </div>
              )}

              {/* Filtre par plateforme. Les totaux viennent du décompte en base, pas de la
                  liste affichée : celle-ci est plafonnée, et compter dessus annoncerait moins
                  d'erreurs qu'il n'y en a. */}
              {Object.keys(quotaStatus.quotaStats.parPlateforme || {}).length > 1 && (
                <div className="errors-platform-filter">
                  <button
                    className={filtrePlateforme === null ? 'active' : ''}
                    onClick={() => setFiltrePlateforme(null)}
                  >
                    Toutes
                  </button>
                  {Object.entries(quotaStatus.quotaStats.parPlateforme)
                    .sort((a, b) => b[1] - a[1])
                    .map(([cle, nb]) => (
                      <button
                        key={cle}
                        className={filtrePlateforme === cle ? 'active' : ''}
                        onClick={() => setFiltrePlateforme(filtrePlateforme === cle ? null : cle)}
                      >
                        {libellePlateforme(cle)} <span className="count">{nb.toLocaleString('fr-FR')}</span>
                      </button>
                    ))}
                </div>
              )}

              <div className="errors-list">
                {(quotaStatus.quotaStats.erreursGroupees || quotaStatus.quotaStats.recentErrors)
                  .filter(e => !filtrePlateforme || e.provider === filtrePlateforme)
                  .map((err, idx) => (
                  <div key={err.id || idx} className="error-item">
                    <div className="error-header">
                      <span className="error-time">
                        {new Date(err.createdAt).toLocaleString('fr-FR', {
                          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                      {err.provider && (
                        <span className={`error-provider provider-${err.provider.toLowerCase().replace('_', '-')}`}>
                          {err.provider === 'GOOGLE_ADS' ? 'Google Ads' :
                           err.provider === 'META_ADS' || err.provider === 'FACEBOOK_ADS' ? 'Meta Ads' :
                           err.provider === 'MICROSOFT_ADS' ? 'Microsoft Ads' :
                           err.provider === 'OPENAI_ADS' ? 'ChatGPT Ads' :
                           err.provider === 'GOOGLE_ANALYTICS_4' ? 'Google Analytics' :
                           err.provider === 'MATOMO' ? 'Matomo' :
                           err.provider === 'PIANO_ANALYTICS' ? 'Piano' :
                           err.provider === 'GOOGLE_SEARCH_CONSOLE' ? 'Search Console' :
                           err.provider === 'GOOGLE_TAG_MANAGER' ? 'Tag Manager' :
                           err.provider === 'MCP'
                             // La source n'est accolée que si elle apprend quelque chose :
                             // le défaut valait « MCP », et le badge affichait « MCP MCP ».
                             ? `MCP ${err.metadata?.sourceType && err.metadata.sourceType !== 'MCP' ? err.metadata.sourceType : ''}`.trim()
                             : err.provider}
                        </span>
                      )}
                      <span className="error-type">{err.operationType}</span>
                      {err.metadata?.source && <span className="error-source">{err.metadata.source}</span>}
                      {err.metadata?.resource && <span className="error-resource">{err.metadata.resource}</span>}
                      {err.metadata?.accountName && <span className="error-account">{err.metadata.accountName}</span>}
                      {/* Le compte : son nom quand on a su le résoudre, son identifiant sinon,
                          et l'identifiant reste en infobulle — c'est lui qu'on recopie dans une
                          requête ou qu'on cherche dans un journal. */}
                      {err.customerId && (
                        <span className="error-customer" title={`Identifiant : ${err.customerId}`}>
                          {err.metadata?.accountName || `#${err.customerId}`}
                        </span>
                      )}
                      {/* L'utilisateur, en tête plutôt qu'en pied : savoir si une erreur touche
                          un client ou l'agence change la lecture qu'on en fait. Le demandeur
                          prime quand un accès client emprunte le compte du propriétaire. */}
                      {(err.metadata?.demandeur?.email || err.user?.email) && (
                        <span
                          className="error-user-badge"
                          title={err.metadata?.demandeur?.email && err.user?.email
                            && err.metadata.demandeur.email !== err.user.email
                            ? `Demandé par ${err.metadata.demandeur.email} sur les données de ${err.user.email}`
                            : undefined}
                        >
                          {err.metadata?.demandeur?.email || err.user.email}
                        </span>
                      )}
                      {err.occurrences > 1 && (
                        <span
                          className="error-occurrences"
                          title={`De ${new Date(err.premiereVue).toLocaleString('fr-FR')} à ${new Date(err.derniereVue).toLocaleString('fr-FR')}`}
                        >
                          ×{err.occurrences}
                        </span>
                      )}
                      <button
                        className="error-copy-btn"
                        onClick={() => copierDiagnostic(err, err.id || idx)}
                        title="Copier tout le contexte : cause, compte, origine de l'appel et requête"
                      >
                        {erreurCopiee === (err.id || idx)
                          ? <><CheckCircle size={13} /> Copié</>
                          : <><FileText size={13} /> Diagnostic</>}
                      </button>
                    </div>
                    <div className="error-message">
                      {err.errorMessage || 'Erreur non spécifiée'}
                    </div>
                    {/* D'où part l'appel : la première question à se poser devant une erreur
                        répétée, et la seule à laquelle le message ne répond jamais. */}
                    {Array.isArray(err.metadata?.origine) && err.metadata.origine.length > 0 && (
                      <div className="error-origine">
                        <span className="error-origine-label">Appelé depuis</span>
                        {err.metadata.origine.map((o, i) => <code key={i}>{o}</code>)}
                      </div>
                    )}
                    {err.metadata?.query && (
                      <details className="error-query-details">
                        <summary>Voir la requête</summary>
                        <pre className="error-query">{err.metadata.query}</pre>
                      </details>
                    )}
                    {/* L'utilisateur est monté en badge d'en-tête ; le répéter ici n'ajoutait
                        rien et allongeait chaque carte. */}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="errors-empty">
              <CheckCircle size={48} />
              <h4>Aucune erreur</h4>
              <p>
                Aucune erreur API enregistrée sur {fenetreErreurs === '24h' ? 'les dernières 24 heures'
                  : fenetreErreurs === '7j' ? 'les 7 derniers jours' : 'les 30 derniers jours'}.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Cache de trafic Awin. En portail, comme toutes les modales du projet : la barre
          latérale et les cartes portent des z-index qui recouvriraient un simple absolu. */}
      {cacheAwinOuvert && ReactDOM.createPortal(
        <div
          onClick={() => setCacheAwinOuvert(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '24px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '12px', padding: '20px 24px',
              width: 'min(980px, 100%)', maxHeight: '86vh', overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <h3 style={{ margin: 0, fontSize: '1.0625rem', color: '#1f2937' }}>
                Trafic Awin stocké en base
              </h3>
              <button
                onClick={() => setCacheAwinOuvert(false)}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280' }}
                aria-label="Fermer"
              >
                <X size={20} />
              </button>
            </div>

            <p style={{ margin: '0 0 16px', fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.5 }}>
              Seuls les impressions et les clics sont stockés : le trafic d'une journée close ne
              bouge plus. Les commissions ne le sont pas — leur statut évolue pendant des
              semaines, et une valeur figée la nuit serait fausse le lendemain.
              {cacheAwin?.fenetre && (
                <> La couverture est mesurée sur la fenêtre entretenue par la tâche nocturne :
                  du {cacheAwin.fenetre.debut} au {cacheAwin.fenetre.fin} ({cacheAwin.fenetre.jours} jours).
                  Au-delà, une journée n'est rapatriée que si un écran la demande.</>
              )}
            </p>

            {cacheAwinChargement && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', padding: '24px 0' }}>
                <Loader2 size={16} className="spin" /> <span>Lecture de la base…</span>
              </div>
            )}

            {cacheAwinErreur && (
              <div style={{ padding: '10px 12px', borderRadius: '8px', background: '#fef2f2', color: '#991b1b', fontSize: '0.875rem' }}>
                {cacheAwinErreur}
              </div>
            )}

            {!cacheAwinChargement && !cacheAwinErreur && cacheAwin && (
              cacheAwin.comptes.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Aucun compte Awin connecté.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                    <thead>
                      <tr style={{ background: '#faf7f5' }}>
                        {['Compte', 'Couverture', 'Journées', 'Profondeur', 'Clics', 'Impressions', 'Dernier rapatriement'].map((t, i) => (
                          <th key={t} style={{
                            textAlign: i === 0 ? 'left' : 'right', padding: '8px 10px',
                            fontWeight: 600, color: '#7c2d12', whiteSpace: 'nowrap'
                          }}>{t}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cacheAwin.comptes.map(c => {
                        const part = c.couverture.attendues > 0
                          ? Math.round((c.couverture.presentes / c.couverture.attendues) * 100)
                          : 0;
                        return (
                          <tr key={c.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 10px' }}>
                              <div style={{ fontWeight: 500, color: '#1f2937' }}>{c.nom}</div>
                              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                annonceur {c.advertiserId}
                                {!c.connexionActive && ' · connexion inactive'}
                              </div>
                              {c.manquantesTotal > 0 && (
                                <div
                                  title={`Journées manquantes les plus récentes : ${c.manquantesRecentes.join(', ')}`}
                                  style={{ fontSize: '0.75rem', color: '#b45309', cursor: 'help', marginTop: '2px' }}
                                >
                                  ⚠ {c.manquantesTotal} journée(s) en attente
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                              <span style={{ color: part === 100 ? '#15803d' : part >= 80 ? '#b45309' : '#b91c1c', fontWeight: 500 }}>
                                {part} %
                              </span>
                              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                {c.couverture.presentes} / {c.couverture.attendues}
                              </div>
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{c.journeesStockees}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap', color: '#6b7280' }}>
                              {c.premiereJournee ? `${c.premiereJournee} → ${c.derniereJournee}` : '—'}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                              {c.clicsTotal.toLocaleString('fr-FR')}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                              {/* Awin ne compte l'impression que sur le display : un zéro ici est
                                  une mesure, pas une lacune. On dit sur combien de journées elle
                                  est renseignée, pour qu'on ne cherche pas une panne. */}
                              {c.impressionsTotal.toLocaleString('fr-FR')}
                              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                sur {c.journeesAvecImpressions} j
                              </div>
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap', color: '#6b7280' }}>
                              {c.dernierRapatriement
                                ? new Date(c.dernierRapatriement).toLocaleString('fr-FR')
                                : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Détail des actions d'une personne, au clic sur sa ligne. En portail comme toutes les
          modales du projet : les cartes et la barre latérale portent des z-index qui
          recouvriraient un simple positionnement absolu. */}
      {detailPersonne && ReactDOM.createPortal(
        <div className="statistics-modal-overlay" onClick={() => setDetailPersonne(null)}>
          <div className="statistics-modal" onClick={e => e.stopPropagation()}>
            <div className="statistics-modal-header">
              <div className="statistics-modal-user">
                {detailPersonne.picture ? (
                  <img src={detailPersonne.picture} alt="" className="member-avatar" />
                ) : (
                  <div className={`member-avatar-placeholder ${detailPersonne.role === 'CLIENT' ? 'client' : ''}`}>
                    {(detailPersonne.name || detailPersonne.email || '?')[0].toUpperCase()}
                  </div>
                )}
                <div className="member-info">
                  <span className="member-name">{detailPersonne.name || 'Sans nom'}</span>
                  <span className="member-email">{detailPersonne.email}</span>
                </div>
              </div>
              <button className="statistics-modal-close" onClick={() => setDetailPersonne(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="statistics-modal-content">
              <h4>
                Actions sur 30 jours
                {chargementActions ? '' : ` (${detailPersonne.actions.length})`}
              </h4>
              {chargementActions && (
                <div className="statistics-no-data"><p>Chargement…</p></div>
              )}
              {!chargementActions && detailPersonne.actions.length === 0 && (
                <div className="statistics-no-data"><p>Aucune action sur la période</p></div>
              )}

              {/* Même mention que dans l'écran Statistiques, et pour la même raison : la
                  « dernière activité » affichée ailleurs compte la présence, que cette liste
                  écarte. Voir `presencePlusRecente`. */}
              {!chargementActions && presencePlusRecente(detailPersonne) && (
                <div className="statistics-presence-note">
                  <Clock size={14} />
                  <span>
                    Vu le {new Date(detailPersonne.dernierePresence).toLocaleString('fr-FR', {
                      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                    {detailPersonne.actions.length > 0
                      ? ', sans action enregistrée depuis.'
                      : ', sans aucune action sur la période.'}
                  </span>
                </div>
              )}
              <div className="statistics-actions-list">
                {detailPersonne.actions.map(action => (
                  <div
                    key={action.id}
                    className={`statistics-action-item ${action.activity === 'MCP' ? 'mcp-action' : ''}`}
                  >
                    <div className="action-info">
                      <span
                        className="activity-badge"
                        style={{ background: ACTIVITY_COLORS[action.activity] || '#6b7280' }}
                      >
                        {ACTIVITY_LABELS[action.activity] || action.activity}
                      </span>
                      {(action.activity === 'DASHBOARD' || action.activity === 'TOOLS') && action.dashboardModel && (
                        <span className="detail-badge">
                          {DASHBOARD_MODEL_LABELS[action.dashboardModel] || action.dashboardModel}
                        </span>
                      )}
                      {action.activity === 'MCP' && action.mcpDetails && (
                        <BadgeMcp details={action.mcpDetails} />
                      )}
                      {(action.accountName || action.mcpDetails?.accountName) && (
                        <span className="account-badge">
                          {action.accountName || action.mcpDetails?.accountName}
                        </span>
                      )}
                    </div>
                    <span className="action-date">
                      <Clock size={14} />
                      {formatFullDate(action.loginAt)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AdminPanel;